import { Component, Input, OnInit, OnDestroy, EventEmitter, Output, SimpleChanges, ViewChild, ElementRef, HostListener, Directive, OnChanges, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { SparePartModel } from '../../../models/spare-part-model/spare-part-model';
import { OrderOutModel } from '../../../models/order-out-model/order-out-model';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { OrdersService } from 'src/app/services/orders/orders.service';
import { SparePartsService } from 'src/app/services/spareParts/spare-parts.service';
import { ExportedFoldersService, CreateExportedFolderModel, CreateBasicExportedFolderModel } from 'src/app/services/exported-folders/exported-folders.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { LabelsService, SparePartForLabel, SparePartLabelInfo } from 'src/app/services/labels/labels.service';
import { LabelPrintAuthorizationService } from 'src/app/services/labels/label-print-authorization.service';
import { WorkshopsService } from 'src/app/services/workshops/workshops.service';
import { WorkshopModel } from '../../../models/workshop-model/workshop-model';
import { ProviderModel } from '../../../models/provider-model/provider-model';
import { ProvidersService } from 'src/app/services/providers/providers.service';
import { PackageListsService } from 'src/app/services/packageLists/package-lists.service';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { switchMap, finalize } from 'rxjs/operators';
import { of, Observable, firstValueFrom } from 'rxjs';
import { ALDANA_COLOR_OPTIONS } from 'src/app/constants/aldana-colors';
import { ColumnVisibilityHelper, ColumnVisibilityOption } from 'src/app/utils/column-visibility.helper';
import { DEFAULT_SPARE_PART_SHIPPING_RECEIPT } from 'src/app/constants/spare-part-defaults';
import { stripPascalCaseShadowKeys, toCanonicalSparePartKeys, applyPostEmergentCommentDuplicateDefaults, normalizeFantUrgency, defaultFantUrgency, FantUrgency } from 'src/app/utils/spare-part-payload.util';
import { getReceptionStatusFieldClass } from 'src/app/utils/spare-part-reception.util';
import { SPARE_PART_DAMAGED_OPTIONS } from 'src/app/constants/spare-part-damaged-states';
import { formatWallClockDateTimeForInput, parseWallClockDateTimeInput } from 'src/app/utils/wall-clock-datetime.util';
import {
  collectLowSaleRatioViolations,
  getSaleValueValidationError,
  LowSaleRatioSparePartCandidate,
  validateSparePartsSaleValues
} from 'src/app/utils/spare-part-validation.util';
import { RETURN_CARRIER_OPTIONS, RETURN_LOG_STATUS_OPTIONS, RETURN_REASON_OPTIONS, RETURN_STATUS_OPTIONS, ML_STATUS_OPTIONS, FRETE_DEV_OPTIONS } from 'src/app/utils/return-options.util';
import {
  canGeneratePackagesForOrder,
  getPackagedSpareParts,
  getUnpackagedSpareParts,
  isPackageClosedForAssignment,
  isSparePartPackaged,
  normalizeIsDamaged,
  ORDER_DELETE_BLOCKED_MESSAGE,
  readApiErrorMessage,
  readNeedReviewFlag,
  SPARE_PART_DELETE_BLOCKED_MESSAGE,
  sparePartHasPackageOrPrintedLabel
} from 'src/app/utils/spare-part-packaging.util';

@Component({
  selector: 'app-order-history',
  templateUrl: './order-history.component.html',
  styleUrls: ['./order-history.component.css']
})
export class OrderHistoryComponent implements OnInit, OnChanges, OnDestroy {
  EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
  private readonly COLUMN_VISIBILITY_STORAGE_KEY = 'orderHistory.visibleColumns.v1';

  @ViewChild('exportDropdownContainer') exportDropdownContainer!: ElementRef;
  @ViewChild('columnSelectorContainer') columnSelectorContainer!: ElementRef;

  @Input() orders: OrderOutModel[] = [];
  @Input() loading = false;
  @Input() allOrdersLoaded = false;
  @Input() searchTerm = '';
  @Input() startDate: Date | undefined;
  @Input() endDate: Date | undefined;
  @Input() totalOrders = 0;
  /** Si true, los datos los carga el padre (p. ej. en /history); no hacer loadPage(1) en ngOnInit para evitar race con filtros. */
  @Input() dataDrivenByParent = false;
  /** Si true, solo se consideran órdenes de los últimos 60 días (filtros y carga con maxAgeDays=60). */
  @Input() onlyLast60Days = false;
  @Output() loadMore = new EventEmitter<void>();
  @Output() searchFilterChanged = new EventEmitter<string>();
  @Output() dateFilterChanged = new EventEmitter<{ startDate?: Date, endDate?: Date }>();

  loadingMore = false;
  currentPage = 1;
  pageSize = 50;
  totalPages = 1;
  filteredTotal = 0;
  totalOrdersInDB = 0; // Total de órdenes en la base de datos
  totalSpareParts = 0; // Total de repuestos en la base de datos
  Math = Math; // Necesario para usar Math en el template
  dateFilterType = 'all';
  customStartDate = '';
  customEndDate = '';
  selectedOrder: OrderOutModel | null = null;
  /** Fila con foco estilo Excel (resaltado gris), por orderId */
  activeFocusedOrderId: number | null = null;
  selectedSparePart: SparePartModel | null = null;
  workshops: WorkshopModel[] = [];
  providers: ProviderModel[] = [];
  selectedWorkshopId: number | null = null; // Filtro por taller
  // Modal crear/ver taller (detalle de orden)
  showCreateWorkshopModal = false;
  showWorkshopInfoModal = false;
  selectedWorkshopInfo: WorkshopModel | null = null;
  creatingWorkshop = false;
  createWorkshopForm!: FormGroup;
  sortedBy = '';
  sortOrder: 'asc' | 'desc' = 'asc';

  // Floating filter popup (multi-selección por columna, igual que repuestos)
  showFilterPopup: boolean = false;
  filterColumn: string = '';
  filterValue: string = '';
  filterPopupPosition: { x: number, y: number } = { x: 0, y: 0 };
  /** Altura máxima del popup para no cortarse (calculada según espacio en viewport). */
  filterPopupMaxHeight: number = 400;
  columnUniqueValues: string[] = [];
  filteredColumnValues: string[] = [];
  /** Filtros por columna aplicados: nombre columna -> valores seleccionados */
  columnFilters: { [key: string]: string[] } = {};
  private readonly CLIENT_SIDE_CALCULATED_COLUMNS = new Set<string>(['Dias de atraso', 'Dias de plazo', 'Costo']);
  /** Selección temporal en el popup; solo se escribe en columnFilters al pulsar Aplicar */
  tempColumnFilterSelection: string[] = [];
  /** Lista completa ordenada/filtrada cuando hay orden o filtros por columna; se pagina en cliente (igual que repuestos). */
  sortedAndFilteredOrders: OrderOutModel[] = [];
  /** Cargando valores únicos para el popup de filtro (desde todo el set). */
  loadingUniqueValues = false;
  /** Cache base para evitar recargar todas las páginas en cada filtro de columna. */
  private allOrdersBaseCache: OrderOutModel[] | null = null;
  private allOrdersBaseCacheKey = '';
  private readonly FILTER_POPUP_HEIGHT = 380;
  private readonly FILTER_POPUP_WIDTH = 320;
  private readonly FILTER_POPUP_MARGIN = 8;

  /** Snapshot del formulario de orden al abrir el modal; para deshabilitar Guardar si no hay cambios. */
  private initialOrderSnapshot: string | null = null;
  /** Snapshot del formulario de repuesto al abrir el modal; para deshabilitar Guardar si no hay cambios. */
  private initialSparePartSnapshot: string | null = null;
  /** Número de exportación al abrir el modal. No se actualiza con las recargas async. */
  private exportNumberWhenSparePartOpened = '';
  /** Token para invalidar respuestas asíncronas viejas del modal de repuesto. */
  private sparePartModalLoadToken = 0;

  private apiUrl = environment.apiEndpoint + '/order';
  private apiUrlSparePart = environment.apiEndpoint + '/SparePart';
  employeeNames = environment.employeeNames;
  damagedStateOptions = SPARE_PART_DAMAGED_OPTIONS;
  sparePartDeliveryStatus = environment.sparePartDeliveryStatus;
  filteredSparePartDeliveryStatus: string[] = [];
  shippingSparePartOptions = environment.shippingSparePartOptions;
  sparePartReceptionStatusOptions = (environment as any).sparePartReceptionStatusOptions ?? ['LLEGO', 'NO-LLEGO'];

  getReceptionStatusFieldClass = getReceptionStatusFieldClass;

  readonly returnStatusOptions = RETURN_STATUS_OPTIONS;
  readonly returnLogStatusOptions = RETURN_LOG_STATUS_OPTIONS;
  readonly returnReasonOptions = RETURN_REASON_OPTIONS;
  readonly returnCarrierOptions = RETURN_CARRIER_OPTIONS;
  readonly mlStatusOptions = ML_STATUS_OPTIONS;
  readonly freteDevOptions = FRETE_DEV_OPTIONS;
  orderStatus = environment.orderStatus;
  // Constantes para manejo de valores Si/No
  readonly Yes: string = 'true'; // Para sendYesOrNo
  readonly No: string = 'false'; // Para sendYesOrNo

  getDacTrackingUrl(remito: string | null | undefined): string {
    const trimmedRemito = (remito || '').trim();
    return trimmedRemito
      ? `${DEFAULT_SPARE_PART_SHIPPING_RECEIPT}${encodeURIComponent(trimmedRemito)}`
      : DEFAULT_SPARE_PART_SHIPPING_RECEIPT;
  }
  readonly si: string = 'si'; // Para isExport
  readonly no: string = 'no'; // Para isExport
  readonly ALERT_DAYS_THRESHOLD = 10; // Cambiado a 10 días
  private readonly DELAY_ALERTS_STORAGE_KEY = 'orderDelayAlertsDismissed';
  selectedOrderCreationDate = '';
  selectedOrderArrivalDate = '';
  selectedSparePartArrivalDate = '';
  selectedSparePartExportDate = '';
  selectedSparePartPhysicalArrivalDateTime = '';
  selectedSparePartFechaComunicado = '';
  selectedSparePartMlDeadline = '';
  isDuplicatingOrder = false;
  isDuplicatingSparePart = false;
  duplicateSparePartTargetOrderId: number | null = null;
  duplicateSparePartTargetOrderNumber = '';
  duplicateSparePartTargetOrderErrorMessage = '';
  private temporarySparePartIdSeed = -1;
  showAldanaForm = false;
  aldanaColorOptions = ALDANA_COLOR_OPTIONS;
  showOrders = true;
  showAllColumns = true;
  openAddSparePartComponent = false;

  // Propiedades para el filtrado
  filteredOrdersList: OrderOutModel[] = [];
  /** Checkbox del encabezado de la tabla de órdenes (página visible). */
  ordersTableAllSelected = false;
  filteredSpareParts: SparePartModel[] = [];

  // Propiedades para la barra de carga
  loadingMessage = '';
  loadingProgress = 0;
  loadingDetail = '';
  private loadingInterval: any;

  // Flag para evitar múltiples actualizaciones de paginación
  private isUpdatingPagination = false;

  // Añadir propiedad para seguir el último repuesto editado
  lastEditedSparePartId: number | null = null;

  // Propiedades para indicadores de estado de empaquetado
  empaquetadoSpareParts: Set<number> = new Set();
  cargadoSpareParts: Set<number> = new Set();
  enviadoSpareParts: Set<number> = new Set();

  // Mapas para almacenar información adicional de los repuestos
  sparePartsTransportType: Map<number, string> = new Map();

  // Variables para exportación de carpetas
  showExportDropdown = false;
  showCreateFolderModal = false;
  showConfirmModal = false;
  showLowSaleRatioConfirmModal = false;
  lowSaleRatioConfirmTitle = 'Confirmar guardado';
  lowSaleRatioViolationLabels: string[] = [];
  isCreatingFolder = false;
  isCopyingWhatsappOrders = false;
  folderProgress = 0;
  folderProgressMessage = '';
  folderName = '';
  folderDescription = '';
  createdFolderUrl: string | null = null;
  canCancelExport = false;
  private exportCancelled = false;
  allFilteredOrders: OrderOutModel[] = []; // Todas las órdenes que cumplen los filtros
  totalFilteredOrders = 0; // Número real de órdenes filtradas
  kpiTotalOrdersCount = 0;
  kpiTotalSparePartsCount = 0;
  kpiTotalOrdersCost = 0;
  kpiTotalOrdersValue = 0;
  private kpiRefreshRequestId = 0;
  private dismissedDelayAlerts = new Set<string>();
  private pendingLowSaleRatioAction: (() => void) | null = null;
  columnSelectorOpen = false;
  columnVisibilityCss = '';
  readonly columnVisibilityOptions: ColumnVisibilityOption[] = [
    { id: 'clienteUY', label: 'Cliente Uy', columnIndex: 4 },
    { id: 'creationDate', label: 'Fecha de Creacion', columnIndex: 5 },
    { id: 'orderNumber', label: 'Orden', columnIndex: 6 },
    { id: 'siniestro', label: 'Siniestro', columnIndex: 7 },
    { id: 'invoiceNumber', label: 'Factura', columnIndex: 8 },
    { id: 'clientName', label: 'Cliente', columnIndex: 9 },
    { id: 'providerName', label: 'Proveedor', columnIndex: 10 },
    { id: 'stateOrder', label: 'Estado', columnIndex: 11 },
    { id: 'expectedArrivalDate', label: 'Fecha de Llegada', columnIndex: 12 },
    { id: 'diasAtraso', label: 'Dias de atraso', columnIndex: 13 },
    { id: 'diasPrazo', label: 'Dias de plazo', columnIndex: 14 },
    { id: 'employeeName', label: 'Vendedor', columnIndex: 15 },
    { id: 'sparePartsCount', label: 'Repuestos', columnIndex: 16 },
    { id: 'estimatedCost', label: 'Costo', columnIndex: 17 }
  ];
  private readonly columnVisibilityHelper = new ColumnVisibilityHelper(
    this.COLUMN_VISIBILITY_STORAGE_KEY,
    'orders-main-table',
    this.columnVisibilityOptions
  );
  visibleColumnIds = new Set<string>();
  private columnVisibilityStyleElement: HTMLStyleElement | null = null;

  constructor(
    private http: HttpClient,
    private router: Router,
    private orderService: OrdersService,
    private sparePartService: SparePartsService,
    private exportedFoldersService: ExportedFoldersService,
    private authService: AuthService,
    private labelsService: LabelsService,
    private labelPrintAuth: LabelPrintAuthorizationService,
    private workshopsService: WorkshopsService,
    private providersService: ProvidersService,
    private packageListsService: PackageListsService,
    private cdr: ChangeDetectorRef,
    private sanitizer: DomSanitizer,
    private fb: FormBuilder
  ) {
    // Verificar que los servicios se inyectaron correctamente
    this.createWorkshopForm = this.fb.group({
      name: ['', Validators.required],
      address: [''],
      phoneNumber: [''],
      city: ['', Validators.required],
      department: [''],
      company: ['AUTOCOMERCIAL']
    });
  }

  ngOnInit(): void {
    this.loadDismissedDelayAlerts();
    this.loadColumnVisibility();
    // Establecer tamaño de página inicial a 50
    this.pageSize = 50;
    this.currentPage = 1;

    // En /history el padre es la fuente de verdad de los filtros.
    if (!this.dataDrivenByParent) {
      this.loadFiltersFromLocalStorage();
    }

    // Inicializar la lista filtrada con las órdenes recibidas del componente padre
    if (this.orders && this.orders.length > 0) {
      this.applyOrderColumnFilters();
      this.calculateTotalPages();
    } else if (this.dataDrivenByParent) {
      // En /history el padre carga; no hacer loadPage(1) aquí para no pisar el resultado con una petición sin filtros
    } else {
      // Mostrar indicador de carga
      this.loading = true;
      this.loadingMessage = 'Obteniendo información de órdenes';
      this.loadingDetail = 'Calculando total de órdenes en la base de datos...';
      this.startLoadingAnimation();

      // Primero, obtener el total de órdenes en la base de datos
      this.orderService.getTotalOrdersCount().subscribe(
        totalCount => {
          this.totalOrdersInDB = totalCount;
          this.filteredTotal = totalCount; // Inicialmente, todas las órdenes están filtradas

          // Calcular el número total de páginas
          this.calculateTotalPages();

          // Cargar la primera página de órdenes
          this.loadingDetail = 'Cargando primera página de órdenes...';
          this.loadPage(1);

          // Verificar la consistencia de la paginación después de la carga inicial
          setTimeout(() => {
            this.verifyPaginationConsistency('después de carga inicial');
          }, 2000);
        },
        error => {
          console.error('Error al obtener el total de órdenes:', error);
          this.showErrorMessage('Error al obtener el total de órdenes. Cargando primera página...');

          // Si falla, intentar cargar la primera página de todos modos
          this.loadPage(1);
        }
      );
    }

    // Filtrar estados de repuestos según cliente por defecto
    this.updateFilteredSparePartStatus("AUTOCOMERCIAL");

    // Cargar lista de talleres
    this.loadWorkshops();
    this.loadProviders();

    // Depurar estado de paginación después de inicialización
    setTimeout(() => {
      this.debugPagination('después de ngOnInit');
    }, 2000);

    // Inicializar nombre de carpeta por defecto y verificar servicio
    try {
      this.folderName = this.exportedFoldersService.generateDefaultFolderName();
    } catch (error) {
      console.error('Error al inicializar ExportedFoldersService:', error);
      this.folderName = `Carpeta_${new Date().toISOString().split('T')[0].replace(/-/g, '')}`;
    }

    // Cargar estados de empaquetado de repuestos
    this.loadPackagingStatus();
  }

  loadProviders(): void {
    this.providersService.getProviders().subscribe({
      next: (providers) => {
        this.providers = (providers || []).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      },
      error: (error) => {
        console.error('Error al cargar proveedores en historial de órdenes:', error);
        this.providers = [];
      }
    });
  }

  getProviderOptions(currentValue?: string): string[] {
    const catalog = this.providers
      .map(provider => (provider.name || '').trim())
      .filter(name => !!name);

    const current = (currentValue || '').trim();
    if (!current) {
      return Array.from(new Set(catalog));
    }

    return Array.from(new Set([current, ...catalog]));
  }

  private loadDismissedDelayAlerts(): void {
    try {
      const raw = sessionStorage.getItem(this.DELAY_ALERTS_STORAGE_KEY);
      if (!raw) {
        this.dismissedDelayAlerts = new Set<string>();
        return;
      }
      const parsed = JSON.parse(raw);
      this.dismissedDelayAlerts = new Set<string>(Array.isArray(parsed) ? parsed : []);
    } catch {
      this.dismissedDelayAlerts = new Set<string>();
    }
  }

  private persistDismissedDelayAlerts(): void {
    try {
      sessionStorage.setItem(
        this.DELAY_ALERTS_STORAGE_KEY,
        JSON.stringify(Array.from(this.dismissedDelayAlerts))
      );
    } catch {
      // Ignore storage failures.
    }
  }

  private normalizeDateOnly(dateValue: Date | string | undefined): Date | null {
    if (!dateValue) return null;
    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return null;
    date.setHours(0, 0, 0, 0);
    return date;
  }

  getSparePartDelayDays(sparePart: SparePartModel): number {
    const exportDate = this.normalizeDateOnly(sparePart.fechaExportacion);
    const arrivalDate = this.normalizeDateOnly(sparePart.expectedArrivalDate);
    if (!exportDate || !arrivalDate) return 0;
    const diffMs = exportDate.getTime() - arrivalDate.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  }

  private getDelaySeverityByDays(delayDays: number): 'yellow' | 'orange' | 'red' | null {
    if (delayDays >= 20) return 'red';
    if (delayDays >= 10) return 'orange';
    if (delayDays >= 5) return 'yellow';
    return null;
  }

  getSparePartDelaySeverity(sparePart: SparePartModel): 'yellow' | 'orange' | 'red' | null {
    return this.getDelaySeverityByDays(this.getSparePartDelayDays(sparePart));
  }

  getOrderDelayDays(order: OrderOutModel): number {
    if (!order?.spareParts?.length) return 0;
    return order.spareParts.reduce((maxDelay, sparePart) => {
      const delay = this.getSparePartDelayDays(sparePart);
      return delay > maxDelay ? delay : maxDelay;
    }, 0);
  }

  /** Días de plazo del repuesto: emisión de la orden → F. Esp. del repuesto (fecha local). */
  getSparePartDiasPrazoFromOrderEmission(order: OrderOutModel, sparePart: SparePartModel): number | null {
    const emissionRaw = order?.creationDate ?? sparePart?.orderDate;
    if (!emissionRaw || sparePart?.expectedArrivalDate == null) return null;
    const emission = new Date(emissionRaw);
    const arrival = new Date(sparePart.expectedArrivalDate);
    if (isNaN(emission.getTime()) || isNaN(arrival.getTime())) return null;
    const a = new Date(emission.getFullYear(), emission.getMonth(), emission.getDate()).getTime();
    const b = new Date(arrival.getFullYear(), arrival.getMonth(), arrival.getDate()).getTime();
    return Math.round((b - a) / (1000 * 60 * 60 * 24));
  }

  /** Mínimo de días de plazo entre repuestos (emisión orden → F. Esp. de cada repuesto). */
  getOrderMinDiasPrazo(order: OrderOutModel): number | null {
    const parts = order?.spareParts || [];
    if (!parts.length) return null;
    let min: number | null = null;
    for (const sp of parts) {
      const d = this.getSparePartDiasPrazoFromOrderEmission(order, sp);
      if (d === null) continue;
      min = min === null ? d : Math.min(min, d);
    }
    return min;
  }

  formatOrderMinDiasPrazo(order: OrderOutModel): string {
    const n = this.getOrderMinDiasPrazo(order);
    return n === null || n === undefined ? '' : String(n);
  }

  getOrderDelaySeverity(order: OrderOutModel): 'yellow' | 'orange' | 'red' | null {
    return this.getDelaySeverityByDays(this.getOrderDelayDays(order));
  }

  private buildOrderDelayAlertKey(order: OrderOutModel, severity: 'yellow' | 'orange' | 'red'): string {
    return `order:${order.orderId}:${severity}`;
  }

  isOrderDelayDismissed(order: OrderOutModel): boolean {
    const severity = this.getOrderDelaySeverity(order);
    if (!severity) return false;
    return this.dismissedDelayAlerts.has(this.buildOrderDelayAlertKey(order, severity));
  }

  dismissOrderDelayAlert(event: Event, order: OrderOutModel): void {
    event.stopPropagation();
    const severity = this.getOrderDelaySeverity(order);
    if (!severity) return;
    this.dismissedDelayAlerts.add(this.buildOrderDelayAlertKey(order, severity));
    this.persistDismissedDelayAlerts();
  }

  getOrderDelayRowClass(order: OrderOutModel): string {
    if (this.isOrderDelayDismissed(order)) return '';
    const severity = this.getOrderDelaySeverity(order);
    if (severity === 'yellow') return 'delay-row-yellow';
    if (severity === 'orange') return 'delay-row-orange';
    if (severity === 'red') return 'delay-row-red';
    return '';
  }

  private getOrderStatusRowClass(order: OrderOutModel): string {
    if (order.orderStatus === 'CREADO') return 'bg-blue-50';
    if (order.orderStatus === 'LLEGO' || order.orderStatus === 'EN PROCESO') return 'bg-orange-50';
    if (order.orderStatus === 'FINALIZADO') return 'bg-green-50';
    if (order.orderStatus === 'CANCELADO' || order.orderStatus === 'ATENCION') return 'bg-red-50';
    if (order.orderStatus === 'PENDIENTE' || order.orderStatus === 'REPUESTOS-PENDIENTES') return 'bg-purple-50';
    return '';
  }

  getOrderRowClass(order: OrderOutModel): string {
    return [this.getOrderStatusRowClass(order), this.getOrderDelayRowClass(order)]
      .filter(Boolean)
      .join(' ');
  }

  getOrderDelayBadgeClass(order: OrderOutModel): string {
    if (this.isOrderDelayDismissed(order)) return '';
    const severity = this.getOrderDelaySeverity(order);
    if (severity === 'yellow') return 'delay-badge delay-yellow';
    if (severity === 'orange') return 'delay-badge delay-orange';
    if (severity === 'red') return 'delay-badge delay-red';
    return '';
  }

  hasOrderDelayAlert(order: OrderOutModel): boolean {
    return !!this.getOrderDelaySeverity(order) && !this.isOrderDelayDismissed(order);
  }

  getOrderDelayCellClass(order: OrderOutModel): string {
    if (!this.hasOrderDelayAlert(order)) return '';
    const severity = this.getOrderDelaySeverity(order);
    if (severity === 'yellow') return 'bg-yellow-100';
    if (severity === 'orange') return 'bg-orange-100';
    if (severity === 'red') return 'bg-red-100';
    return '';
  }

  getOrderDelayIconClass(order: OrderOutModel): string {
    if (!this.hasOrderDelayAlert(order)) return '';
    const severity = this.getOrderDelaySeverity(order);
    if (severity === 'yellow') return 'text-yellow-500';
    if (severity === 'orange') return 'text-orange-500';
    if (severity === 'red') return 'text-red-500';
    return '';
  }

  // Listener para cerrar dropdown cuando se hace clic fuera
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (this.exportDropdownContainer && !this.exportDropdownContainer.nativeElement.contains(event.target)) {
      this.showExportDropdown = false;
    }
    if (this.columnSelectorContainer && !this.columnSelectorContainer.nativeElement.contains(event.target)) {
      this.columnSelectorOpen = false;
    }
  }

  private loadColumnVisibility(): void {
    this.visibleColumnIds = this.columnVisibilityHelper.loadVisibleColumnIds();
    this.rebuildColumnVisibilityCss();
  }

  private persistColumnVisibility(): void {
    this.columnVisibilityHelper.persistVisibleColumnIds(this.visibleColumnIds);
  }

  private rebuildColumnVisibilityCss(): void {
    this.columnVisibilityCss = this.columnVisibilityHelper.buildCss(this.visibleColumnIds);
    this.syncColumnVisibilityStyleElement();
  }

  private syncColumnVisibilityStyleElement(): void {
    if (!this.columnVisibilityStyleElement) {
      const existing = document.getElementById('order-history-column-visibility-style') as HTMLStyleElement | null;
      this.columnVisibilityStyleElement = existing ?? document.createElement('style');
      this.columnVisibilityStyleElement.id = 'order-history-column-visibility-style';
      if (!existing) {
        document.head.appendChild(this.columnVisibilityStyleElement);
      }
    }

    this.columnVisibilityStyleElement.textContent = this.columnVisibilityCss || '';
  }

  isColumnVisible(columnId: string): boolean {
    return this.columnVisibilityHelper.isVisible(this.visibleColumnIds, columnId);
  }

  toggleColumnSelector(): void {
    this.columnSelectorOpen = !this.columnSelectorOpen;
  }

  toggleColumnVisibility(columnId: string): void {
    this.visibleColumnIds = this.columnVisibilityHelper.toggleColumn(this.visibleColumnIds, columnId);
    this.persistColumnVisibility();
    this.rebuildColumnVisibilityCss();
  }

  ngOnDestroy(): void {
    if (this.columnVisibilityStyleElement?.parentNode) {
      this.columnVisibilityStyleElement.parentNode.removeChild(this.columnVisibilityStyleElement);
    }
    this.columnVisibilityStyleElement = null;
  }

  ngOnChanges(changes: SimpleChanges) {
    const parentFiltersChanged =
      (changes['searchTerm'] && !changes['searchTerm'].firstChange) ||
      (changes['startDate'] && !changes['startDate'].firstChange) ||
      (changes['endDate'] && !changes['endDate'].firstChange) ||
      (changes['onlyLast60Days'] && !changes['onlyLast60Days'].firstChange);

    if (this.dataDrivenByParent && parentFiltersChanged) {
      this.clearAllOrdersBaseCache();
    }

    // Si cambian las órdenes, actualizar el total de órdenes y recalcular las páginas
    if (changes['orders'] && changes['orders'].currentValue) {
      this.filteredOrdersList = [...this.orders];
      this.syncOrdersTableAllSelectedHeader();

      this.calculateTotalPages();

      this.debugPagination('después de ngOnChanges');
      this.refreshKpis();
    }

    // Si cambia el total de órdenes desde el componente padre
    if (changes['totalOrders'] && changes['totalOrders'].currentValue !== undefined) {
      this.filteredTotal = this.totalOrders;

      // Actualizar también el total en la base de datos si es mayor
      if (this.totalOrders > this.totalOrdersInDB) {
        this.totalOrdersInDB = this.totalOrders;
      }

      this.calculateTotalPages();

      // Verificar si necesitamos actualizar la interfaz de usuario
      setTimeout(() => {
        this.debugPagination('después de actualizar totalOrders');
      }, 0);
    }

    // Si cambia el estado de carga, actualizar el estado local
    if (changes['loading'] !== undefined) {
      this.loadingMore = this.loading;
    }

    if (changes['loading'] && !changes['loading'].firstChange) {
      if (this.loading && this.loadingMessage.includes('Excel')) {
        this.startLoadingAnimation();
      } else if (!this.loading) {
        this.stopLoadingAnimation();
      }
    }

    // Si cambia el estado de allOrdersLoaded
    if (changes['allOrdersLoaded']) {
      this.allOrdersLoaded = changes['allOrdersLoaded'].currentValue;
    }

    // Si cambia el término de búsqueda desde un componente padre
    if (changes['searchTerm'] && !changes['searchTerm'].firstChange) {
      // No aplicamos filtros aquí para evitar bucles infinitos
    }

    // Si cambian las fechas desde el componente padre
    if ((changes['startDate'] || changes['endDate']) &&
      (!changes['startDate']?.firstChange || !changes['endDate']?.firstChange)) {
    }
  }

  /**
   * Inicia la animación de carga con progreso automático
   */
  startLoadingAnimation(): void {
    // Limpiar cualquier intervalo existente
    if (this.loadingInterval) {
      clearInterval(this.loadingInterval);
    }

    // Iniciar un intervalo para simular progreso si está estancado
    this.loadingInterval = setInterval(() => {
      // Solo incrementar automáticamente si el progreso está por debajo del 90%
      if (this.loadingProgress < 90) {
        // Incrementar gradualmente, más lento a medida que se acerca al 90%
        const increment = Math.max(0.1, (90 - this.loadingProgress) / 100);
        this.loadingProgress = Math.min(89.9, this.loadingProgress + increment);

        // Actualizar el mensaje según el progreso
        if (this.loadingProgress < 30) {
          this.loadingDetail = this.loadingDetail || 'Procesando datos...';
        } else if (this.loadingProgress < 60) {
          this.loadingDetail = this.loadingDetail || 'Casi listo...';
        } else {
          this.loadingDetail = this.loadingDetail || 'Finalizando...';
        }
      }
    }, 200);
  }

  /**
   * Detiene la animación de carga y finaliza el progreso
   */
  stopLoadingAnimation(): void {
    // Detener el intervalo
    if (this.loadingInterval) {
      clearInterval(this.loadingInterval);
      this.loadingInterval = null;
    }

    // Completar la barra de progreso
    this.loadingProgress = 100;
    this.loadingDetail = 'Completado';

    // Después de un breve momento, ocultar la barra de carga
    setTimeout(() => {
      this.loading = false;
      this.loadingProgress = 0;
      this.loadingMessage = '';
      this.loadingDetail = '';
    }, 1000);
  }

  // Método para cargar una página específica
  loadPage(page: number): void {
    if (this.loading) return;

    if (page < 1) page = 1;
    if (this.totalPages > 0 && page > this.totalPages) page = this.totalPages;

    this.sortedAndFilteredOrders = [];

    this.loading = true;
    this.currentPage = page;

    if (this.hasActiveCalculatedOrderFilters()) {
      this.loading = false;
      this.loadAllOrdersWithColumnFilters();
      return;
    }

    // Depurar estado de paginación
    this.debugPagination('antes de cargar datos');

    // Guardar los filtros en localStorage para mantenerlos entre navegaciones
    this.saveFiltersToLocalStorage();

    // Mostrar indicador de carga con información de filtros aplicados
    const loadingIndicator = document.createElement('div');
    loadingIndicator.id = 'page-loading-indicator';
    loadingIndicator.className = 'fixed top-4 right-4 bg-blue-500 text-white px-4 py-2 rounded shadow-lg z-50 flex flex-col';

    // Preparar mensaje con filtros aplicados
    let filtrosAplicados = '';
    if (this.searchTerm) {
      filtrosAplicados += `<div class="text-xs mt-1">Filtro de texto: "${this.searchTerm}"</div>`;
    }
    if (this.startDate || this.endDate) {
      const fechaInicio = this.startDate ? this.formatFilterDate(this.startDate) : 'inicio';
      const fechaFin = this.endDate ? this.formatFilterDate(this.endDate) : 'ahora';
      filtrosAplicados += `<div class="text-xs mt-1">Filtro de fecha: ${fechaInicio} a ${fechaFin}</div>`;
    }
    if (this.selectedWorkshopId) {
      const workshopName = this.workshops.find(w => w.workshopId === this.selectedWorkshopId)?.name || 'Taller';
      filtrosAplicados += `<div class="text-xs mt-1">Filtro de taller: ${workshopName}</div>`;
    }

    loadingIndicator.innerHTML = `
      <div class="flex items-center">
        <div class="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
        <span>Cargando página ${page}...</span>
      </div>
      ${filtrosAplicados}
    `;
    document.body.appendChild(loadingIndicator);

    // Cargar los datos de la página solicitada con los filtros aplicados
    this.orderService.getPaginatedOrders(
      page,
      this.pageSize,
      this.searchTerm,
      this.startDate,
      this.endDate,
      undefined, // productStatus
      this.selectedWorkshopId,
      this.onlyLast60Days ? 60 : undefined,
      this.sortedBy || undefined,
      this.sortOrder,
      this.getServerOrderColumnFilters()
    ).subscribe({
      next: (response) => {
        if (response && response.orders) {
          this.orders = response.orders;
          this.filteredOrdersList = [...this.orders];
          this.clearOrdersTableSelectionState();
          this.syncOrdersTableAllSelectedHeader();

          // Actualizar el total filtrado con el total de la respuesta
          this.filteredTotal = response.totalCount;
          this.calculateTotalPages();
          this.refreshKpis();
        } else {
          this.refreshKpis();
        }

        // Eliminar el indicador de carga
        const indicator = document.getElementById('page-loading-indicator');
        if (indicator) indicator.remove();

        this.loading = false;
      },
      error: (error) => {
        console.error('Error al cargar la página de órdenes:', error);
        this.showErrorMessage('Error al cargar los datos. Intente nuevamente.');

        // Eliminar el indicador de carga
        const indicator = document.getElementById('page-loading-indicator');
        if (indicator) indicator.remove();

        this.loading = false;
      }
    });
  }

  // Método para actualizar los contadores totales
  updateTotalCounts(): void {
    // Obtener el total de órdenes en la base de datos
    this.orderService.getTotalOrdersCount().subscribe({
      next: (count) => {
        this.totalOrdersInDB = count;

        // Recalcular el total de páginas solo si no hay filtros aplicados
        // y si no estamos en medio de una carga de página
        if (!this.loading && !this.searchTerm && !this.startDate && !this.endDate) {
          this.filteredTotal = count;
          this.calculateTotalPages();
          this.debugPagination('después de updateTotalCounts');
        } else {
        }
      },
      error: (error) => {
        console.error('Error al obtener el total de órdenes:', error);
      }
    });

    // Obtener el total de repuestos en la base de datos
    this.sparePartService.getTotalSparePartsCount().subscribe({
      next: (count) => {
        this.totalSpareParts = count;
      },
      error: (error) => {
        console.error('Error al obtener el total de repuestos:', error);
      }
    });
  }

  // Método auxiliar para contar el total de piezas de repuesto en las órdenes filtradas
  countTotalSpareParts(): number {
    return this.kpiTotalSparePartsCount;
  }

  // Método para obtener los números de página a mostrar
  getPageNumbers(): number[] {
    // NO verificar la consistencia aquí para evitar loops infinitos
    // this.verifyPaginationConsistency('en getPageNumbers');

    const pages: number[] = [];
    const maxVisiblePages = 7; // Reducido a 7 para una mejor visualización

    // Verificar que totalPages sea correcto
    // Si no hay páginas o solo hay una, mostrar solo la página 1
    if (this.totalPages <= 1) {
      return [1];
    }

    if (this.totalPages <= maxVisiblePages) {
      // Si hay menos páginas que el máximo visible, mostrar todas
      for (let i = 1; i <= this.totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Siempre mostrar la primera página
      pages.push(1);

      // Si la página actual está lejos de la primera, mostrar puntos suspensivos
      if (this.currentPage > 3) {
        pages.push(-1); // -1 representa "..."
      }

      // Calcular el rango de páginas alrededor de la página actual
      let startPage = Math.max(2, this.currentPage - 1);
      let endPage = Math.min(this.totalPages - 1, this.currentPage + 1);

      // Ajustar para mostrar al menos 3 páginas en el medio cuando sea posible
      if (endPage - startPage < 2) {
        if (startPage === 2) {
          endPage = Math.min(this.totalPages - 1, startPage + 2);
        } else if (endPage === this.totalPages - 1) {
          startPage = Math.max(2, endPage - 2);
        }
      }

      // Agregar páginas del rango calculado
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }

      // Si la página actual está lejos de la última, mostrar puntos suspensivos
      if (this.currentPage < this.totalPages - 2) {
        pages.push(-1); // -1 representa "..."
      }

      // Siempre mostrar la última página
      pages.push(this.totalPages);
    }
    return pages;
  }

  // Método para ir a una página específica
  goToPage(page: number): void {
    if (this.loading || page === this.currentPage) return;

    if (page < 1) page = 1;
    if (this.totalPages > 0 && page > this.totalPages) page = this.totalPages;

    this.currentPage = page;
    this.loadPage(page);
  }

  // Método para cambiar el tamaño de página
  onPageSizeChange(): void {
    this.currentPage = 1;
    this.clearAllOrdersBaseCache();
    this.loadPage(1);
  }

  onWorkshopFilterChange(): void {
    this.currentPage = 1; // Volver a la primera página

    // Guardar filtros
    this.saveFiltersToLocalStorage();

    // Cargar la primera página con el nuevo filtro
    this.loadPage(1);
  }

  // Método para calcular el total de páginas
  calculateTotalPages(): void {
    // Calcular el número total de páginas basado en el total de órdenes filtradas
    if (this.filteredTotal <= 0) {
      this.totalPages = 1;
    } else {
      this.totalPages = Math.max(1, Math.ceil(this.filteredTotal / this.pageSize));
    }

    // Corrección proactiva de página actual
    if (this.currentPage > this.totalPages) {
      this.currentPage = this.totalPages;
    }

    // Forzar la detección de cambios para actualizar la interfaz de usuario
    setTimeout(() => {
      this.debugPagination('después de calculateTotalPages');
    }, 0);

    // Persistir cambios
    this.saveFiltersToLocalStorage();
  }

  // Método para cargar todas las órdenes (mantener para compatibilidad)
  async loadAllOrders() {
    if (this.loading) return;

    this.loading = true;
    this.loadingMessage = 'Cargando todas las órdenes';
    this.loadingDetail = 'Obteniendo datos completos...';
    this.startLoadingAnimation();

    try {
      // Primero, obtener el total de órdenes en la base de datos
      const totalCountResponse = await this.orderService.getTotalOrdersCount().toPromise();
      const totalCount = totalCountResponse || 0; // Asegurar que siempre sea un número
      this.totalOrdersInDB = totalCount;
      this.filteredTotal = totalCount;

      // Calcular cuántas páginas necesitamos cargar
      const totalPages = Math.ceil(totalCount / this.pageSize);
      // Preparar array para almacenar todas las órdenes
      let allOrders: OrderOutModel[] = [];

      // Cargar todas las páginas
      for (let page = 1; page <= totalPages; page++) {
        this.loadingDetail = `Cargando página ${page} de ${totalPages}...`;
        this.loadingProgress = Math.floor((page / totalPages) * 100);

        try {
          const response = await this.orderService.getPaginatedOrders(
            page,
            this.pageSize,
            this.searchTerm,
            this.startDate,
            this.endDate,
            undefined,
            this.selectedWorkshopId ?? undefined,
            this.onlyLast60Days ? 60 : undefined,
            this.sortedBy || undefined,
            this.sortOrder,
            this.getServerOrderColumnFilters()
          ).toPromise();
          if (response && response.orders) {
            allOrders = [...allOrders, ...response.orders];
          }
        } catch (error) {
          console.error(`Error cargando página ${page}:`, error);
        }

        // Pequeña pausa para no sobrecargar el servidor
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      // Actualizar los datos
      this.orders = allOrders;
      this.filteredOrdersList = [...this.orders];
      this.clearOrdersTableSelectionState();
      this.syncOrdersTableAllSelectedHeader();
      this.calculateTotalPages();

      // Actualizar contadores
      this.updateTotalCounts();

      // Volver a la primera página
      this.currentPage = 1;

      return this.orders;
    } catch (error) {
      console.error('Error cargando todas las órdenes:', error);
      this.showErrorMessage('Error al cargar las órdenes. Intente nuevamente.');
      throw error;
    } finally {
      this.stopLoadingAnimation();
      this.loading = false;
    }
  }

  onSearchChange(searchTerm: string): void {
    // Emitir el evento solo si no estamos en medio de una carga
    if (!this.loading) {
      this.searchFilterChanged.emit(searchTerm);
    }
  }

  // Sin trackBy, Angular destruye y recrea todas las filas cada vez que el array cambia
  // de identidad. Las listas de entidades se siguen por su id, que es unico por venir de
  // la base. Las de strings y numeros van por indice a proposito: pueden repetir valores
  // —el pager usa -1 dos veces para los "..."— y una clave duplicada no degrada el
  // rendimiento, hace explotar el render con NG0955.
  trackByOrderId(_index: number, order: OrderOutModel): number {
    return order.orderId;
  }

  trackBySparePartId(_index: number, sparePart: SparePartModel): number {
    return sparePart.sparePartId;
  }

  trackByIndex(index: number): number {
    return index;
  }

  // El template llama a getAllSpareParts() dentro de un *ngFor, asi que sin cache la
  // lista se reconstruye —clonando cada repuesto— en cada ciclo de deteccion de cambios.
  // La invalidacion es por identidad: la del array de ordenes y la del array spareParts
  // de cada orden. Todos los puntos que reasignan esas listas crean un array nuevo, asi
  // que quedan cubiertos sin tener que acordarse de nada. Las mutaciones de un campo
  // suelto, que no cambian ninguna identidad, llaman a invalidateAllSparePartsCache().
  private allSparePartsCache: SparePartModel[] = [];
  private allSparePartsCacheSource: OrderOutModel[] | null = null;
  private allSparePartsCacheParts: (SparePartModel[] | undefined)[] = [];

  private invalidateAllSparePartsCache(): void {
    this.allSparePartsCacheSource = null;
  }

  getAllSpareParts(): SparePartModel[] {
    const source = this.getActiveOrdersForKpis();

    if (this.allSparePartsCacheSource === source &&
        source.every((order, i) => this.allSparePartsCacheParts[i] === order.spareParts)) {
      return this.allSparePartsCache;
    }

    const allSpareParts: SparePartModel[] = [];
    source.forEach((order: OrderOutModel) => {
      if (order.spareParts) {
        order.spareParts.forEach((sparePart: SparePartModel) => {
          allSpareParts.push({ ...sparePart, orderNumber: order.orderNumber });
        });
      }
    });

    this.allSparePartsCacheSource = source;
    this.allSparePartsCacheParts = source.map(order => order.spareParts);
    this.allSparePartsCache = allSpareParts;
    return allSpareParts;
  }

  onDateFilterChange(): void {
    let startDate: Date | null = null;
    let endDate: Date | null = null;

    switch (this.dateFilterType) {
      case 'today':
        startDate = new Date();
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date();
        endDate.setHours(23, 59, 59, 999);
        break;

      case 'last7days':
        endDate = new Date();
        endDate.setHours(23, 59, 59, 999);
        startDate = new Date();
        startDate.setDate(startDate.getDate() - 7);
        startDate.setHours(0, 0, 0, 0);
        break;

      case 'last30days':
        endDate = new Date();
        endDate.setHours(23, 59, 59, 999);
        startDate = new Date();
        startDate.setDate(startDate.getDate() - 30);
        startDate.setHours(0, 0, 0, 0);
        break;

      case 'custom':
        if (this.customStartDate) {
          startDate = new Date(this.customStartDate);
          startDate.setHours(0, 0, 0, 0);
        }
        if (this.customEndDate) {
          endDate = new Date(this.customEndDate);
          endDate.setHours(23, 59, 59, 999);
        }
        break;

      default: // 'all'
        startDate = null;
        endDate = null;
        break;
    }

    // Actualizar las fechas locales
    // No asignamos directamente a this.startDate y this.endDate porque son inputs
    // y deben ser actualizados por el componente padre

    // Emitir el evento con las fechas solo si no estamos en medio de una carga
    if (!this.loading) {
      this.dateFilterChanged.emit({
        startDate: startDate || undefined,
        endDate: endDate || undefined
      });
    }

    // Guardar filtros en localStorage
    this.saveFiltersToLocalStorage();
  }

  clearSearch(): void {
    this.searchTerm = '';
    this.searchFilterChanged.emit('');
  }

  clearDateFilter(): void {
    this.dateFilterType = 'all';
    this.customStartDate = '';
    this.customEndDate = '';
    this.dateFilterChanged.emit({});
  }

  filteredOrders(): OrderOutModel[] {
    const term = this.normalizeString(this.searchTerm);
    if (term === '') {
      return this.orders;
    }
    return this.orders.filter(order =>
      this.matchesOrderFields(order) ||
      (order.spareParts && order.spareParts.some(sp => this.matchesSparePartFields(sp, term)))
    );
  }

  private matchesSparePartFields(sparePart: SparePartModel, term: string): boolean {
    const terms = term.split(/\s+/);
    return terms.every(subTerm =>
      this.normalizeString(sparePart.sparePartName).includes(subTerm) ||
      this.normalizeString(sparePart.comments).includes(subTerm) ||
      this.normalizeString(sparePart.emergentComment || sparePart.effectiveEmergentComment || '').includes(subTerm) ||
      this.normalizeString(sparePart.productStatus).includes(subTerm) ||
      this.normalizeString(sparePart.taxNote).includes(subTerm) ||
      this.normalizeString(sparePart.productCode).includes(subTerm) ||
      this.normalizeString(sparePart.expectedArrivalDate.toString()).includes(subTerm) ||
      sparePart.unitPrice.toString().includes(subTerm) ||
      sparePart.amount.toString().includes(subTerm) ||
      sparePart.valorDeVenta.toString().includes(subTerm)
    );
  }

  private matchesOrderFields(order: OrderOutModel): boolean {
    const term = this.normalizeString(this.searchTerm);
    return this.normalizeString(order.creationDate).includes(term) ||
      this.normalizeString(order.siniestro).includes(term) ||
      this.normalizeString(order.orderNumber.toString()).includes(term) ||
      this.normalizeString(order.expectedArrivalDate).includes(term) ||
      this.normalizeString(order.providerName).includes(term) ||
      this.normalizeString(order.providerPhone).includes(term) ||
      this.normalizeString(order.clientName).includes(term) ||
      this.normalizeString(order.clientPhone).includes(term) ||
      this.normalizeString(order.invoiceNumber).includes(term) ||
      this.normalizeString(order.employeeName).includes(term) ||
      this.normalizeString(order.orderStatus).includes(term) ||
      order.totalPrice.toString().includes(term) ||
      (term === "alerta" && order.isNearArrival && !order.isNearArrivalAccepted);
  }

  matchesSearchTerm(order: OrderOutModel): boolean {
    // Si no hay término de búsqueda, mostrar todas las órdenes
    if (!this.searchTerm || this.searchTerm.trim() === '') {
      return true;
    }

    // Dividir el término de búsqueda en palabras individuales
    const searchTerms = this.normalizeString(this.searchTerm).split(/\s+/).filter(term => term.length > 0);

    if (searchTerms.length === 0) {
      return true;
    }

    // Verificar si todos los términos de búsqueda están presentes en los campos de la orden
    const matchesOrderFields = searchTerms.every(term => {
      return this.normalizeString(order.creationDate || '').includes(term) ||
        this.normalizeString(order.siniestro || '').includes(term) ||
        this.normalizeString(order.orderNumber?.toString() || '').includes(term) ||
        this.normalizeString(order.expectedArrivalDate || '').includes(term) ||
        this.normalizeString(order.providerName || '').includes(term) ||
        this.normalizeString(order.providerPhone || '').includes(term) ||
        this.normalizeString(order.clientName || '').includes(term) ||
        this.normalizeString(order.clientPhone || '').includes(term) ||
        this.normalizeString(order.invoiceNumber || '').includes(term) ||
        this.normalizeString(order.employeeName || '').includes(term) ||
        this.normalizeString(order.orderStatus || '').includes(term) ||
        this.normalizeString(order.deliveryAddress || '').includes(term) ||
        this.normalizeString(order.carMakeModel || '').includes(term) ||
        this.normalizeString(String(order.isExport) || '').includes(term) ||
        this.normalizeString(String(order.sendYesOrNo) || '').includes(term) ||
        this.normalizeString(order.totalPrice?.toString() || '').includes(term) ||
        (term === "alerta" && order.isNearArrival && !order.isNearArrivalAccepted) ||
        (this.getOrderMinDiasPrazo(order) !== null &&
          this.normalizeString(String(this.getOrderMinDiasPrazo(order))).includes(term));
    });

    if (matchesOrderFields) {
      return true;
    }

    // Verificar si algún repuesto coincide con todos los términos de búsqueda
    if (order.spareParts && order.spareParts.length > 0) {
      const matchingSpareParts = order.spareParts.filter(sparePart => {
        return searchTerms.every(term => {
          return this.normalizeString(sparePart.sparePartName || '').includes(term) ||
            this.normalizeString(sparePart.productCode || '').includes(term) ||
            this.normalizeString(sparePart.productStatus || '').includes(term) ||
            this.normalizeString(sparePart.taxNote || '').includes(term) ||
            this.normalizeString(sparePart.ncm || '').includes(term) ||
            this.normalizeString(sparePart.comments || '').includes(term) ||
            this.normalizeString(sparePart.emergentComment || sparePart.effectiveEmergentComment || '').includes(term) ||
            this.normalizeString(sparePart.expectedArrivalDate?.toString() || '').includes(term) ||
            this.normalizeString(sparePart.unitPrice?.toString() || '').includes(term) ||
            this.normalizeString(sparePart.amount?.toString() || '').includes(term) ||
            this.normalizeString(sparePart.valorDeVenta?.toString() || '').includes(term) ||
            this.normalizeString(sparePart['codBR'] || '').includes(term) ||
            this.normalizeString(sparePart.country || '').includes(term) ||
            this.normalizeString(sparePart.shippingStatus || '').includes(term) ||
            this.normalizeString(sparePart.shippingReceipt || '').includes(term);
        });
      });

      // Si hay repuestos que coinciden, actualizar la lista de repuestos de la orden
      if (matchingSpareParts.length > 0) {
        // Solo mostrar los repuestos que coinciden con la búsqueda
        order.spareParts = matchingSpareParts;
        return true;
      }
    }

    return false;
  }

  matchesSearchTermSparePart(sparePart: SparePartModel): boolean {
    if (!this.searchTerm) return true;

    const term = this.normalizeString(this.searchTerm);

    // Lista completa de estados posibles de repuestos
    const possibleStatuses = this.sparePartDeliveryStatus.map(status => this.normalizeString(status));

    // Si estamos buscando por un estado específico
    if (possibleStatuses.includes(term)) {
      // Verificar coincidencia EXACTA para estados
      return this.normalizeString(sparePart.productStatus) === term;
    }

    // Para otros campos, verificar si contienen el término
    return this.normalizeString(sparePart.sparePartName).includes(term) ||
      this.normalizeString(sparePart.taxNote).includes(term) ||
      this.normalizeString(sparePart.ncm).includes(term) ||
      this.normalizeString(sparePart.productCode).includes(term) ||
      this.normalizeString(sparePart.expectedArrivalDate.toString()).includes(term) ||
      this.normalizeString(sparePart.comments).includes(term) ||
      this.normalizeString(sparePart.emergentComment || sparePart.effectiveEmergentComment || '').includes(term) ||
      sparePart.unitPrice.toString().includes(term) ||
      sparePart.amount.toString().includes(term) ||
      sparePart.valorDeVenta.toString().includes(term);
  }

  normalizeString(str: string): string {
    if (!str) return '';
    return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  }

  matchesDateFilter(order: OrderOutModel): boolean {
    const creationDate = new Date(order.creationDate).getTime(); // Obtener el timestamp en milisegundos
    const now = Date.now(); // Timestamp actual

    let relativeFilterPass = true;
    switch (this.dateFilterType) {
      case 'week':
        relativeFilterPass = this.isWithinLastDays(new Date(creationDate), 7);
        break;
      case 'month':
        relativeFilterPass = this.isWithinLastDays(new Date(creationDate), 30);
        break;
      case '3months':
        relativeFilterPass = this.isWithinLastDays(new Date(creationDate), 90);
        break;
      case 'all':
      default:
        relativeFilterPass = true;
        break;
    }

    let customFilterPass = true;

    // Ajustamos la fecha de inicio para que considere desde las 00:00:00 del día
    if (this.startDate) {
      const startDate = new Date(this.startDate).setHours(0, 0, 0, 0); // Desde las 00:00:00
      customFilterPass = creationDate >= startDate;
    }

    // Ajustamos la fecha de fin para que considere hasta las 23:59:59 del día
    if (this.endDate) {
      const endDate = new Date(this.endDate).setUTCHours(23, 59, 59, 999); // Asegura que el fin del día sea UTC
      customFilterPass = customFilterPass && creationDate <= endDate;
    }

    return relativeFilterPass && customFilterPass;
  }


  // Show floating filter for column (multi-selección, posicionamiento dentro del viewport)
  showColumnFilter(event: MouseEvent, column: string): void {
    event.stopPropagation();
    this.filterColumn = column;
    this.filterValue = '';

    // Inicializar selección temporal desde el filtro actual
    this.tempColumnFilterSelection = this.columnFilters[column] ? [...this.columnFilters[column]] : [];

    // Valores únicos desde todo el set de órdenes (igual que repuestos)
    this.loadingUniqueValues = true;
    this.columnUniqueValues = [];
    this.filteredColumnValues = [];
    this.showFilterPopup = true;

    const columnFilters = this.getDistinctOrderColumnFiltersExcept(column);
    this.orderService.getDistinctColumnValues(
      column,
      this.searchTerm,
      this.startDate,
      this.endDate,
      this.selectedWorkshopId,
      columnFilters,
      this.onlyLast60Days ? 60 : undefined
    )
      .pipe(finalize(() => { this.loadingUniqueValues = false; this.cdr.detectChanges(); }))
      .subscribe({
        next: (response) => {
          const values = [...(response?.values || [])];
          if (response?.hasEmpty) {
            values.push(this.EMPTY_FILTER_VALUE);
          }
          this.columnUniqueValues = values;
          this.filteredColumnValues = [...values];
          this.cdr.detectChanges();
        },
        error: () => {}
      });

    const rect = (event.target as HTMLElement).getBoundingClientRect();
    let x = rect.left;
    let y = rect.bottom + 5;
    if (y + this.FILTER_POPUP_HEIGHT > window.innerHeight - this.FILTER_POPUP_MARGIN) {
      y = rect.top - this.FILTER_POPUP_HEIGHT - 5;
    }
    if (y < this.FILTER_POPUP_MARGIN) {
      y = this.FILTER_POPUP_MARGIN;
    }
    if (x + this.FILTER_POPUP_WIDTH > window.innerWidth - this.FILTER_POPUP_MARGIN) {
      x = window.innerWidth - this.FILTER_POPUP_WIDTH - this.FILTER_POPUP_MARGIN;
    }
    if (x < this.FILTER_POPUP_MARGIN) {
      x = this.FILTER_POPUP_MARGIN;
    }
    this.filterPopupPosition = { x, y };
    // Altura máxima para que el popup no se corte: espacio desde y hasta el borde del viewport
    this.filterPopupMaxHeight = window.innerHeight - y - this.FILTER_POPUP_MARGIN;
  }

  /** True si hay al menos un filtro por columna activo (igual que repuestos). */
  private hasActiveOrderColumnFilters(): boolean {
    return Object.values(this.columnFilters).some(arr => Array.isArray(arr) && arr.length > 0);
  }

  private isClientSideCalculatedOrderColumn(column?: string | null): boolean {
    if (!column) {
      return false;
    }
    return this.CLIENT_SIDE_CALCULATED_COLUMNS.has(column);
  }

  private hasActiveCalculatedOrderFilters(): boolean {
    return false;
  }

  /** Indica si una columna específica tiene filtro aplicado (para destacar encabezado). */
  isOrderColumnFiltered(column: string): boolean {
    const selectedValues = this.columnFilters[column];
    return Array.isArray(selectedValues) && selectedValues.length > 0;
  }

  /** Indica si hay al menos un filtro por columna activo (para template). */
  get hasAnyOrderColumnFilter(): boolean {
    return this.hasActiveOrderColumnFilters();
  }

  /** Limpia todos los filtros por columna y refresca la tabla de órdenes. */
  clearAllOrderColumnFilters(): void {
    this.columnFilters = {};
    this.applyOrderColumnFilters();
  }

  /** Carga todas las órdenes (respetando filtros backend), aplica filtros por columna en cliente y pagina en cliente (igual que repuestos). */
  private loadAllOrdersWithColumnFilters(): void {
    this.loading = true;
    this.fetchAllOrdersWithCurrentFilters()
      .pipe(finalize(() => { this.loading = false; this.cdr.detectChanges(); }))
      .subscribe({
        next: (allOrders) => {
          const filtered = allOrders.filter(o => this.passesOrderColumnFilters(o));
          this.sortedAndFilteredOrders = this.sortedBy
            ? this.sortOrdersList(filtered, this.sortedBy, this.sortOrder)
            : filtered;
          this.filteredTotal = this.sortedAndFilteredOrders.length;
          this.calculateTotalPages();
          if (this.currentPage > this.totalPages && this.totalPages > 0) {
            this.currentPage = 1;
          }
          this.updateDisplayedOrdersPage();
          this.refreshKpis(this.sortedAndFilteredOrders);
        },
        error: () => {}
      });
  }

  /** Aplica los filtros por columna. Si hay filtros activos, busca en todo el set (igual que repuestos); si no, filtra solo la página actual. */
  applyOrderColumnFilters(): void {
    this.currentPage = 1;
    this.clearAllOrdersBaseCache();
    this.loadPage(1);
  }

  /** Carga todas las órdenes con los filtros actuales (para ordenar sobre el conjunto completo). */
  private fetchAllOrdersWithCurrentFilters(
    page: number = 1,
    accumulated: OrderOutModel[] = []
  ): Observable<OrderOutModel[]> {
    const currentKey = this.buildOrdersCacheKey();
    if (page === 1 && accumulated.length === 0 && this.allOrdersBaseCache && this.allOrdersBaseCacheKey === currentKey) {
      return of(this.allOrdersBaseCache);
    }

    return this.fetchAllOrdersWithCurrentFiltersFromApi(page, accumulated).pipe(
      switchMap(all => {
        if (page === 1 && accumulated.length === 0) {
          this.allOrdersBaseCache = all;
          this.allOrdersBaseCacheKey = currentKey;
        }
        return of(all);
      })
    );
  }

  private clearAllOrdersBaseCache(): void {
    this.allOrdersBaseCache = null;
    this.allOrdersBaseCacheKey = '';
    this.sortedAndFilteredOrders = [];
  }

  private buildOrdersCacheKey(): string {
    return JSON.stringify({
      searchTerm: (this.searchTerm || '').trim(),
      startDate: this.startDate ? new Date(this.startDate).getTime() : null,
      endDate: this.endDate ? new Date(this.endDate).getTime() : null,
      workshopId: this.selectedWorkshopId ?? null,
      onlyLast60Days: this.onlyLast60Days,
      sortBy: this.sortedBy || null,
      sortOrder: this.sortOrder,
      columnFilters: this.getServerOrderColumnFilters()
    });
  }

  private getServerOrderColumnFilters(): Record<string, string[]> {
    const filters: Record<string, string[]> = {};
    Object.entries(this.columnFilters).forEach(([column, values]) => {
      if (!Array.isArray(values) || values.length === 0) {
        return;
      }

      const normalized = values
        .map(v => (v || '').trim())
        .filter(v => v.length > 0);

      if (normalized.length > 0) {
        filters[column] = normalized;
      }
    });
    return filters;
  }

  private getServerOrderColumnFiltersExcept(exceptColumn?: string): Record<string, string[]> {
    const filters = this.getServerOrderColumnFilters();
    if (exceptColumn && Object.prototype.hasOwnProperty.call(filters, exceptColumn)) {
      delete filters[exceptColumn];
    }
    return filters;
  }

  private getDistinctOrderColumnFiltersExcept(exceptColumn?: string): Record<string, string[]> {
    const filters: Record<string, string[]> = {};
    Object.entries(this.columnFilters).forEach(([column, values]) => {
      if (exceptColumn && column === exceptColumn) return;
      if (!Array.isArray(values) || values.length === 0) return;
      const normalized = values
        .map(v => (v || '').trim())
        .filter(v => v.length > 0);
      if (normalized.length > 0) {
        filters[column] = normalized;
      }
    });
    return filters;
  }

  private fetchAllOrdersWithCurrentFiltersFromApi(
    page: number = 1,
    accumulated: OrderOutModel[] = []
  ): Observable<OrderOutModel[]> {
    return this.orderService.getPaginatedOrders(
      page,
      500,
      this.searchTerm,
      this.startDate,
      this.endDate,
      undefined,
      this.selectedWorkshopId ?? undefined,
      this.onlyLast60Days ? 60 : undefined,
      this.sortedBy || undefined,
      this.sortOrder,
      this.getServerOrderColumnFilters()
    ).pipe(
      switchMap(response => {
        const all = [...accumulated, ...response.orders];
        const totalPages = Math.ceil(response.totalCount / response.pageSize);
        if (page < totalPages) {
          return this.fetchAllOrdersWithCurrentFiltersFromApi(page + 1, all);
        }
        return of(all);
      })
    );
  }

  /** Actualiza filteredOrdersList con el slice de la página actual cuando hay datos en cliente (filtros por columna y/o orden). */
  private updateDisplayedOrdersPage(): void {
    if (this.sortedAndFilteredOrders.length === 0) return;
    const start = (this.currentPage - 1) * this.pageSize;
    const end = start + this.pageSize;
    this.filteredOrdersList = this.sortedAndFilteredOrders.slice(start, end);
    this.clearOrdersTableSelectionState();
    this.syncOrdersTableAllSelectedHeader();
    this.cdr.detectChanges();
  }

  private clearOrdersTableSelectionState(): void {
    this.ordersTableAllSelected = false;

    const clearSelection = (list: OrderOutModel[] | undefined): void => {
      (list || []).forEach(order => {
        order.selected = false;
      });
    };

    clearSelection(this.filteredOrdersList);
    clearSelection(this.sortedAndFilteredOrders);
    clearSelection(this.orders);
  }

  private syncOrdersTableAllSelectedHeader(): void {
    this.ordersTableAllSelected =
      this.filteredOrdersList.length > 0 &&
      this.filteredOrdersList.every(o => !!o.selected);
  }

  toggleAllOrdersTableSelection(): void {
    const checked = this.ordersTableAllSelected;
    this.filteredOrdersList.forEach(o => { o.selected = checked; });
  }

  onOrderTableRowSelectionChange(): void {
    this.syncOrdersTableAllSelectedHeader();
  }

  onDuplicateOrderFromTable(order: OrderOutModel, event: Event): void {
    event.stopPropagation();
    this.openModal(order);
    setTimeout(() => {
      if (this.selectedOrder?.orderId === order.orderId) {
        this.duplicateOrderWithChanges();
      }
    }, 0);
  }

  /** Ordena una lista de órdenes por columna y dirección; devuelve nueva lista. */
  private sortOrdersList(
    list: OrderOutModel[],
    column: string,
    order: 'asc' | 'desc'
  ): OrderOutModel[] {
    const sorted = [...list];
    sorted.sort((a, b) => {
      let valueA: any;
      let valueB: any;
      switch (column) {
        case 'id':
          valueA = a.orderId;
          valueB = b.orderId;
          break;
        case 'creationDate': {
          const ta = a.creationDate ? new Date(a.creationDate).getTime() : 0;
          const tb = b.creationDate ? new Date(b.creationDate).getTime() : 0;
          valueA = (typeof ta === 'number' && !isNaN(ta)) ? ta : 0;
          valueB = (typeof tb === 'number' && !isNaN(tb)) ? tb : 0;
          break;
        }
        case 'orderNumber':
          valueA = a.orderNumber || '';
          valueB = b.orderNumber || '';
          break;
        case 'siniestro':
          valueA = a.siniestro || '';
          valueB = b.siniestro || '';
          break;
        case 'invoiceNumber':
          valueA = a.invoiceNumber || '';
          valueB = b.invoiceNumber || '';
          break;
        case 'clienteUY':
          valueA = a.clienteUY || '';
          valueB = b.clienteUY || '';
          break;
        case 'clientName':
          valueA = a.clientName || '';
          valueB = b.clientName || '';
          break;
        case 'providerName':
          valueA = a.providerName || '';
          valueB = b.providerName || '';
          break;
        case 'stateOrder':
          valueA = a.orderStatus || '';
          valueB = b.orderStatus || '';
          break;
        case 'expectedArrivalDate': {
          const ta = a.expectedArrivalDate ? new Date(a.expectedArrivalDate).getTime() : 0;
          const tb = b.expectedArrivalDate ? new Date(b.expectedArrivalDate).getTime() : 0;
          valueA = (typeof ta === 'number' && !isNaN(ta)) ? ta : 0;
          valueB = (typeof tb === 'number' && !isNaN(tb)) ? tb : 0;
          break;
        }
        case 'employeeName':
          valueA = a.employeeName || '';
          valueB = b.employeeName || '';
          break;
        case 'estimatedCost':
          valueA = this.getTotalCost(a) ?? 0;
          valueB = this.getTotalCost(b) ?? 0;
          break;
        default:
          valueA = (a as any)[column] ?? '';
          valueB = (b as any)[column] ?? '';
      }
      if (valueA == null) valueA = '';
      if (valueB == null) valueB = '';
      if (valueA < valueB) return order === 'asc' ? -1 : 1;
      if (valueA > valueB) return order === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }

  /** Valor de una orden para una columna (mismo mapeo que getUniqueColumnValues). */
  private getOrderColumnValue(order: OrderOutModel, columnDisplayName: string): string {
    let value: any = '';
    switch (columnDisplayName) {
      case 'ID':
        value = order.orderId?.toString() || '';
        break;
      case 'Fecha de Creación':
        value = order.creationDate ? new Date(order.creationDate).toLocaleDateString('es-UY') : '';
        break;
      case 'Orden':
        value = order.orderNumber || '';
        break;
      case 'Siniestro':
        value = order.siniestro || '';
        break;
      case 'Factura':
        value = order.invoiceNumber || '';
        break;
      case 'Cliente Uy':
        value = order.clienteUY || '';
        break;
      case 'Cliente':
        value = order.clientName || '';
        break;
      case 'Proveedor':
        value = order.providerName || '';
        break;
      case 'Estado':
        value = order.orderStatus || '';
        break;
      case 'Fecha de Llegada':
        value = order.expectedArrivalDate ? new Date(order.expectedArrivalDate).toLocaleDateString('es-UY') : '';
        break;
      case 'Dias de atraso':
        value = this.getOrderDelayDays(order).toString();
        break;
      case 'Dias de plazo':
        value = this.formatOrderMinDiasPrazo(order);
        break;
      case 'Vendedor':
        value = order.employeeName || '';
        break;
      case 'Costo':
        value = this.getTotalCost(order)?.toString() || '';
        break;
      default:
        value = '';
    }
    return value != null ? value.toString().trim() : '';
  }

  private readonly PREFIX_ORDEN = 'Orden: ';
  private readonly PREFIX_REPUESTO = 'Repuesto: ';
  private readonly EMPTY_FILTER_VALUE = '(vacío)';

  /** True si la orden pasa todos los filtros por columna aplicados. */
  private passesOrderColumnFilters(order: OrderOutModel): boolean {
    for (const [column, selectedValues] of Object.entries(this.columnFilters)) {
      if (!selectedValues || selectedValues.length === 0) continue;

      if (column === 'Estado') {
        const passes = selectedValues.some(v => {
          if (v === this.EMPTY_FILTER_VALUE || v === '(vazio)') {
            const isOrderStatusEmpty = !(order.orderStatus || '').trim();
            const hasEmptySparePartStatus = (order.spareParts || []).some(sp => !(sp.productStatus || '').trim());
            return isOrderStatusEmpty || hasEmptySparePartStatus;
          }
          if (v.startsWith(this.PREFIX_ORDEN)) {
            return (order.orderStatus || '') === v.slice(this.PREFIX_ORDEN.length);
          }
          if (v.startsWith(this.PREFIX_REPUESTO)) {
            const status = v.slice(this.PREFIX_REPUESTO.length);
            return (order.spareParts || []).some(sp => (sp.productStatus || '') === status);
          }
          return false;
        });
        if (!passes) return false;
        continue;
      }

      const orderValue = this.getOrderColumnValue(order, column);
      const includesEmpty = selectedValues.includes(this.EMPTY_FILTER_VALUE) || selectedValues.includes('(vazio)');
      const matchesEmpty = includesEmpty && !orderValue.trim();
      if (!selectedValues.includes(orderValue) && !matchesEmpty) {
        return false;
      }
    }
    return true;
  }

  /** True si la orden pasa todos los filtros por columna excepto la columna indicada (cascada tipo Excel). */
  private passesOrderColumnFiltersExcept(order: OrderOutModel, exceptColumn: string): boolean {
    for (const [column, selectedValues] of Object.entries(this.columnFilters)) {
      if (column === exceptColumn || !selectedValues || selectedValues.length === 0) continue;

      if (column === 'Estado') {
        const passes = selectedValues.some(v => {
          if (v === this.EMPTY_FILTER_VALUE || v === '(vazio)') {
            const isOrderStatusEmpty = !(order.orderStatus || '').trim();
            const hasEmptySparePartStatus = (order.spareParts || []).some(sp => !(sp.productStatus || '').trim());
            return isOrderStatusEmpty || hasEmptySparePartStatus;
          }
          if (v.startsWith(this.PREFIX_ORDEN)) {
            return (order.orderStatus || '') === v.slice(this.PREFIX_ORDEN.length);
          }
          if (v.startsWith(this.PREFIX_REPUESTO)) {
            const status = v.slice(this.PREFIX_REPUESTO.length);
            return (order.spareParts || []).some(sp => (sp.productStatus || '') === status);
          }
          return false;
        });
        if (!passes) return false;
        continue;
      }

      const orderValue = this.getOrderColumnValue(order, column);
      const includesEmpty = selectedValues.includes(this.EMPTY_FILTER_VALUE) || selectedValues.includes('(vazio)');
      const matchesEmpty = includesEmpty && !orderValue.trim();
      if (!selectedValues.includes(orderValue) && !matchesEmpty) {
        return false;
      }
    }
    return true;
  }

  isOrderFilterValueSelected(value: string): boolean {
    return this.tempColumnFilterSelection.includes(value);
  }

  toggleOrderFilterValue(value: string, event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    if (checked) {
      if (!this.tempColumnFilterSelection.includes(value)) {
        this.tempColumnFilterSelection.push(value);
      }
    } else {
      const index = this.tempColumnFilterSelection.indexOf(value);
      if (index > -1) {
        this.tempColumnFilterSelection.splice(index, 1);
      }
    }
  }

  areAllOrderFilterValuesSelected(): boolean {
    return this.filteredColumnValues.length > 0
      && this.filteredColumnValues.every(value => this.tempColumnFilterSelection.includes(value));
  }

  toggleAllOrderFilterValues(): void {
    if (this.filteredColumnValues.length === 0) {
      return;
    }

    const allSelected = this.filteredColumnValues
      .every(value => this.tempColumnFilterSelection.includes(value));

    if (allSelected) {
      this.tempColumnFilterSelection = this.tempColumnFilterSelection
        .filter(selected => !this.filteredColumnValues.includes(selected));
      return;
    }

    const selectedValues = new Set(this.tempColumnFilterSelection);
    this.filteredColumnValues.forEach(value => selectedValues.add(value));
    this.tempColumnFilterSelection = Array.from(selectedValues);
  }

  clearTempOrderColumnFilter(): void {
    this.tempColumnFilterSelection = [];
  }

  /** Limpiar el filtro de la columna actual y aplicar al instante (refresco automático). */
  clearAndApplyOrderColumnFilter(): void {
    this.tempColumnFilterSelection = [];
    this.closeFilterPopup(true);
  }

  /** Obtiene valores únicos de una columna. Si se pasa orders, usa ese set (todo el set); si no, usa this.orders (página actual). */
  getUniqueColumnValues(columnDisplayName: string, orders?: OrderOutModel[]): string[] {
    const values = new Set<string>();
    let hasEmptyValues = false;
    const source = orders ?? this.orders;

    source.forEach(order => {
      let value: any = '';

      switch (columnDisplayName) {
        case 'ID':
          value = order.orderId?.toString() || '';
          break;
        case 'Fecha de Creación':
          value = order.creationDate ? new Date(order.creationDate).toLocaleDateString('es-UY') : '';
          break;
        case 'Orden':
          value = order.orderNumber || '';
          break;
        case 'Siniestro':
          value = order.siniestro || '';
          break;
        case 'Factura':
          value = order.invoiceNumber || '';
          break;
        case 'Cliente Uy':
          value = order.clienteUY || '';
          break;
        case 'Cliente':
          value = order.clientName || '';
          break;
        case 'Proveedor':
          value = order.providerName || '';
          break;
        case 'Estado': {
          const orderStatus = (order.orderStatus || '').trim();
          if (orderStatus) {
            values.add(this.PREFIX_ORDEN + orderStatus);
          } else {
            hasEmptyValues = true;
          }
          (order.spareParts || []).forEach(sp => {
            const ps = (sp.productStatus || '').trim();
            if (ps) {
              values.add(this.PREFIX_REPUESTO + ps);
            } else {
              hasEmptyValues = true;
            }
          });
          break;
        }
        case 'Fecha de Llegada':
          value = order.expectedArrivalDate ? new Date(order.expectedArrivalDate).toLocaleDateString('es-UY') : '';
          break;
        case 'Dias de atraso':
          value = this.getOrderDelayDays(order).toString();
          break;
        case 'Dias de plazo':
          value = this.formatOrderMinDiasPrazo(order);
          break;
        case 'Vendedor':
          value = order.employeeName || '';
          break;
        case 'Costo':
          value = this.getTotalCost(order)?.toString() || '';
          break;
      }

      const normalizedValue = value != null ? value.toString().trim() : '';
      if (normalizedValue) {
        values.add(normalizedValue);
      } else {
        hasEmptyValues = true;
      }
    });

    const sortedValues = Array.from(values).sort();
    if (hasEmptyValues) {
      sortedValues.unshift(this.EMPTY_FILTER_VALUE);
    }
    return sortedValues;
  }

  // Filter column values as user types
  onFilterInputChange(): void {
    const filterText = this.filterValue.toLowerCase();
    if (filterText) {
      this.filteredColumnValues = this.columnUniqueValues.filter(value =>
        value.toLowerCase().includes(filterText)
      );
    } else {
      this.filteredColumnValues = [...this.columnUniqueValues];
    }
  }

  // Cierra el popup: apply=true aplica la selección temporal como filtro por columna; apply=false cancela.
  closeFilterPopup(apply: boolean = false): void {
    if (apply && this.filterColumn) {
      this.columnFilters[this.filterColumn] =
        this.tempColumnFilterSelection.length > 0 ? [...this.tempColumnFilterSelection] : [];
      this.applyOrderColumnFilters();
    }
    this.showFilterPopup = false;
    this.filterValue = '';
    this.filterColumn = '';
    this.columnUniqueValues = [];
    this.filteredColumnValues = [];
    this.tempColumnFilterSelection = [];
  }

  isWithinLastDays(date: Date, days: number): boolean {
    // Obtener la fecha actual en UTC
    const now = new Date(Date.UTC(
      new Date().getUTCFullYear(),
      new Date().getUTCMonth(),
      new Date().getUTCDate(),
      0, 0, 0 // Poner la hora en 00:00:00 para evitar problemas de zona horaria
    ));

    // Calcular la fecha límite en UTC
    const limitDate = new Date(now.getTime() - (days * 24 * 60 * 60 * 1000));

    // Comparar la fecha en UTC
    return date.getTime() >= limitDate.getTime();
  }

  applyDateFilter(): void {
    if (this.dateFilterType !== 'all') {
      // Si se selecciona un filtro relativo, limpia las fechas personalizadas
      this.customStartDate = '';
      this.customEndDate = '';
    }
    // Si deseas realizar alguna acción adicional al aplicar el filtro, agrégala aquí
  }

  sortData(column: string): void {
    if (this.sortedBy === column) {
      this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortedBy = column;
      this.sortOrder = 'asc';
    }
    this.currentPage = 1;
    this.clearAllOrdersBaseCache();
    this.loadPage(1);
  }

  formatSpareParts(sparePartNames: string[]): string {
    return sparePartNames.join(', ');
  }

  openModal(order: OrderOutModel): void {
    if (order) {
      this.activeFocusedOrderId = order.orderId;
      // Crear una copia profunda del objeto sin convertir isExport y sendYesOrNo
      this.isDuplicatingOrder = false;
      this.selectedOrder = {
        ...order
      };

      // Formatear las fechas para los inputs
      this.selectedOrderCreationDate = this.formatDateForInput(this.selectedOrder.creationDate);
      this.selectedOrderArrivalDate = this.formatDateForInput(this.selectedOrder.expectedArrivalDate);

      // Actualizar los estados de repuestos filtrados según el cliente seleccionado
      this.updateFilteredSparePartStatus(this.selectedOrder.clienteUY || 'AUTOCOMERCIAL');

      this.initialOrderSnapshot = this.getOrderFormSnapshotJson();
      document.getElementById('order-modal')?.classList.add('modal-open');
    }
  }

  // Método para filtrar estados de repuestos según el cliente seleccionado
  updateFilteredSparePartStatus(clienteUY: string): void {
    if (clienteUY === 'AUTOCOMERCIAL') {
      this.filteredSparePartDeliveryStatus = this.sparePartDeliveryStatus.filter(status =>
        !status.includes('SERIMPORT'));
    } else if (clienteUY === 'SERIMPORT') {
      this.filteredSparePartDeliveryStatus = this.sparePartDeliveryStatus.filter(status =>
        !status.includes('AUTOCOMERCIAL'));
    } else {
      this.filteredSparePartDeliveryStatus = [...this.sparePartDeliveryStatus];
    }

    // Cambiar automáticamente los estados de las piezas existentes cuando cambia el cliente UY
    if (this.selectedOrder && this.selectedOrder.spareParts) {
      this.updateSparePartsCompanyStatus(clienteUY);
    }
  }

  // Cargar lista de talleres disponibles
  loadWorkshops(): void {
    this.workshopsService.getWorkshops().subscribe({
      next: (workshops) => {
        // Filtrar talleres que no estén eliminados
        this.workshops = workshops.filter(w => !w.isDeleted);
      },
      error: (error) => {
        console.error('Error al cargar talleres en order-history:', error);
      }
    });
  }

  /**
   * FIX AGRESIVO: Actualizar datos completos del workshop cuando cambia el dropdown
   * IMPORTANTE: Este método se llama cada vez que el usuario cambia el taller en el modal
   */
  onWorkshopChange(event: any): void {
    if (!this.selectedOrder) {
      console.error('❌ selectedOrder es null en onWorkshopChange');
      return;
    }

    const workshopId = this.selectedOrder.workshopId;

    // CASO 1: Sin taller asignado (null, undefined, 0, o valor falsy)
    if (!workshopId || workshopId === null || workshopId === undefined) {
      this.selectedOrder.workshopName = '';
      this.selectedOrder.workshopAddress = '';
      this.selectedOrder.workshopPhone = '';
      this.selectedOrder.workshopCity = '';
      this.selectedOrder.workshopDepartment = '';
      return;
    }

    // CASO 2: Taller específico seleccionado
    const workshopIdNumber = Number(workshopId);

    const workshop = this.workshops.find(w => w.workshopId === workshopIdNumber);

    if (!workshop) {
      console.error('❌ CRÍTICO: Workshop NO ENCONTRADO para ID:', workshopIdNumber);
      console.error('❌ Workshops disponibles:', this.workshops.map(w => `${w.workshopId}: ${w.name}`));
      return;
    }

    // ACTUALIZACIÓN AGRESIVA - Forzar actualización de TODOS los campos
    this.selectedOrder.workshopName = workshop.name || '';
    this.selectedOrder.workshopAddress = workshop.address || '';
    this.selectedOrder.workshopPhone = workshop.phoneNumber || '';
    this.selectedOrder.workshopCity = workshop.city || '';
    this.selectedOrder.workshopDepartment = workshop.department || '';

    // FORZAR detección de cambios
    this.cdr.detectChanges();
  }

  openCreateWorkshopModal(): void {
    this.showCreateWorkshopModal = true;
  }

  cancelCreateWorkshop(): void {
    this.showCreateWorkshopModal = false;
    this.createWorkshopForm.reset({
      name: '',
      address: '',
      phoneNumber: '',
      city: '',
      department: '',
      company: 'AUTOCOMERCIAL'
    });
  }

  createWorkshop(): void {
    if (!this.createWorkshopForm.valid || !this.selectedOrder) return;
    this.creatingWorkshop = true;
    const workshopData = this.createWorkshopForm.value;
    this.workshopsService.createWorkshop(workshopData).subscribe({
      next: (newWorkshop) => {
        this.workshops.push(newWorkshop);
        this.selectedOrder!.workshopId = newWorkshop.workshopId;
        this.selectedOrder!.workshopName = newWorkshop.name || '';
        this.selectedOrder!.workshopAddress = newWorkshop.address || '';
        this.selectedOrder!.workshopPhone = newWorkshop.phoneNumber || '';
        this.selectedOrder!.workshopCity = newWorkshop.city || '';
        this.selectedOrder!.workshopDepartment = newWorkshop.department || '';
        this.cancelCreateWorkshop();
        this.cdr.detectChanges();
        alert(`✅ Taller "${newWorkshop.name}" creado y asignado a la orden.`);
      },
      error: (err) => {
        console.error('Error al crear taller:', err);
        alert('❌ Error al crear el taller. Intente nuevamente.');
      },
      complete: () => { this.creatingWorkshop = false; }
    });
  }

  openWorkshopInfo(workshopId: number | string | null | undefined): void {
    const id = Number(workshopId);
    if (!id || !Number.isInteger(id)) return;
    const workshop = this.workshops.find(w => w.workshopId === id);
    const order = this.selectedOrder;

    if (!order && !workshop) return;

    const hasSnapshotName = !!order?.workshopName && order.workshopName.trim() !== '';
    const hasSnapshotAddress = !!order?.workshopAddress && order.workshopAddress.trim() !== '';
    const hasSnapshotPhone = !!order?.workshopPhone && order.workshopPhone.trim() !== '';
    const hasSnapshotCity = !!order?.workshopCity && order.workshopCity.trim() !== '';
    const hasSnapshotDepartment = !!order?.workshopDepartment && order.workshopDepartment.trim() !== '';

    this.selectedWorkshopInfo = {
      workshopId: id,
      name: hasSnapshotName ? order!.workshopName : (workshop?.name || ''),
      address: hasSnapshotAddress ? order!.workshopAddress : (workshop?.address || ''),
      phoneNumber: hasSnapshotPhone ? order!.workshopPhone : (workshop?.phoneNumber || ''),
      city: hasSnapshotCity ? order!.workshopCity : (workshop?.city || ''),
      department: hasSnapshotDepartment ? order!.workshopDepartment : (workshop?.department || ''),
      company: workshop?.company || order?.clienteUY || 'AUTOCOMERCIAL'
    };
    this.showWorkshopInfoModal = true;
  }

  closeWorkshopInfo(): void {
    this.showWorkshopInfoModal = false;
    this.selectedWorkshopInfo = null;
  }

  // Método para actualizar automáticamente los estados de las piezas según la empresa
  updateSparePartsCompanyStatus(newClienteUY: string): void {
    if (!this.selectedOrder || !this.selectedOrder.spareParts) return;

    let hasChanges = false;
    const sparePartsToUpdate: any[] = [];

    this.selectedOrder.spareParts.forEach(sparePart => {
      let newStatus = sparePart.productStatus;

      // Cambiar estados que terminan con SERIMPORT
      if (sparePart.productStatus.endsWith('-SERIMPORT') && newClienteUY === 'AUTOCOMERCIAL') {
        newStatus = sparePart.productStatus.replace('-SERIMPORT', '-AUTOCOMERCIAL');
        hasChanges = true;
      }
      // Cambiar estados que terminan con AUTOCOMERCIAL  
      else if (sparePart.productStatus.endsWith('-AUTOCOMERCIAL') && newClienteUY === 'SERIMPORT') {
        newStatus = sparePart.productStatus.replace('-AUTOCOMERCIAL', '-SERIMPORT');
        hasChanges = true;
      }

      if (newStatus !== sparePart.productStatus) {
        sparePart.productStatus = newStatus;
        // Muta un campo suelto: no cambia la identidad de ningun array, hay que avisar.
        this.invalidateAllSparePartsCache();
        sparePartsToUpdate.push({
          sparePartId: sparePart.sparePartId,
          productStatus: newStatus
        });
      }
    });

    // Si hay cambios, enviar la actualización al backend
    if (hasChanges && sparePartsToUpdate.length > 0) {
      this.http.put(`${this.apiUrlSparePart}/update-multiple-status`, sparePartsToUpdate)
        .subscribe({
          next: (response) => {
            this.showSuccessMessage(`${sparePartsToUpdate.length} repuestos actualizados por cambio de empresa`);
          },
          error: (error) => {
            console.error('Error al actualizar estados de repuestos:', error);
            this.showErrorMessage('Error al actualizar los estados de los repuestos');
          }
        });
    }
  }

  closeModal(): void {
    this.selectedOrder = null;
    this.isDuplicatingOrder = false;
    this.openAddSparePartComponent = false;
    this.initialOrderSnapshot = null;
    document.getElementById('order-modal')?.classList.remove('modal-open');
  }

  openSparePartModal(sparePart: SparePartModel): void {
    const loadToken = ++this.sparePartModalLoadToken;

    this.initialSparePartSnapshot = null;
    this.isDuplicatingSparePart = false;
    // Primero mostrar el modal con los datos en memoria (para respuesta rápida)
    const canonicalSparePart = toCanonicalSparePartKeys(sparePart as Record<string, unknown>);
    this.selectedSparePart = this.normalizeSparePartFlags({
      ...canonicalSparePart,
      providerName: canonicalSparePart.providerName || this.selectedOrder?.providerName || ''
    });
    // Formatear la fecha para mostrarla en formato DD/MM/AAAA
    if (this.selectedSparePart.expectedArrivalDate) {
      this.selectedSparePartArrivalDate = this.formatDate(this.selectedSparePart.expectedArrivalDate);
    }
    this.selectedSparePartExportDate = this.formatDateForInput(this.selectedSparePart.fechaExportacion as any);
    this.selectedSparePartPhysicalArrivalDateTime = this.formatDateTimeForInput(this.selectedSparePart.arrivalDate as any);
    this.selectedSparePartFechaComunicado = this.formatDateForInput(this.selectedSparePart.fechaDeComunicado as any);
    this.selectedSparePartMlDeadline = this.formatDateForInput(this.selectedSparePart.mlDeadline as any);
    document.getElementById('spare-part-modal')?.classList.add('modal-open');
    this.updateSparePartSnapshotFromCurrent();
    setTimeout(() => {
      if (!this.selectedSparePart) return;
      if (this.exportNumberText(this.selectedSparePart.exportNumber) === this.exportNumberWhenSparePartOpened) {
        this.updateSparePartSnapshotFromCurrent();
      }
    }, 0);

    if (!sparePart.sparePartId || sparePart.sparePartId <= 0) {
      return;
    }

    // ENFOQUE AGRESIVO: Recargar el repuesto desde el servidor de múltiples formas para asegurar datos actualizados
    if (sparePart.orderId) {
      // Método 1: Recargar desde la orden
      this.orderService.getOrderById(sparePart.orderId).subscribe({
        next: (order) => {
          if (!this.isActiveSparePartModalLoad(loadToken, sparePart.sparePartId)) {
            return;
          }
          // Buscar el repuesto actualizado en la orden recargada
          const updatedSparePart = order.spareParts?.find(sp => sp.sparePartId === sparePart.sparePartId);
          if (updatedSparePart) {
            // CRÍTICO: Verificar todos los posibles nombres del campo remito
            const remitoFromOrder = updatedSparePart.shippingReceipt ||
              (updatedSparePart as any).ShippingReceipt ||
              (updatedSparePart as any).shippingReceipt ||
              '';
            // CRÍTICO: Crear una nueva referencia del objeto para forzar la detección de cambios
            // Esto es esencial para que Angular detecte el cambio y actualice el input
            const hasLocalSparePartEdits = this.hasExportNumberEdit() || this.isSparePartFormDirty();
            if (!hasLocalSparePartEdits) {
              const canonicalUpdated = toCanonicalSparePartKeys(updatedSparePart as Record<string, unknown>);
              this.selectedSparePart = this.normalizeSparePartFlags({
                ...canonicalUpdated,
                providerName: canonicalUpdated.providerName || order.providerName || '',
                shippingReceipt: remitoFromOrder && remitoFromOrder.trim() !== ''
                  ? remitoFromOrder.trim()
                  : (canonicalUpdated.shippingReceipt || '')
              });
            } else {
            }

            // Forzar detección de cambios
            this.cdr.detectChanges();
            // Actualizar también la fecha formateada
            if (this.selectedSparePart.expectedArrivalDate) {
              this.selectedSparePartArrivalDate = this.formatDate(this.selectedSparePart.expectedArrivalDate);
            }
            this.selectedSparePartExportDate = this.formatDateForInput(this.selectedSparePart.fechaExportacion as any);
            this.selectedSparePartPhysicalArrivalDateTime = this.formatDateTimeForInput(this.selectedSparePart.arrivalDate as any);
            this.selectedSparePartFechaComunicado = this.formatDateForInput(this.selectedSparePart.fechaDeComunicado as any);
            this.selectedSparePartMlDeadline = this.formatDateForInput(this.selectedSparePart.mlDeadline as any);
            if (!hasLocalSparePartEdits) {
              this.updateSparePartSnapshotFromCurrent();
            }

            // Actualizar también el repuesto en la lista de la orden seleccionada si está abierta
            if (this.selectedOrder && this.selectedOrder.orderId === order.orderId) {
              const index = this.selectedOrder.spareParts?.findIndex(sp => sp.sparePartId === sparePart.sparePartId);
              if (index !== undefined && index >= 0 && this.selectedOrder.spareParts) {
                this.selectedOrder.spareParts[index] = this.normalizeSparePartFlags({
                  ...toCanonicalSparePartKeys(updatedSparePart as Record<string, unknown>)
                });
                // Forzar actualización del remito también en la lista
                if (remitoFromOrder && remitoFromOrder.trim() !== '') {
                  this.selectedOrder.spareParts[index].shippingReceipt = remitoFromOrder.trim();
                }
              }
            }

            // Método 2: Si el repuesto tiene packageNumber, también recargar desde el paquete
            if (sparePart.packageNumber) {
              setTimeout(() => {
                this.packageListsService.getSparePartsByPackage(sparePart.packageNumber!).subscribe({
                  next: (sparePartsFromPackage) => {
                    if (!this.isActiveSparePartModalLoad(loadToken, sparePart.sparePartId)) {
                      return;
                    }
                    const sparePartFromPackage = sparePartsFromPackage.find(sp => sp.sparePartId === sparePart.sparePartId);
                    if (sparePartFromPackage) {
                      // Mapear el remito (puede venir como shippingReceipt o ShippingReceipt)
                      const remitoFromPackage = sparePartFromPackage.shippingReceipt ||
                        (sparePartFromPackage as any).ShippingReceipt ||
                        '';
                      const hadLocalSparePartEdits = this.hasExportNumberEdit() || this.isSparePartFormDirty();
                      // CRÍTICO: SIEMPRE actualizar, incluso si está vacío
                      // CRÍTICO: Crear nueva referencia del objeto para forzar detección de cambios
                      this.selectedSparePart = {
                        ...this.selectedSparePart,
                        shippingReceipt: remitoFromPackage ? remitoFromPackage.trim() : ''
                      };

                      // Forzar detección de cambios múltiples veces
                      this.cdr.detectChanges();
                      setTimeout(() => this.cdr.detectChanges(), 0);
                      setTimeout(() => this.cdr.detectChanges(), 100);
                      setTimeout(() => this.cdr.detectChanges(), 300);
                      if (!hadLocalSparePartEdits) {
                        this.updateSparePartSnapshotFromCurrent();
                      }

                      // También emitir un evento para que el modal se actualice
                      if (remitoFromPackage && remitoFromPackage.trim() !== '') {
                      } else {
                      }
                    } else {
                    }
                  },
                  error: (error) => {
                    console.error('Error al recargar desde el paquete:', error);
                  }
                });
              }, 300);
            }
          } else {
          }
        },
        error: (error) => {
          console.error('Error al recargar el repuesto desde el servidor:', error);
          // Si falla, intentar recargar desde el paquete
          if (sparePart.packageNumber) {
            this.packageListsService.getSparePartsByPackage(sparePart.packageNumber).subscribe({
              next: (sparePartsFromPackage) => {
                if (!this.isActiveSparePartModalLoad(loadToken, sparePart.sparePartId)) {
                  return;
                }
                const sparePartFromPackage = sparePartsFromPackage.find(sp => sp.sparePartId === sparePart.sparePartId);
                if (sparePartFromPackage && sparePartFromPackage.shippingReceipt) {
                  const hadLocalSparePartEdits = this.hasExportNumberEdit() || this.isSparePartFormDirty();
                  this.selectedSparePart!.shippingReceipt = sparePartFromPackage.shippingReceipt;
                  if (!hadLocalSparePartEdits) {
                    this.updateSparePartSnapshotFromCurrent();
                  }
                }
              },
              error: (err) => console.error('Error al recargar desde el paquete (fallback):', err)
            });
          }
        }
      });
    } else if (sparePart.packageNumber) {
      // Si no hay orderId pero hay packageNumber, recargar desde el paquete
      this.packageListsService.getSparePartsByPackage(sparePart.packageNumber).subscribe({
        next: (sparePartsFromPackage) => {
          if (!this.isActiveSparePartModalLoad(loadToken, sparePart.sparePartId)) {
            return;
          }
          const sparePartFromPackage = sparePartsFromPackage.find(sp => sp.sparePartId === sparePart.sparePartId);
          if (sparePartFromPackage && sparePartFromPackage.shippingReceipt) {
            const hadLocalSparePartEdits = this.hasExportNumberEdit() || this.isSparePartFormDirty();
            this.selectedSparePart!.shippingReceipt = sparePartFromPackage.shippingReceipt;
            if (!hadLocalSparePartEdits) {
              this.updateSparePartSnapshotFromCurrent();
            }
          }
        },
        error: (error) => console.error('Error al recargar desde el paquete:', error)
      });
    }
  }

  closeSparePartModal(): void {
    // Guardar la posición actual del scroll antes de cerrar el modal
    const scrollPosition = window.scrollY || document.documentElement.scrollTop;
    this.sparePartModalLoadToken++;

    this.selectedSparePart = null;
    this.isDuplicatingSparePart = false;
    this.duplicateSparePartTargetOrderId = null;
    this.duplicateSparePartTargetOrderNumber = '';
    this.duplicateSparePartTargetOrderErrorMessage = '';
    this.showAldanaForm = false;
    this.initialSparePartSnapshot = null;
    this.exportNumberWhenSparePartOpened = '';
    document.getElementById('spare-part-modal')?.classList.remove('modal-open');

    // Restaurar la posición del scroll después de cerrar el modal
    setTimeout(() => {
      window.scrollTo({
        top: scrollPosition,
        behavior: 'auto' // Usar 'auto' en lugar de 'smooth' para evitar animaciones visibles
      });
    }, 100);
  }

  formatDate(date: string | Date): string {
    if (!date) return '';
    const dateObj = typeof date === 'string' ? new Date(date) : date;

    // Asegurarnos de que la fecha sea válida
    if (isNaN(dateObj.getTime())) return '';

    // Obtener los componentes de la fecha
    const day = String(dateObj.getDate()).padStart(2, '0');
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const year = dateObj.getFullYear();

    // Retornar en formato DD/MM/AAAA
    return `${day}/${month}/${year}`;
  }

  formatDateForInput(date: string | Date): string {
    if (!date) return '';
    const dateObj = typeof date === 'string' ? new Date(date) : date;

    // Asegurarnos de que la fecha sea válida
    if (isNaN(dateObj.getTime())) return '';

    // Retornar en formato YYYY-MM-DD para inputs de tipo date
    return dateObj.toISOString().split('T')[0];
  }

  formatDateTimeForInput(date: string | Date | null | undefined): string {
    return formatWallClockDateTimeForInput(date);
  }

  toggleAldanaForm(): void {
    this.showAldanaForm = !this.showAldanaForm;
  }

  selectAldanaColor(color: string): void {
    if (!this.selectedSparePart) return;
    this.selectedSparePart.color = color;
  }

  /** Objeto serializable con los campos editables del formulario de orden (para comparar dirty). */
  private getOrderFormSnapshotJson(): string {
    if (!this.selectedOrder) return '';
    const o = this.selectedOrder;
    return JSON.stringify({
      orderId: o.orderId,
      orderNumber: o.orderNumber,
      siniestro: o.siniestro,
      invoiceNumber: o.invoiceNumber,
      orderStatus: o.orderStatus,
      clientName: o.clientName,
      clientPhone: o.clientPhone,
      providerName: o.providerName,
      providerPhone: o.providerPhone,
      employeeName: o.employeeName,
      deliveryAddress: o.deliveryAddress,
      carMakeModel: o.carMakeModel,
      isExport: o.isExport,
      clienteUY: o.clienteUY,
      workshopId: o.workshopId,
      sendYesOrNo: o.sendYesOrNo,
      emergentComment: o.emergentComment,
      creationDate: this.selectedOrderCreationDate,
      expectedArrivalDate: this.selectedOrderArrivalDate
    });
  }

  /** Objeto serializable del formulario de repuesto (para comparar dirty). */
  private getSparePartFormSnapshotJson(): string {
    if (!this.selectedSparePart) return '';
    const normalizedPart = this.normalizeSparePartFlags({ ...this.selectedSparePart } as any);
    return JSON.stringify({
      part: normalizedPart,
      arrivalDate: this.selectedSparePartArrivalDate,
      physicalArrivalDateTime: this.selectedSparePartPhysicalArrivalDateTime,
      exportDate: this.selectedSparePartExportDate,
      fechaComunicado: this.selectedSparePartFechaComunicado,
      mlDeadline: this.selectedSparePartMlDeadline
    });
  }

  /** Actualiza el snapshot del repuesto al estado actual (tras carga async o tras guardar). */
  private updateSparePartSnapshotFromCurrent(): void {
    this.exportNumberWhenSparePartOpened = this.exportNumberText(this.selectedSparePart?.exportNumber);
    this.initialSparePartSnapshot = this.getSparePartFormSnapshotJson();
  }

  isOrderFormDirty(): boolean {
    return this.initialOrderSnapshot != null && this.selectedOrder != null &&
      this.getOrderFormSnapshotJson() !== this.initialOrderSnapshot;
  }

  canSaveOrderForm(): boolean {
    if (!this.selectedOrder) return false;
    if (this.isDuplicatingOrder) {
      return !!this.selectedOrder.orderNumber?.trim()
        && !!this.selectedOrder.providerName?.trim()
        && (this.selectedOrder.spareParts?.length ?? 0) > 0;
    }
    return this.isOrderFormDirty();
  }

  isDuplicateOrderNumberMissing(): boolean {
    return this.isDuplicatingOrder && !this.selectedOrder?.orderNumber?.trim();
  }

  isDuplicateOrderProviderMissing(): boolean {
    return this.isDuplicatingOrder && !this.selectedOrder?.providerName?.trim();
  }

  isSparePartFormDirty(): boolean {
    return this.initialSparePartSnapshot != null && this.selectedSparePart != null &&
      this.getSparePartFormSnapshotJson() !== this.initialSparePartSnapshot;
  }

  private exportNumberText(value: unknown): string {
    return value == null ? '' : String(value).trim();
  }

  private hasExportNumberEdit(): boolean {
    if (!this.selectedSparePart) return false;
    return this.exportNumberText(this.selectedSparePart.exportNumber) !== this.exportNumberWhenSparePartOpened;
  }

  canSaveSparePartForm(): boolean {
    if (this.isDuplicatingSparePart) return true;
    return this.hasExportNumberEdit() || this.isSparePartFormDirty();
  }

  private nextTemporarySparePartId(): number {
    return this.temporarySparePartIdSeed--;
  }

  onDuplicateSparePartTargetOrderNumberChange(value: string): void {
    this.duplicateSparePartTargetOrderNumber = value || '';
    this.duplicateSparePartTargetOrderErrorMessage = '';
  }

  private normalizeOrderNumber(value: string | undefined | null): string {
    return (value || '').trim().toLowerCase();
  }

  private resolveOrderByNumber(
    orderNumberInput: string,
    onResolved: (order: OrderOutModel) => void,
    onFailed?: () => void
  ): void {
    const orderNumber = orderNumberInput.trim();
    if (!orderNumber) {
      this.duplicateSparePartTargetOrderErrorMessage = 'Debe ingresar un número de orden destino.';
      onFailed?.();
      return;
    }

    this.duplicateSparePartTargetOrderErrorMessage = '';
    const normalizedTarget = this.normalizeOrderNumber(orderNumber);
    const loadedMatch = (this.orders || []).find(order =>
      this.normalizeOrderNumber(order.orderNumber) === normalizedTarget
    );
    if (loadedMatch?.orderId) {
      onResolved(loadedMatch);
      return;
    }

    this.orderService.getAllFilteredOrders(orderNumber).subscribe({
      next: (orders) => {
        const exactMatch = (orders || []).find(order =>
          this.normalizeOrderNumber(order.orderNumber) === normalizedTarget
        );
        if (!exactMatch?.orderId) {
          this.duplicateSparePartTargetOrderErrorMessage = `No existe una orden con número "${orderNumber}".`;
          onFailed?.();
          return;
        }
        this.duplicateSparePartTargetOrderErrorMessage = '';
        onResolved(exactMatch);
      },
      error: () => {
        this.duplicateSparePartTargetOrderErrorMessage = 'No se pudo validar la orden destino. Intente nuevamente.';
        onFailed?.();
      }
    });
  }

  private duplicateSparePartDraft(sparePart: SparePartModel, forcedOrderId?: number): SparePartModel {
    const canonicalSource = toCanonicalSparePartKeys(sparePart as Record<string, unknown>);
    return applyPostEmergentCommentDuplicateDefaults(this.normalizeSparePartFlags({
      ...canonicalSource,
      sparePartId: this.nextTemporarySparePartId(),
      orderId: forcedOrderId ?? canonicalSource.orderId ?? this.selectedOrder?.orderId,
      receptionStatus: 'NO-LLEGO',
      providerName: '',
      taxNote: '',
      comments: '',
      netWheight: null,
      grossWheight: null,
      country: '',
      isFant: false,
      fantUrgency: null,
      recompra: false,
      control: false,
      anulado: false,
      trello: false,
      devZureo: false,
      controleSeba: false,
      okAnulacao: false,
      facturado: false,
      packageNumber: '',
      exportNumber: '',
      externo: '',
      interno: '',
      color: '',
      fechaDeComunicado: null,
      shippingReceipt: '',
      transportType: '',
      arrivalDate: undefined,
      lastExportedAt: undefined,
      fechaExportacion: undefined,
      shippingStatus: 'NO-ENVIADO',
      productStatus: 'CREADO'
    } as any));
  }

  duplicateOrderWithChanges(): void {
    if (!this.selectedOrder) return;
    const sourceOrderId = this.selectedOrder.orderId;
    const duplicatedSpareParts = (this.selectedOrder.spareParts || []).map(sp => this.duplicateSparePartDraft(sp, 0));
    this.selectedOrder = {
      ...this.selectedOrder,
      orderId: 0,
      orderNumber: '',
      providerName: '',
      orderStatus: 'CREADO',
      recordedAt: undefined,
      spareParts: duplicatedSpareParts
    };
    this.selectedOrderCreationDate = this.formatDateForInput(new Date());
    this.selectedOrderArrivalDate = this.formatDateForInput(this.selectedOrder.expectedArrivalDate || new Date());
    this.isDuplicatingOrder = true;
    if (sourceOrderId > 0) {
      this.activeFocusedOrderId = sourceOrderId;
    }
    this.openAddSparePartComponent = false;
    this.updateFilteredSparePartStatus(this.selectedOrder.clienteUY || 'AUTOCOMERCIAL');
    this.initialOrderSnapshot = this.getOrderFormSnapshotJson();
    this.showSuccessMessage('Orden duplicada. Edita los datos y guarda para crear la nueva orden.');
    this.scrollOrderModalToTop();
  }

  duplicateSparePartWithChanges(sparePart?: SparePartModel): void {
    if (!this.selectedOrder) {
      this.showErrorMessage('No hay orden seleccionada para duplicar el repuesto.');
      return;
    }
    const basePart = sparePart ?? this.selectedSparePart;
    if (!basePart) return;
    this.selectedSparePart = this.duplicateSparePartDraft(basePart, this.selectedOrder.orderId);
    this.duplicateSparePartTargetOrderId = this.selectedOrder.orderId;
    this.duplicateSparePartTargetOrderNumber = this.selectedOrder.orderNumber || '';
    this.duplicateSparePartTargetOrderErrorMessage = '';
    this.selectedSparePartArrivalDate = this.selectedSparePart.expectedArrivalDate
      ? this.formatDate(this.selectedSparePart.expectedArrivalDate)
      : '';
    this.selectedSparePartExportDate = this.formatDateForInput(this.selectedSparePart.fechaExportacion as any);
    this.selectedSparePartPhysicalArrivalDateTime = this.formatDateTimeForInput(this.selectedSparePart.arrivalDate as any);
    this.selectedSparePartFechaComunicado = this.formatDateForInput(this.selectedSparePart.fechaDeComunicado as any);
    this.selectedSparePartMlDeadline = this.formatDateForInput(this.selectedSparePart.mlDeadline as any);
    this.isDuplicatingSparePart = true;
    document.getElementById('spare-part-modal')?.classList.add('modal-open');
    setTimeout(() => this.updateSparePartSnapshotFromCurrent(), 0);
    this.scrollSparePartModalToTop();
  }

  private scrollOrderModalToTop(): void {
    setTimeout(() => {
      const modalContent = document.getElementById('order-history-order-modal-content');
      if (modalContent) {
        modalContent.scrollTop = 0;
      }
      const orderNumberInput = document.getElementById('order-number') as HTMLInputElement | null;
      orderNumberInput?.focus();
    }, 0);
  }

  private scrollSparePartModalToTop(): void {
    setTimeout(() => {
      const modalContent = document.getElementById('order-history-spare-part-modal-content');
      if (modalContent) {
        modalContent.scrollTop = 0;
      }
    }, 0);
  }

  private refreshSelectedOrderFromBackend(orderId: number): void {
    this.orderService.getOrderById(orderId).subscribe({
      next: (order) => {
        const replaceOrder = (current: OrderOutModel): OrderOutModel =>
          current.orderId === order.orderId ? { ...order } : current;
        this.orders = this.orders.map(replaceOrder);
        this.filteredOrdersList = this.filteredOrdersList.map(replaceOrder);
        this.sortedAndFilteredOrders = this.sortedAndFilteredOrders.map(replaceOrder);
        if (this.selectedOrder?.orderId === order.orderId) {
          this.selectedOrder = { ...order };
        }
        this.applyOrderColumnFilters();
        this.cdr.detectChanges();
      },
      error: () => {
        this.reloadTable();
      }
    });
  }

  saveChanges(): void {
    if (this.selectedOrder) {
      if (this.isDuplicatingOrder) {
        const orderNumber = (this.selectedOrder.orderNumber || '').trim();
        if (!orderNumber) {
          this.showErrorMessage('Debe ingresar un número de orden para guardar la orden duplicada.');
          return;
        }
        const providerName = (this.selectedOrder.providerName || '').trim();
        if (!providerName) {
          this.showErrorMessage('Debe seleccionar un proveedor para guardar la orden duplicada.');
          return;
        }
        if (!this.selectedOrder.spareParts || this.selectedOrder.spareParts.length === 0) {
          this.showErrorMessage('La orden duplicada debe tener al menos un repuesto.');
          return;
        }
        const saleValuesError = validateSparePartsSaleValues(this.selectedOrder.spareParts);
        if (saleValuesError) {
          this.showErrorMessage(saleValuesError);
          return;
        }
        const orderToCreate = stripPascalCaseShadowKeys({
          ...this.selectedOrder,
          orderId: 0,
          orderNumber,
          workshopId: this.normalizeNullableInt(
            this.selectedOrder.workshopId ?? (this.selectedOrder as any).WorkshopId
          ),
          emergentComment: this.normalizeNullableText(
            this.selectedOrder.emergentComment ?? (this.selectedOrder as any).EmergentComment
          ),
          creationDate: this.selectedOrderCreationDate,
          expectedArrivalDate: this.selectedOrderArrivalDate,
          spareParts: this.selectedOrder.spareParts.map(part => this.prepareSparePartForOrderCreate(part))
        });
        const duplicateOrderCandidates = this.selectedOrder.spareParts.map((part) => ({
          sparePartName: part.sparePartName,
          productCode: part.productCode,
          orderNumber,
          siniestro: this.selectedOrder?.siniestro || part.siniestro || '',
          unitPrice: part.unitPrice,
          valorDeVenta: part.valorDeVenta
        }));
        if (this.requestLowSaleRatioConfirmation(
          duplicateOrderCandidates,
          'Confirmar guardado de orden duplicada',
          () => this.http.post<OrderOutModel>(`${this.apiUrl}`, orderToCreate)
            .subscribe({
              next: () => {
                this.showSuccessMessage('Orden duplicada creada correctamente.');
                this.closeModal();
                this.reloadTable();
              },
              error: (error) => {
                if (error.status === 409) {
                  this.showErrorMessage(error.error || 'El número de orden ya existe. Elija otro número.');
                  return;
                }
                const backendError =
                  error?.error?.message ||
                  (typeof error?.error === 'string' ? error.error : '') ||
                  error?.message ||
                  'Error al crear la orden duplicada.';
                this.showErrorMessage(backendError);
              }
            })
        )) {
          return;
        }
        this.http.post<OrderOutModel>(`${this.apiUrl}`, orderToCreate)
          .subscribe({
            next: () => {
              this.showSuccessMessage('Orden duplicada creada correctamente.');
              this.closeModal();
              this.reloadTable();
            },
            error: (error) => {
              if (error.status === 409) {
                this.showErrorMessage(error.error || 'El número de orden ya existe. Elija otro número.');
                return;
              }
              const backendError =
                error?.error?.message ||
                (typeof error?.error === 'string' ? error.error : '') ||
                error?.message ||
                'Error al crear la orden duplicada.';
              this.showErrorMessage(backendError);
            }
          });
        return;
      }

      // No convertir isExport y sendYesOrNo, mantener los valores originales
      const orderToUpdate = {
        ...this.selectedOrder,
        orderId: this.selectedOrder.orderId, // Asegurar que se incluya el orderId
        creationDate: this.selectedOrderCreationDate,
        expectedArrivalDate: this.selectedOrderArrivalDate,
      };

      this.http.put<OrderOutModel>(`${this.apiUrl}/${this.selectedOrder.orderId}`, orderToUpdate)
        .subscribe({
          next: (updatedOrder) => {
            this.showEmergentCommentAlertGroups((updatedOrder as any)?.emergentCommentAlerts, 'Actualización de orden');
            // CRÍTICO: Actualizar la orden en la lista local CON LOS DATOS DEL BACKEND
            const index = this.orders.findIndex(o => o.orderId === this.selectedOrder?.orderId);
            if (index !== -1) {
              // Usar la orden actualizada que retorna el backend
              // Esto asegura que workshopName, workshopAddress, etc. estén sincronizados
              this.orders[index] = updatedOrder;

              // FIX AGRESIVO: Actualizar selectedOrder Y filteredOrdersList para que TODOS los lugares tengan datos frescos
              this.selectedOrder = { ...updatedOrder }; // Crear una nueva referencia

              // Forzar actualización en filteredOrdersList también
              const filteredIndex = this.filteredOrdersList.findIndex(o => o.orderId === updatedOrder.orderId);
              if (filteredIndex !== -1) {
                const prevSelected = this.filteredOrdersList[filteredIndex].selected;
                this.filteredOrdersList[filteredIndex] = { ...updatedOrder, selected: prevSelected };
              }

              // Reordenar las órdenes por fecha de creación
              this.orders.sort((a, b) =>
                new Date(b.creationDate).getTime() - new Date(a.creationDate).getTime()
              );

              this.applyOrderColumnFilters();

              // Forzar change detection
              this.cdr.detectChanges();

              // Mostrar mensaje de éxito con confirmación de taller
              const workshopMsg = updatedOrder.workshopName || 'Sin taller asignado';
              this.showSuccessMessage(`Orden actualizada exitosamente.\nTaller: ${workshopMsg}`);
            }

            this.closeModal();
          },
          error: (error) => {
            console.error('Error al actualizar la orden:', error);
            let errorMessage = 'Error al actualizar la orden: ';

            if (error.status === 409) {
              errorMessage = error.error && typeof error.error === 'string'
                ? error.error
                : 'El número de orden ya existe. Elija otro número.';
            } else if (error.error && typeof error.error === 'string') {
              errorMessage += error.error;
            } else if (error.status === 404) {
              errorMessage += 'La orden no fue encontrada en el servidor.';
            } else if (error.status === 400) {
              errorMessage += 'Datos inválidos. Por favor, verifique la información ingresada.';
            } else if (error.status === 500) {
              errorMessage += 'Error interno del servidor. Por favor, intente nuevamente más tarde.';
            } else {
              errorMessage += 'Error desconocido al procesar la solicitud.';
            }

            this.showErrorMessage(errorMessage);
          }
        });
    }
  }

  confirmLowSaleRatioAction(): void {
    const pendingAction = this.pendingLowSaleRatioAction;
    this.showLowSaleRatioConfirmModal = false;
    this.pendingLowSaleRatioAction = null;
    this.lowSaleRatioViolationLabels = [];
    if (pendingAction) {
      pendingAction();
    }
  }

  cancelLowSaleRatioAction(): void {
    this.showLowSaleRatioConfirmModal = false;
    this.pendingLowSaleRatioAction = null;
    this.lowSaleRatioViolationLabels = [];
  }

  private requestLowSaleRatioConfirmation(
    candidates: LowSaleRatioSparePartCandidate[],
    title: string,
    onConfirm: () => void
  ): boolean {
    const violations = collectLowSaleRatioViolations(candidates);
    if (violations.length === 0) {
      return false;
    }

    this.lowSaleRatioConfirmTitle = title;
    this.lowSaleRatioViolationLabels = violations.map(v => v.label);
    this.pendingLowSaleRatioAction = onConfirm;
    this.showLowSaleRatioConfirmModal = true;
    return true;
  }

  reloadTable(): void {
    // Mostrar indicador de carga
    const loadingMessage = document.createElement('div');
    loadingMessage.className = 'fixed top-4 right-4 bg-white p-4 rounded-lg shadow-lg z-50 flex flex-col items-center';
    loadingMessage.innerHTML = `
      <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mb-2"></div>
      <p class="text-gray-700">Actualizando datos...</p>
    `;
    document.body.appendChild(loadingMessage);

    // Emitir eventos para mantener los filtros aplicados
    if (this.searchTerm) {
      this.searchFilterChanged.emit(this.searchTerm);
    }

    if (this.startDate || this.endDate) {
      this.dateFilterChanged.emit({
        startDate: this.startDate || undefined,
        endDate: this.endDate || undefined
      });
    }

    // Actualizar los contadores totales
    this.orderService.getTotalOrdersCount().subscribe({
      next: (count) => {
        this.totalOrdersInDB = count;
      }
    });

    this.sparePartService.getTotalSparePartsCount().subscribe({
      next: (count) => {
        this.totalSpareParts = count;
      }
    });

    // En lugar de llamar a loadPage, hacemos la solicitud directamente
    // para tener más control sobre la actualización de la paginación
    this.loading = true;

    this.orderService.getPaginatedOrders(
      this.currentPage,
      this.pageSize,
      this.searchTerm,
      this.startDate || undefined,
      this.endDate || undefined,
      undefined,
      this.selectedWorkshopId ?? undefined,
      this.onlyLast60Days ? 60 : undefined,
      this.sortedBy || undefined,
      this.sortOrder,
      this.getServerOrderColumnFilters()
    ).subscribe({
      next: (response) => {
        // Actualizar las órdenes y el total
        this.orders = response.orders;
        this.filteredOrdersList = response.orders;
        this.syncOrdersTableAllSelectedHeader();

        // IMPORTANTE: Guardar el total de órdenes filtradas que viene del servidor
        const totalFromServer = response.totalCount;
        this.filteredTotal = totalFromServer;

        // Calcular el número total de páginas basado en el total recibido del servidor
        this.totalPages = Math.max(1, Math.ceil(totalFromServer / this.pageSize));

        // Depurar estado de paginación después de recibir datos
        this.debugPagination('después de recargar tabla');

        // Verificar si se han cargado todas las órdenes
        this.allOrdersLoaded = this.currentPage * this.pageSize >= this.filteredTotal;

        // Actualizar explícitamente los números de página
        const pageNumbers = this.getPageNumbers();

        this.loading = false;

        // Verificar la consistencia de la paginación después de cargar
        setTimeout(() => {
          this.verifyPaginationConsistency('después de recargar tabla');
        }, 500);

        // Quitar el indicador de carga y mostrar mensaje de éxito
        loadingMessage.remove();

        const successMessage = document.createElement('div');
        successMessage.className = 'fixed top-4 right-4 bg-green-500 text-white px-4 py-2 rounded shadow-lg z-50';
        successMessage.textContent = 'Datos actualizados correctamente';
        document.body.appendChild(successMessage);

        setTimeout(() => {
          successMessage.remove();
        }, 3000);
      },
      error: (error) => {
        console.error('Error al recargar las órdenes:', error);
        this.loading = false;
        loadingMessage.remove();

        const errorMessage = document.createElement('div');
        errorMessage.className = 'fixed top-4 right-4 bg-red-500 text-white px-4 py-2 rounded shadow-lg z-50';
        errorMessage.textContent = 'Error al actualizar los datos';
        document.body.appendChild(errorMessage);

        setTimeout(() => {
          errorMessage.remove();
        }, 3000);
      }
    });
  }

  applySendYesOrNoToAllSpareParts(): void {
    if (!this.selectedOrder?.orderId) return;

    const sendYesOrNo = this.selectedOrder.sendYesOrNo === 'true' ? 'true' : 'false';
    const count = this.selectedOrder.spareParts?.length || 0;
    if (!confirm(`Se aplicará Enviar = ${sendYesOrNo === 'true' ? 'Sí' : 'No'} a los ${count} repuesto(s) de esta orden. ¿Continuar?`)) {
      return;
    }

    this.sparePartService.applySendYesOrNoToOrderSpareParts(this.selectedOrder.orderId, sendYesOrNo).subscribe({
      next: () => {
        const updateOrder = (order: OrderOutModel): OrderOutModel => order.orderId === this.selectedOrder!.orderId
          ? {
              ...order,
              sendYesOrNo,
              spareParts: (order.spareParts || []).map(part => ({ ...part, sendYesOrNo }))
            }
          : order;

        this.selectedOrder = updateOrder(this.selectedOrder);
        this.orders = this.orders.map(updateOrder);
        this.filteredOrdersList = this.filteredOrdersList.map(updateOrder);
        this.sortedAndFilteredOrders = this.sortedAndFilteredOrders.map(updateOrder);
        this.showSuccessMessage(`Enviar actualizado en ${count} repuesto(s).`);
        this.cdr.detectChanges();
      },
      error: () => this.showErrorMessage('No se pudo actualizar Enviar en los repuestos de la orden.')
    });
  }

  calculateOrderStatus(spareParts: SparePartModel[]): string {
    if (!spareParts || spareParts.length === 0) {
      return 'CREADO';
    }

    // 1. Si hay algún repuesto ATRASADO -> RETRASO
    if (spareParts.some(part => part.productStatus === 'ATRASADO')) {
      return 'RETRASO';
    }

    // 2. Si hay algún repuesto en DEVOLUCION-PROVEEDOR -> ATENCION
    if (spareParts.some(part => part.productStatus === 'DEVOLUCION-PROVEEDOR')) {
      return 'ATENCION';
    }

    // 3. Si todos los repuestos están ENTREGADO-EN-LOCAL o EXPORTADO -> FINALIZADO
    if (spareParts.every(part =>
      part.productStatus === 'ENTREGADO-EN-LOCAL' ||
      part.productStatus === 'EXPORTADO')) {
      return 'FINALIZADO';
    }

    // 4. Si hay algún repuesto en STOCK-ENTREGA-EN-LOCAL, STOCK-ENTREGA-AL-CLIENTE o EMPAQUETADO-ENTREGA-AL-CLIENTE -> LLEGO
    if (spareParts.some(part =>
      part.productStatus === 'STOCK-ENTREGA-EN-LOCAL' ||
      part.productStatus === 'STOCK-ENTREGA-AL-CLIENTE' ||
      part.productStatus === 'EMPAQUETADO-ENTREGA-AL-CLIENTE')) {
      return 'LLEGO';
    }

    // 5. Si todos los repuestos están EMPAQUETADOS o EXPORTADOS -> EXPORTADO
    // IMPORTANTE: NO incluir estados STOCK-* porque generan confusión
    // (el usuario ve "EXPORTADO" pero los repuestos están aún en STOCK)
    const exportedStates = [
      'EMPAQUETADO-OTRAS-EXPORTACIONES-AUTOCOMERCIAL',
      'EMPAQUETADO-OTRAS-EXPORTACIONES-SERIMPORT',
      'EMPAQUETADO-EXPORTACION-ACTUAL-AUTOCOMERCIAL',
      'EMPAQUETADO-EXPORTACION-ACTUAL-SERIMPORT',
      'EXPORTADO'  // Estado real de exportado
    ];
    if (spareParts.every(part => exportedStates.includes(part.productStatus))) {
      return 'EXPORTADO';
    }

    // 5.5. Si todos los repuestos están en STOCK para exportación -> EN PROCESO
    const stockExportStates = [
      'STOCK-OTRAS-EXPORTACIONES-AUTOCOMERCIAL',
      'STOCK-OTRAS-EXPORTACIONES-SERIMPORT',
      'STOCK-EXPORTACION-ACTUAL-AUTOCOMERCIAL',
      'STOCK-EXPORTACION-ACTUAL-SERIMPORT'
    ];
    if (spareParts.every(part => stockExportStates.includes(part.productStatus))) {
      return 'EN PROCESO';
    }

    // 6. Si todos los repuestos están en CREADO -> CREADO
    if (spareParts.every(part => part.productStatus === 'CREADO')) {
      return 'CREADO';
    }

    // 7. Si no cumple ninguna condición anterior -> REPUESTOS-PENDIENTES
    return 'REPUESTOS-PENDIENTES';
  }

  // Método para resaltar visualmente un repuesto recién editado
  highlightLastEditedSparePart(sparePartId: number): void {
    this.lastEditedSparePartId = sparePartId;

    // Quitar el resaltado después de 3 segundos
    setTimeout(() => {
      this.lastEditedSparePartId = null;
    }, 3000);
  }

  saveSparePartChanges(): void {
    if (this.selectedSparePart && this.selectedOrder) {
      // Validar el formato de fecha
      if (!this.isValidDateFormat(this.selectedSparePartArrivalDate)) {
        this.showErrorMessage('La fecha debe tener el formato DD/MM/AAAA');
        return;
      }

      // Convertir fecha de string a Date
      const [day, month, year] = this.selectedSparePartArrivalDate.split('/');
      const expectedArrivalDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));

      // Guardar el shippingReceipt original antes de actualizar
      const originalShippingReceipt = this.selectedSparePart.shippingReceipt;

      // Crear una copia del repuesto para la actualización
      const sparePartToUpdate = {
        ...this.selectedSparePart,
        expectedArrivalDate
      };
      sparePartToUpdate.fechaDeComunicado = this.selectedSparePartFechaComunicado
        ? new Date(`${this.selectedSparePartFechaComunicado}T00:00:00`)
        : undefined;
      sparePartToUpdate.mlDeadline = this.selectedSparePartMlDeadline
        ? new Date(`${this.selectedSparePartMlDeadline}T00:00:00`)
        : undefined;
      sparePartToUpdate.fechaExportacion = this.selectedSparePartExportDate
        ? new Date(`${this.selectedSparePartExportDate}T00:00:00`)
        : undefined;
      sparePartToUpdate.arrivalDate = this.selectedSparePartPhysicalArrivalDateTime
        ? parseWallClockDateTimeInput(this.selectedSparePartPhysicalArrivalDateTime)
        : undefined;
      sparePartToUpdate.isFant = this.isTruthyFlag(sparePartToUpdate.isFant ?? (sparePartToUpdate as any).IsFant);
      sparePartToUpdate.fantUrgency = sparePartToUpdate.isFant
        ? normalizeFantUrgency(sparePartToUpdate.fantUrgency ?? (sparePartToUpdate as any).FantUrgency)
        : null;
      sparePartToUpdate.recompra = this.isTruthyFlag(sparePartToUpdate.recompra ?? (sparePartToUpdate as any).Recompra);
      sparePartToUpdate.control = this.isTruthyFlag(sparePartToUpdate.control ?? (sparePartToUpdate as any).Control);
      sparePartToUpdate.anulado = this.isTruthyFlag(sparePartToUpdate.anulado ?? (sparePartToUpdate as any).Anulado);
      sparePartToUpdate.trello = this.isTruthyFlag(sparePartToUpdate.trello ?? (sparePartToUpdate as any).Trello);
      sparePartToUpdate.devZureo = this.isTruthyFlag(sparePartToUpdate.devZureo ?? (sparePartToUpdate as any).DevZureo);
      sparePartToUpdate.entradaSage = this.isTruthyFlag(sparePartToUpdate.entradaSage ?? (sparePartToUpdate as any).EntradaSage);
      sparePartToUpdate.controleSeba = this.isTruthyFlag(sparePartToUpdate.controleSeba ?? (sparePartToUpdate as any).ControleSeba);
      sparePartToUpdate.okAnulacao = this.isTruthyFlag(sparePartToUpdate.okAnulacao ?? (sparePartToUpdate as any).OkAnulacao);
      sparePartToUpdate.facturado = this.isTruthyFlag(sparePartToUpdate.facturado ?? (sparePartToUpdate as any).Facturado);
      sparePartToUpdate.needReview = this.isTruthyFlag(sparePartToUpdate.needReview ?? (sparePartToUpdate as any).NeedReview);
      sparePartToUpdate.isDamaged = normalizeIsDamaged(sparePartToUpdate.isDamaged ?? (sparePartToUpdate as any).IsDamaged);
      delete (sparePartToUpdate as any).IsFant;
      delete (sparePartToUpdate as any).FantUrgency;
      delete (sparePartToUpdate as any).Recompra;
      delete (sparePartToUpdate as any).Control;
      delete (sparePartToUpdate as any).Anulado;
      delete (sparePartToUpdate as any).Trello;
      delete (sparePartToUpdate as any).DevZureo;
      delete (sparePartToUpdate as any).EntradaSage;
      delete (sparePartToUpdate as any).ControleSeba;
      delete (sparePartToUpdate as any).OkAnulacao;
      delete (sparePartToUpdate as any).Facturado;
      delete (sparePartToUpdate as any).NeedReview;
      delete (sparePartToUpdate as any).IsDamaged;

      // CRÍTICO: Preservar el shippingReceipt original para evitar sobrescribirlo
      // Si el repuesto ya tiene un remito asignado (por envío al taller), no lo sobrescribimos
      if (originalShippingReceipt && originalShippingReceipt.trim() !== '') {
        sparePartToUpdate.shippingReceipt = originalShippingReceipt;
      }

      // Normalizar todos los valores numéricos - Asegurarse de que el formato es correcto
      sparePartToUpdate.unitPrice = this.normalizeNumericValue(sparePartToUpdate.unitPrice);
      sparePartToUpdate.valorDeVenta = this.normalizeNumericValue(sparePartToUpdate.valorDeVenta);
      sparePartToUpdate.netWheight = this.normalizeNumericValue(sparePartToUpdate.netWheight);
      sparePartToUpdate.grossWheight = this.normalizeNumericValue(sparePartToUpdate.grossWheight);

      // Para amount, asegurar que sea un número entero y mínimo 1
      sparePartToUpdate.amount = Math.max(1, Math.round(this.normalizeNumericValue(sparePartToUpdate.amount)));

      // Asegurar que los valores numéricos son exactamente con 2 decimales
      sparePartToUpdate.unitPrice = parseFloat(sparePartToUpdate.unitPrice.toFixed(2));
      sparePartToUpdate.valorDeVenta = parseFloat(sparePartToUpdate.valorDeVenta.toFixed(2));
      sparePartToUpdate.netWheight = parseFloat(sparePartToUpdate.netWheight.toFixed(2));
      sparePartToUpdate.grossWheight = parseFloat(sparePartToUpdate.grossWheight.toFixed(2));

      const isDraftSparePartEdition = !this.isDuplicatingSparePart &&
        (this.isDuplicatingOrder || Number(this.selectedOrder.orderId) <= 0 || Number(sparePartToUpdate.sparePartId) <= 0);
      if (isDraftSparePartEdition || this.isDuplicatingSparePart) {
        const saleValueError = getSaleValueValidationError(
          sparePartToUpdate.valorDeVenta,
          sparePartToUpdate.sparePartName
        );
        if (saleValueError) {
          this.showErrorMessage(saleValueError);
          return;
        }
      }

      // Permitir valores vacíos en la edición - solo validar estructura básica

      // Asegurarse de que el repuesto tenga la propiedad Orders inicializada para el backend
      if (!('Orders' in sparePartToUpdate)) {
        (sparePartToUpdate as any).Orders = [{
          Amount: sparePartToUpdate.amount,
          OrderId: this.selectedOrder.orderId,
          SparePartId: sparePartToUpdate.sparePartId,
          SparePartName: sparePartToUpdate.sparePartName,
          TotalPrice: sparePartToUpdate.unitPrice * sparePartToUpdate.amount
        }];
      }

      // Mostrar indicador de carga
      const loadingIndicator = document.createElement('div');
      loadingIndicator.id = 'spare-part-saving-indicator';
      loadingIndicator.className = 'fixed top-4 right-4 bg-blue-500 text-white px-4 py-2 rounded shadow-lg z-50 flex items-center';
      loadingIndicator.innerHTML = `
        <div class="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
        <span>Guardando cambios...</span>
      `;
      document.body.appendChild(loadingIndicator);

      if (isDraftSparePartEdition) {
        const draftUpdatedPart = this.normalizeSparePartFlags({
          ...sparePartToUpdate,
          orderId: 0
        } as any);
        const draftSpareParts = this.selectedOrder.spareParts || [];
        const draftIndex = draftSpareParts.findIndex(sp => Number(sp.sparePartId) === Number(draftUpdatedPart.sparePartId));
        if (draftIndex !== -1) {
          draftSpareParts[draftIndex] = draftUpdatedPart;
          this.selectedOrder.spareParts = [...draftSpareParts];
        }
        this.selectedSparePart = draftUpdatedPart;
        this.updateSparePartSnapshotFromCurrent();
        const indicator = document.getElementById('spare-part-saving-indicator');
        if (indicator) indicator.remove();
        this.showSuccessMessage('Repuesto del borrador actualizado correctamente.');
        this.closeSparePartModal();
        return;
      }

      if (this.isDuplicatingSparePart) {
        const targetOrderId = Number(this.duplicateSparePartTargetOrderId ?? this.selectedOrder.orderId);
        const executeDuplicate = () => {
          if (!targetOrderId || targetOrderId <= 0 || this.isDuplicatingOrder || this.selectedOrder!.orderId <= 0) {
            const localDuplicatedPart = this.normalizeSparePartFlags(applyPostEmergentCommentDuplicateDefaults({
              ...sparePartToUpdate,
              sparePartId: this.nextTemporarySparePartId(),
              orderId: 0,
              packageNumber: '',
              exportNumber: '',
              shippingReceipt: '',
              arrivalDate: undefined,
              lastExportedAt: undefined,
              fechaExportacion: undefined
            } as any));
            this.selectedOrder!.spareParts = [...(this.selectedOrder!.spareParts || []), localDuplicatedPart];
            const indicator = document.getElementById('spare-part-saving-indicator');
            if (indicator) indicator.remove();
            this.showSuccessMessage(`Repuesto "${localDuplicatedPart.sparePartName}" agregado al borrador de orden.`);
            this.closeSparePartModal();
            return;
          }

          this.resolveOrderByNumber(this.duplicateSparePartTargetOrderNumber, (targetOrder) => {
            const sparePartToCreate = applyPostEmergentCommentDuplicateDefaults({
              ...sparePartToUpdate,
              sparePartId: 0,
              orderId: targetOrder.orderId,
              packageNumber: '',
              exportNumber: '',
              shippingReceipt: '',
              arrivalDate: undefined,
              lastExportedAt: undefined,
              fechaExportacion: undefined
            } as SparePartModel);

            this.sparePartService.addSparePart(sparePartToCreate, targetOrder.orderId).subscribe({
              next: () => {
                const indicator = document.getElementById('spare-part-saving-indicator');
                if (indicator) indicator.remove();
                this.showSuccessMessage(`¡Repuesto "${sparePartToCreate.sparePartName}" duplicado en la orden #${targetOrder.orderNumber}!`);
                this.closeSparePartModal();
                if (targetOrder.orderId === this.selectedOrder!.orderId) {
                  this.refreshSelectedOrderFromBackend(this.selectedOrder!.orderId);
                } else {
                  this.reloadTable();
                }
              },
              error: (error) => {
                const indicator = document.getElementById('spare-part-saving-indicator');
                if (indicator) indicator.remove();
                console.error('Error al duplicar el repuesto:', error);
                this.showErrorMessage('Error al guardar el repuesto duplicado.');
              }
            });
          }, () => {
            const indicator = document.getElementById('spare-part-saving-indicator');
            if (indicator) indicator.remove();
          });
        };

        const duplicateSparePartCandidates: LowSaleRatioSparePartCandidate[] = [{
          sparePartName: sparePartToUpdate.sparePartName,
          productCode: sparePartToUpdate.productCode,
          orderNumber: this.duplicateSparePartTargetOrderNumber || this.selectedOrder.orderNumber || sparePartToUpdate.orderNumber || '',
          siniestro: this.selectedOrder.siniestro || sparePartToUpdate.siniestro || '',
          unitPrice: sparePartToUpdate.unitPrice,
          valorDeVenta: sparePartToUpdate.valorDeVenta
        }];
        if (this.requestLowSaleRatioConfirmation(
          duplicateSparePartCandidates,
          'Confirmar guardado de repuesto duplicado',
          executeDuplicate
        )) {
          const indicator = document.getElementById('spare-part-saving-indicator');
          if (indicator) indicator.remove();
          return;
        }

        executeDuplicate();
        return;
      }

      this.sparePartService.updateSparePart(sparePartToUpdate, this.selectedOrder!.orderId)
        .subscribe({
          next: (response: any) => {
            this.showEmergentCommentAlertGroups(response?.emergentCommentAlerts, 'Actualización de repuesto');
            // Quitar el indicador de carga
            const indicator = document.getElementById('spare-part-saving-indicator');
            if (indicator) indicator.remove();

            if (response?.updatedFechaDeComunicado) {
              sparePartToUpdate.fechaDeComunicado = new Date(response.updatedFechaDeComunicado);
              this.selectedSparePartFechaComunicado = this.formatDateForInput(sparePartToUpdate.fechaDeComunicado as any);
            }

            const savedSparePart = this.normalizeSparePartFlags({ ...sparePartToUpdate } as any);
            this.updateSparePartInLocalState(savedSparePart, this.selectedOrder?.orderId);

            // Actualizar el repuesto en la orden local
            const orderIndex = this.orders.findIndex(o => o.orderId === this.selectedOrder?.orderId);

            if (orderIndex !== -1) {
              const sparePartIndex = this.orders[orderIndex].spareParts.findIndex(
                sp => sp.sparePartId === sparePartToUpdate.sparePartId
              );

              if (sparePartIndex !== -1) {
                const updated = savedSparePart;
                this.orders[orderIndex].spareParts[sparePartIndex] = updated;
                this.orders[orderIndex].spareParts = [...this.orders[orderIndex].spareParts];

                if (this.selectedOrder && this.selectedOrder.orderId === this.orders[orderIndex].orderId) {
                  const selIdx = this.selectedOrder.spareParts.findIndex(sp => sp.sparePartId === sparePartToUpdate.sparePartId);
                  if (selIdx !== -1) {
                    this.selectedOrder.spareParts[selIdx] = updated;
                    this.selectedOrder.spareParts = [...this.selectedOrder.spareParts];
                  }
                }
                if (this.selectedSparePart && this.selectedSparePart.sparePartId === sparePartToUpdate.sparePartId) {
                  this.selectedSparePart = updated;
                  if (updated.expectedArrivalDate) {
                    this.selectedSparePartArrivalDate = this.formatDate(updated.expectedArrivalDate);
                  }
                  this.selectedSparePartExportDate = this.formatDateForInput(updated.fechaExportacion as any);
                  this.selectedSparePartPhysicalArrivalDateTime = this.formatDateTimeForInput(updated.arrivalDate as any);
                  this.updateSparePartSnapshotFromCurrent();
                }

                const filteredOrderIndex = this.filteredOrdersList.findIndex(o => o.orderId === this.selectedOrder?.orderId);
                if (filteredOrderIndex !== -1) {
                  const filteredSparePartIndex = this.filteredOrdersList[filteredOrderIndex].spareParts.findIndex(
                    sp => sp.sparePartId === sparePartToUpdate.sparePartId
                  );
                  if (filteredSparePartIndex !== -1) {
                    this.filteredOrdersList[filteredOrderIndex].spareParts[filteredSparePartIndex] = updated;
                    this.filteredOrdersList[filteredOrderIndex].spareParts = [...this.filteredOrdersList[filteredOrderIndex].spareParts];
                  }
                }

                this.highlightLastEditedSparePart(sparePartToUpdate.sparePartId);
              }

              // Recalcular y actualizar el estado de la orden
              const newStatus = this.calculateOrderStatus(this.orders[orderIndex].spareParts);

              if (newStatus !== this.orders[orderIndex].orderStatus) {
                this.orders[orderIndex].orderStatus = newStatus;

                // CRÍTICO: NO enviar los repuestos al actualizar solo el estado de la orden
                // Solo enviar los campos de la orden, no los repuestos
                const orderUpdateData = {
                  orderId: this.orders[orderIndex].orderId,
                  orderNumber: this.orders[orderIndex].orderNumber,
                  providerName: this.orders[orderIndex].providerName,
                  providerPhone: this.orders[orderIndex].providerPhone,
                  clientName: this.orders[orderIndex].clientName,
                  clientPhone: this.orders[orderIndex].clientPhone,
                  invoiceNumber: this.orders[orderIndex].invoiceNumber,
                  employeeName: this.orders[orderIndex].employeeName,
                  deliveryAddress: this.orders[orderIndex].deliveryAddress,
                  carMakeModel: this.orders[orderIndex].carMakeModel,
                  isExport: this.orders[orderIndex].isExport,
                  sendYesOrNo: this.orders[orderIndex].sendYesOrNo,
                  siniestro: this.orders[orderIndex].siniestro,
                  clienteUY: this.orders[orderIndex].clienteUY,
                  workshopId: this.orders[orderIndex].workshopId,
                  creationDate: this.orders[orderIndex].creationDate,
                  expectedArrivalDate: this.orders[orderIndex].expectedArrivalDate,
                  isNearArrival: this.orders[orderIndex].isNearArrival,
                  isNearArrivalAccepted: this.orders[orderIndex].isNearArrivalAccepted,
                  orderStatus: newStatus
                  // NO incluir spareParts aquí
                };

                // Actualizar el estado en el backend pero sin esperar la respuesta
                this.http.put(`${this.apiUrl}/${this.selectedOrder?.orderId}`, orderUpdateData).subscribe({
                  next: () => {
                    // Actualizar la vista principal
                    if (this.selectedOrder) {
                      this.selectedOrder.orderStatus = newStatus;
                    }
                  },
                  error: (error) => {
                    console.error('❌ Error al actualizar el estado de la orden:', error);
                  }
                });
              }

              // Actualizar los totales y resúmenes sin recargar la tabla
            }

            // Forzar actualización de la vista para que la tabla de repuestos refleje los cambios
            this.cdr.detectChanges();

            // Mostrar mensaje de éxito y cerrar el modal
            this.showSuccessMessage(`¡Repuesto "${sparePartToUpdate.sparePartName}" actualizado exitosamente!`);
            this.closeSparePartModal();
          },
          error: (error) => {
            // Quitar el indicador de carga
            const indicator = document.getElementById('spare-part-saving-indicator');
            if (indicator) indicator.remove();

            console.error('Error al guardar los cambios del repuesto:', error);
            let errorMessage = 'Error al guardar los cambios del repuesto: ';

            if (error.error && error.error.message) {
              errorMessage += error.error.message;
            } else if (error.status === 404) {
              errorMessage += 'El repuesto no fue encontrado en el servidor.';
            } else if (error.status === 400) {
              if (error.error && typeof error.error === 'string') {
                errorMessage += error.error;
              } else {
                errorMessage += 'Datos inválidos. Por favor, verifique la información ingresada.';
              }
            } else if (error.status === 500) {
              errorMessage += 'Error interno del servidor. Por favor, intente nuevamente más tarde.';
            } else {
              errorMessage += 'Error desconocido al procesar la solicitud.';
            }

            this.showErrorMessage(errorMessage);
          }
        });
    }
  }

  showSuccessMessage(message: string): void {
    const notification = document.createElement('div');
    notification.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded shadow-lg z-[9999] animate-fade-in-out';
    notification.style.zIndex = '9999';
    notification.style.animation = 'fadeInOut 3s ease-in-out';
    notification.textContent = message;
    document.body.appendChild(notification);

    // Asegurarnos de que el mensaje sea visible
    notification.style.display = 'block';
    notification.style.opacity = '1';

    // Agregar estilos de animación si no existen
    if (!document.querySelector('#notification-styles')) {
      const style = document.createElement('style');
      style.id = 'notification-styles';
      style.textContent = `
        @keyframes fadeInOut {
          0% { opacity: 0; transform: translateY(-20px); }
          10% { opacity: 1; transform: translateY(0); }
          90% { opacity: 1; transform: translateY(0); }
          100% { opacity: 0; transform: translateY(-20px); }
        }
      `;
      document.head.appendChild(style);
    }

    setTimeout(() => {
      notification.remove();
    }, 3000);
  }

  showErrorMessage(message: string): void {
    const notification = document.createElement('div');
    notification.className = 'fixed top-4 right-4 bg-red-500 text-white px-6 py-3 rounded shadow-lg z-50 animate-fade-in-out';
    notification.textContent = message;
    document.body.appendChild(notification);
    setTimeout(() => notification.remove(), 3000);
  }

  private showEmergentCommentAlertGroups(alertGroups: any[] | undefined, context: string): void {
    if (!Array.isArray(alertGroups) || alertGroups.length === 0) {
      return;
    }

    const lines: string[] = [];
    alertGroups.forEach((group, groupIndex) => {
      const comment = (group?.comment || '').toString().trim();
      if (!comment) {
        return;
      }

      const items = Array.isArray(group?.items) ? group.items : [];
      const names = Array.from(new Set(
        items
          .map((item: any) => (item?.sparePartName || '').toString().trim())
          .filter((name: string) => name !== '')
      ));
      const namesText = names.length > 0 ? ` - ${names.join(', ')}` : '';
      lines.push(`${groupIndex + 1}) "${comment}"${namesText}`);
    });

    if (lines.length === 0) {
      return;
    }

    const previousNotification = document.getElementById('order-history-emergent-comment-alert');
    if (previousNotification) {
      previousNotification.remove();
    }

    const notification = document.createElement('div');
    notification.id = 'order-history-emergent-comment-alert';
    notification.className = 'fixed top-24 right-4 bg-amber-500 text-white px-4 py-3 rounded shadow-lg z-[10000] max-w-xl whitespace-pre-line';

    const title = document.createElement('div');
    title.className = 'font-semibold mb-1';
    title.textContent = `Comentario emergente (${context})`;

    const body = document.createElement('div');
    body.textContent = lines.join('\n');

    notification.appendChild(title);
    notification.appendChild(body);
    document.body.appendChild(notification);

    setTimeout(() => {
      const message = document.getElementById('order-history-emergent-comment-alert');
      if (message) message.remove();
    }, 7000);
  }

  exportToExcel(): void {
    // Iniciar animación de carga
    this.startLoadingAnimation();
    this.loadingMessage = 'Exportando a Excel';
    this.loadingDetail = 'Preparando datos...';
    this.loading = true;

    // Mostrar indicador de carga en la esquina superior derecha
    const loadingIndicator = document.createElement('div');
    loadingIndicator.id = 'excel-loading-indicator';
    loadingIndicator.className = 'fixed top-4 right-4 bg-green-500 text-white px-4 py-2 rounded shadow-lg z-50 flex items-center';
    loadingIndicator.innerHTML = `
      <div class="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
      <span>Exportando a Excel...</span>
    `;
    document.body.appendChild(loadingIndicator);

    // Exportar el conjunto completo de órdenes filtradas (no solo la página visible).
    this.fetchAllOrdersWithCurrentFilters(1, []).subscribe({
      next: (allOrders) => {
        const orders = this.hasActiveOrderColumnFilters()
          ? allOrders.filter(o => this.passesOrderColumnFilters(o))
          : allOrders;

        this.loadingDetail = `Procesando ${orders.length} órdenes...`;

        if (orders.length === 0) {
          this.loadingDetail = 'No hay datos que coincidan con los filtros aplicados';
          this.showErrorMessage('No hay datos que coincidan con los filtros aplicados para exportar');

          // Detener la animación de carga
          this.stopLoadingAnimation();
          this.loading = false;

          // Eliminar el indicador de carga
          const indicator = document.getElementById('excel-loading-indicator');
          if (indicator) {
            indicator.remove();
          }

          return;
        }

        // Crear una matriz para los datos de Excel
        const data: any[][] = [];

        // Encabezados según el orden solicitado para exportación desde ventana de órdenes.
        data.push([
          'Número de Orden',
          'Fecha de Creación',
          'Siniestro',
          'Número de Factura',
          'Cliente',
          'Teléfono Cliente',
          'Proveedor',
          'Teléfono Proveedor',
          'Fecha de Llegada Espera',
          'Empleado',
          'Nombre del Repuesto',
          'Código del Producto',
          'Cantidad',
          'Precio Unitario',
          'Estado del Producto',
          'Nota Fiscal',
          'NCM',
          'Código BR',
          'Valor de Venta TOTAL',
          'Dañado',
          'Peso Neto',
          'Peso Bruto',
          'Fecha de Llegada Espera',
          'Precio Total de la Orden',
          'Cant. Rep. Orden',
          'País',
          'Vehículo',
          'Dirección de Entrega',
          'Número de Paquete',
          'Es Exportación',
          'Enviar',
          'Estado de Envío'
        ]);
        const EXCEL_COLUMNS_COUNT = 32;
        const sanitizeExcelRow = (row: any[], nullFallback: string = ''): any[] => {
          const sanitized = row.map((value) => (value === null || value === undefined ? nullFallback : value));
          if (sanitized.length < EXCEL_COLUMNS_COUNT) {
            return [...sanitized, ...Array(EXCEL_COLUMNS_COUNT - sanitized.length).fill(nullFallback)];
          }
          if (sanitized.length > EXCEL_COLUMNS_COUNT) {
            return sanitized.slice(0, EXCEL_COLUMNS_COUNT);
          }
          return sanitized;
        };

        // Contador para seguimiento del progreso
        let processedOrders = 0;
        const totalOrders = orders.length;
        let totalExportedRows = 0;

        // Exportar una fila por repuesto para cada orden.
        orders.forEach(order => {
          processedOrders++;
          this.loadingProgress = (processedOrders / totalOrders) * 100;
          this.loadingDetail = `Procesando orden ${processedOrders} de ${totalOrders}...`;

          const spareParts = order.spareParts || [];

          // Si no hay repuestos, se mantiene una fila de la orden con columnas de repuesto vacías.
          if (spareParts.length === 0) {
            data.push(sanitizeExcelRow([
              order.orderNumber || '',
              this.formatDate(order.creationDate),
              order.siniestro || '',
              order.invoiceNumber || '',
              order.clientName || '',
              order.clientPhone || '',
              order.providerName || '',
              order.providerPhone || '',
              this.formatDate(order.expectedArrivalDate),
              order.employeeName || '',
              '',
              '',
              '',
              '',
              '',
              '',
              '',
              '',
              '',
              '',
              '',
              '',
              '',
              this.getOrderTotal(order).toString(),
              (order.spareParts?.length || 0).toString(),
              '',
              order.carMakeModel || '',
              order.deliveryAddress || '',
              '',
              order.isExport || '',
              order.sendYesOrNo || '',
              ''
            ]));
            totalExportedRows++;
            return;
          }

          spareParts.forEach(sparePart => {
            data.push(sanitizeExcelRow([
              order.orderNumber || '',
              this.formatDate(order.creationDate),
              order.siniestro || '',
              order.invoiceNumber || '',
              order.clientName || '',
              order.clientPhone || '',
              order.providerName || '',
              order.providerPhone || '',
              this.formatDate(order.expectedArrivalDate),
              order.employeeName || '',
              sparePart.sparePartNamePt || sparePart.sparePartName || '',
              sparePart.productCode || '',
              sparePart.amount?.toString() || '',
              sparePart.unitPrice?.toString() || '',
              sparePart.productStatus || '',
              sparePart.taxNote || '',
              sparePart.ncm || '',
              sparePart.codBr || '',
              sparePart.valorDeVenta?.toString() || '',
              sparePart.isDamaged || '',
              sparePart.netWheight?.toString() || '',
              sparePart.grossWheight?.toString() || '',
              this.formatDate(sparePart.arrivalDate || sparePart.expectedArrivalDate),
              this.getOrderTotal(order).toString(),
              (order.spareParts?.length || 0).toString(),
              sparePart.country || '',
              order.carMakeModel || '',
              order.deliveryAddress || '',
              sparePart.packageNumber || '',
              order.isExport || '',
              order.sendYesOrNo || '',
              sparePart.shippingStatus || ''
            ]));

            totalExportedRows++;
          });
        });


        this.loadingDetail = `Generando archivo Excel con ${totalExportedRows} filas...`;

        // Crear el libro de Excel
        const worksheet: XLSX.WorkSheet = XLSX.utils.aoa_to_sheet(data);
        const workbook: XLSX.WorkBook = XLSX.utils.book_new();

        // Ajustar el ancho de las columnas
        const wscols = data[0].map(() => ({ wch: 20 })); // Ancho de 20 para todas las columnas
        worksheet['!cols'] = wscols;

        // Agregar la hoja al libro
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Órdenes');

        // Generar el archivo
        const excelBuffer: any = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

        // Generar nombre de archivo con fecha y filtros aplicados
        let fileName = 'Ordenes_' + new Date().toISOString().split('T')[0];

        // Agregar información de filtros al nombre del archivo
        if (this.searchTerm) {
          fileName += '_Busqueda_' + this.searchTerm.replace(/[^a-zA-Z0-9]/g, '_').substring(0, 10);
        }

        if (this.dateFilterType !== 'all') {
          fileName += '_Fecha_' + this.dateFilterType;
        }

        fileName += '.xlsx';

        this.saveAsExcelFile(excelBuffer, fileName);

        // Registrar la exportación Excel en "Carpetas Exportadas" solo como metadatos.
        this.registerExcelExportMetadata(orders, fileName, totalExportedRows, excelBuffer?.byteLength ?? 0)
          .catch(error => {
            console.error('No se pudo registrar la exportación Excel en Carpetas Exportadas:', error);
          });

        // Detener la animación de carga
        this.stopLoadingAnimation();
        this.loading = false;

        // Eliminar el indicador de carga
        const indicator = document.getElementById('excel-loading-indicator');
        if (indicator) {
          indicator.remove();
        }

        // Mostrar mensaje de éxito
        this.showSuccessMessage(`Exportación completada: ${totalExportedRows} filas exportadas`);
      },
      error: (error) => {
        console.error('Error al exportar a Excel:', error);
        this.loadingDetail = 'Error al exportar a Excel';
        this.showErrorMessage('Error al exportar a Excel. Por favor, inténtelo de nuevo.');

        // Detener la animación de carga
        this.stopLoadingAnimation();
        this.loading = false;

        // Eliminar el indicador de carga
        const indicator = document.getElementById('excel-loading-indicator');
        if (indicator) {
          indicator.remove();
        }
      }
    });
  }

  private async registerExcelExportMetadata(
    orders: OrderOutModel[],
    fileName: string,
    totalExportedRows: number,
    totalSize: number
  ): Promise<void> {
    if (!orders || orders.length === 0) {
      return;
    }

    try {
      const now = new Date();
      const timestamp = now.toISOString().replace(/[:.]/g, '-');
      const folderModel: CreateBasicExportedFolderModel = {
        folderName: `Excel_${timestamp}`,
        description: `Exportación Excel: ${fileName}`,
        orderCount: orders.length,
        sparePartCount: totalExportedRows,
        totalSize,
        fileName
      };

      await this.exportedFoldersService.createBasicExportedFolder(folderModel).toPromise();
    } catch (error) {
      console.error('Error registrando metadatos de exportación Excel:', error);
      throw error;
    }
  }

  saveAsExcelFile(buffer: any, fileName: string): void {
    const data: Blob = new Blob([buffer], { type: this.EXCEL_TYPE });
    saveAs(data, fileName);

    // Mostrar mensaje de éxito
    setTimeout(() => {
      this.loading = false;
      this.showSuccessMessage('Archivo Excel generado correctamente');
    }, 500);
  }

  deleteSparePart(sparePart: SparePartModel, order: OrderOutModel): void {
    const confirmed = window.confirm('¿Estás seguro de que deseas eliminar este repuesto? Esta acción no se puede deshacer.');
    if (confirmed) {
      if (this.isDuplicatingOrder || !order.orderId || order.orderId <= 0) {
        if (this.selectedOrder) {
          this.selectedOrder.spareParts = this.selectedOrder.spareParts.filter(
            sp => sp.sparePartId !== sparePart.sparePartId
          );
          this.selectedOrder.orderStatus = this.calculateOrderStatus(this.selectedOrder.spareParts);
          this.initialOrderSnapshot = this.getOrderFormSnapshotJson();
          if (this.selectedSparePart?.sparePartId === sparePart.sparePartId) {
            this.closeSparePartModal();
          }
          this.showSuccessMessage('Repuesto eliminado del borrador duplicado.');
        }
        return;
      }

      if (sparePartHasPackageOrPrintedLabel(sparePart)) {
        this.showErrorMessage(SPARE_PART_DELETE_BLOCKED_MESSAGE);
        return;
      }

      const sparePartId = sparePart.sparePartId;
      const orderId = order.orderId;
      this.sparePartService.deleteSparePart(sparePartId, orderId).subscribe({
        next: () => {
          // Actualizar la lista de repuestos localmente
          const orderIndex = this.orders.findIndex(o => o.orderId === orderId);
          if (orderIndex !== -1) {
            this.orders[orderIndex].spareParts = this.orders[orderIndex].spareParts.filter(
              sp => sp.sparePartId !== sparePartId
            );

            // Recalcular el estado de la orden
            const newStatus = this.calculateOrderStatus(this.orders[orderIndex].spareParts);
            if (newStatus !== this.orders[orderIndex].orderStatus) {
              this.orders[orderIndex].orderStatus = newStatus;
              // Actualizar el estado en el backend
              this.http.put(`${this.apiUrl}/${orderId}`, {
                ...this.orders[orderIndex],
                orderStatus: newStatus
              }).subscribe();
            }

            // Si estamos en el modal de orden, actualizar también el selectedOrder
            if (this.selectedOrder && this.selectedOrder.orderId === orderId) {
              this.selectedOrder.spareParts = this.selectedOrder.spareParts.filter(
                sp => sp.sparePartId !== sparePartId
              );
              this.selectedOrder.orderStatus = newStatus;
            }

            // Actualizar la lista filtrada
            const filteredOrderIndex = this.filteredOrdersList.findIndex(o => o.orderId === orderId);
            if (filteredOrderIndex !== -1) {
              this.filteredOrdersList[filteredOrderIndex].spareParts = this.filteredOrdersList[filteredOrderIndex].spareParts.filter(
                sp => sp.sparePartId !== sparePartId
              );
              this.filteredOrdersList[filteredOrderIndex].orderStatus = newStatus;
            }
          }

          // Actualizar contadores
          this.totalSpareParts--;

          this.showSuccessMessage('Repuesto eliminado exitosamente');
        },
        error: (error) => {
          console.error('Error al eliminar el repuesto:', error);
          this.showErrorMessage(readApiErrorMessage(error, 'Error al eliminar el repuesto'));
        }
      });
    }
  }

  deleteOrder(order: OrderOutModel): void {
    const confirmed = window.confirm('¿Estás seguro de que deseas eliminar esta orden? Esta acción eliminará todos los repuestos asociados y no se puede deshacer.');
    if (confirmed) {
      if ((order.spareParts || []).some(sparePart => sparePartHasPackageOrPrintedLabel(sparePart))) {
        this.showErrorMessage(ORDER_DELETE_BLOCKED_MESSAGE);
        return;
      }

      // Primero eliminar todos los spare parts
      const deleteSparePartsPromises = (order.spareParts || []).map(sparePart =>
        this.sparePartService.deleteSparePart(sparePart.sparePartId, order.orderId).toPromise()
      );

      Promise.all(deleteSparePartsPromises)
        .then(() => {
          // Una vez eliminados todos los spare parts, eliminar la orden
          return this.orderService.deleteOrder(order.orderId).toPromise();
        })
        .then(() => {
          // Eliminar la orden de las listas locales
          this.orders = this.orders.filter(o => o.orderId !== order.orderId);
          this.filteredOrdersList = this.filteredOrdersList.filter(o => o.orderId !== order.orderId);
          this.syncOrdersTableAllSelectedHeader();

          // Actualizar contadores
          this.totalOrdersInDB--;
          this.filteredTotal--;
          this.totalSpareParts -= order.spareParts?.length || 0;
          this.calculateTotalPages();

          // Si la página actual queda vacía, ir a la página anterior
          if (this.filteredOrdersList.length === 0 && this.currentPage > 1) {
            this.goToPage(this.currentPage - 1);
          }

          this.showSuccessMessage('Orden y sus repuestos eliminados exitosamente');
          this.closeModal();
        })
        .catch(error => {
          console.error('Error al eliminar la orden y sus repuestos:', error);
          this.showErrorMessage(readApiErrorMessage(error, 'Error al eliminar la orden y sus repuestos'));
        });
    }
  }

  toggleSparePartsColumns() {
    this.showAllColumns = !this.showAllColumns; // Alternar entre mostrar todos o solo algunos datos
  }

  toggleSparePartComponent(): void {
    this.openAddSparePartComponent = !this.openAddSparePartComponent;
  }

  async loadOrderDetails(orderIds: number[]) {
    try {
      const details = await this.orderService.getOrdersByIds(orderIds).toPromise();
      if (details) {
        this.orders = [...this.orders, ...details];
      }
    } catch (error) {
      console.error('Error loading order details:', error);
    }
  }

  // Método para manejar la adición de un nuevo repuesto
  onSparePartAdded(newSparePart: SparePartModel): void {
    if (this.selectedOrder) {
      // Agregar el nuevo repuesto a la lista local. Se reasigna en vez de mutar in situ
      // para que el cache de getAllSpareParts() lo detecte por identidad.
      this.selectedOrder.spareParts = [...this.selectedOrder.spareParts, newSparePart];

      // Recalcular el estado de la orden
      const newStatus = this.calculateOrderStatus(this.selectedOrder.spareParts);
      if (newStatus !== this.selectedOrder.orderStatus) {
        this.selectedOrder.orderStatus = newStatus;
        // Actualizar el estado en el backend
        this.http.put(`${this.apiUrl}/${this.selectedOrder.orderId}`, {
          ...this.selectedOrder,
          orderStatus: newStatus
        }).subscribe();
      }

      // Actualizar la orden en la lista principal
      const orderIndex = this.orders.findIndex(o => o.orderId === this.selectedOrder?.orderId);
      if (orderIndex !== -1) {
        this.orders[orderIndex] = { ...this.selectedOrder };
      }

      // Cerrar el componente de agregar repuesto
      this.toggleSparePartComponent();
      this.showSuccessMessage('Repuesto agregado exitosamente');
    }
  }

  async loadMoreOrders(): Promise<void> {
    if (this.loading || this.allOrdersLoaded) return Promise.resolve();

    return new Promise((resolve, reject) => {
      this.currentPage++;

      this.orderService.getPaginatedOrders(
        this.currentPage,
        this.pageSize,
        this.searchTerm,
        this.startDate,
        this.endDate,
        undefined,
        this.selectedWorkshopId ?? undefined,
        this.onlyLast60Days ? 60 : undefined,
        this.sortedBy || undefined,
        this.sortOrder,
        this.getServerOrderColumnFilters()
      ).subscribe({
        next: (response) => {
          if (response.orders.length === 0) {
            this.allOrdersLoaded = true;
            const completionMessage = document.createElement('div');
            completionMessage.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded shadow-lg z-50 animate-fade-in-out';
            completionMessage.textContent = `Se cargaron todas las órdenes (${this.orders.length} en total)`;
            document.body.appendChild(completionMessage);
            setTimeout(() => completionMessage.remove(), 3000);
            resolve();
          } else {
            this.orders = [...this.orders, ...response.orders];
            this.orders.sort((a, b) => {
              return new Date(b.creationDate).getTime() - new Date(a.creationDate).getTime();
            });
            // Eliminamos la llamada recursiva para evitar bucles infinitos
            resolve();
          }
        },
        error: (error) => {
          console.error('Error al cargar más órdenes:', error);
          this.currentPage--;
          reject(error);
        }
      });
    });
  }

  showArrivalAlert(expectedArrivalDate: string | Date, order?: OrderOutModel): boolean {
    if (!expectedArrivalDate) return false;

    // Si la orden ya tiene la alerta aceptada, no mostrar
    if (order && order.isNearArrivalAccepted) return false;

    const arrival = new Date(expectedArrivalDate);
    const today = new Date();

    // Si la fecha ya pasó, no mostrar alerta
    if (arrival < today) return false;

    // Calcular la diferencia en días
    const diffTime = arrival.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    // Mostrar alerta si estamos dentro del umbral de días
    return diffDays <= this.ALERT_DAYS_THRESHOLD;
  }

  // Método para aceptar la alerta de llegada próxima
  acceptArrivalAlert(event: Event, order: OrderOutModel): void {
    // Detener la propagación del evento para evitar que se abra el modal
    event.stopPropagation();

    // Actualizar la propiedad en el objeto local
    order.isNearArrivalAccepted = true;

    // Enviar la actualización al servidor
    this.http.put(`${this.apiUrl}/${order.orderId}`, {
      ...order,
      isNearArrivalAccepted: true
    }).subscribe({
      next: () => {
        this.showSuccessMessage('Alerta de llegada aceptada');
      },
      error: (error) => {
        console.error('Error al aceptar la alerta de llegada:', error);
        // Revertir el cambio local en caso de error
        order.isNearArrivalAccepted = false;
        this.showErrorMessage('Error al aceptar la alerta de llegada');
      }
    });
  }

  getDaysUntilArrival(expectedArrivalDate: string | Date): number {
    if (!expectedArrivalDate) return 0;

    const arrival = new Date(expectedArrivalDate);
    const today = new Date();

    // Si la fecha ya pasó, retornar 0
    if (arrival < today) return 0;

    // Calcular la diferencia en días
    const diffTime = arrival.getTime() - today.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }

  onCreationDateChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input && this.isValidDateFormat(input.value)) {
      this.selectedOrderCreationDate = this.convertToISODate(input.value);
    }
  }

  onArrivalDateChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input && this.isValidDateFormat(input.value)) {
      this.selectedOrderArrivalDate = this.convertToISODate(input.value);
    }
  }

  onSparePartArrivalDateChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input && this.isValidDateFormat(input.value)) {
      this.selectedSparePartArrivalDate = this.convertToISODate(input.value);
    }
  }

  // Método para validar el formato DD/MM/AAAA
  isValidDateFormat(dateStr: string): boolean {
    if (!dateStr) return false;

    // Verificar el formato DD/MM/AAAA
    const regex = /^(\d{2})\/(\d{2})\/(\d{4})$/;
    if (!regex.test(dateStr)) return false;

    // Extraer los componentes de la fecha
    const [, day, month, year] = dateStr.match(regex) || [];

    // Convertir a números
    const numYear = parseInt(year);
    const numMonth = parseInt(month);
    const numDay = parseInt(day);

    // Validar rangos
    if (numYear < 1900 || numYear > 2100) return false;
    if (numMonth < 1 || numMonth > 12) return false;
    if (numDay < 1 || numDay > 31) return false;

    // Validar días según el mes
    const daysInMonth = new Date(numYear, numMonth, 0).getDate();
    if (numDay > daysInMonth) return false;

    return true;
  }

  // Método para convertir fecha de DD/MM/AAAA a YYYY-MM-DD
  convertToISODate(dateStr: string): string {
    if (!dateStr) return '';
    const [day, month, year] = dateStr.split('/');
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }

  // Método para convertir fecha de YYYY-MM-DD a DD/MM/AAAA
  convertToDisplayDate(dateStr: string): string {
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-');
    return `${day}/${month}/${year}`;
  }

  // Método para formatear fecha para mostrar en los filtros
  formatFilterDate(date: Date | null): string {
    if (!date) return '';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  // Método para probar la exportación con diferentes criterios de filtrado
  testExportWithFilter(filterType: string): void {
    // Guardar el estado actual
    const currentSearchTerm = this.searchTerm;
    const currentDateFilterType = this.dateFilterType;

    // Aplicar el filtro de prueba
    switch (filterType) {
      case 'repuesto':
        // Filtrar por un nombre de repuesto común
        this.searchTerm = 'filtro';
        break;
      case 'estado':
        // Filtrar por un estado de repuesto
        this.searchTerm = 'creado';
        break;
      case 'codigo':
        // Filtrar por un código de repuesto
        this.searchTerm = 'ABC123';
        break;
      case 'fecha':
        // Filtrar por fecha (últimos 7 días)
        this.dateFilterType = 'last7days';
        this.onDateFilterChange();
        break;
      default:
        // No aplicar filtro
        break;
    }

    // Cargar la primera página con el filtro aplicado
    this.loadPage(1);

    // Mostrar mensaje informativo
    this.showSuccessMessage(`Filtro de prueba aplicado: ${filterType}. Exporta a Excel para ver los resultados.`);

    // Restaurar el estado original después de un tiempo
    setTimeout(() => {
      this.searchTerm = currentSearchTerm;
      this.dateFilterType = currentDateFilterType;
      this.loadPage(1);
      this.showSuccessMessage('Filtro restaurado al estado original.');
    }, 10000); // 10 segundos para exportar
  }

  // Método para actualizar el total de repuestos desde el backend
  updateTotalSpareParts(): void {
    this.sparePartService.getTotalSparePartsCount().subscribe(
      count => {
        this.totalSpareParts = count;
      },
      error => {
        console.error('Error al obtener el total de repuestos:', error);
        // Si falla, usar el conteo local como respaldo
        this.totalSpareParts = this.countTotalSpareParts();
      }
    );
  }

  // Métodos para manejar los íconos de estado
  getStatusIcon(status: string): string {
    switch (status.toUpperCase()) {
      case 'CREADO':
        return '🔵';
      case 'EN PROCESO':
      case 'LLEGO':
        return '🟠';
      case 'COMPLETADO':
      case 'FINALIZADO':
        return '🟢';
      case 'CANCELADO':
      case 'ATENCION':
        return '🔴';
      case 'PENDIENTE':
      case 'REPUESTOS-PENDIENTES':
        return '🟣';
      default:
        return '⚪';
    }
  }

  // Método para obtener la clase CSS según el estado
  getStatusClass(status: string): string {
    switch (status.toUpperCase()) {
      case 'CREADO':
        return 'bg-blue-100 text-blue-800 px-2 py-1 rounded';
      case 'EN PROCESO':
      case 'LLEGO':
        return 'bg-orange-100 text-orange-800 px-2 py-1 rounded';
      case 'COMPLETADO':
      case 'FINALIZADO':
        return 'bg-green-100 text-green-800 px-2 py-1 rounded';
      case 'CANCELADO':
      case 'ATENCION':
        return 'bg-red-100 text-red-800 px-2 py-1 rounded';
      case 'PENDIENTE':
      case 'REPUESTOS-PENDIENTES':
        return 'bg-purple-100 text-purple-800 px-2 py-1 rounded';
      default:
        return 'bg-gray-100 text-gray-800 px-2 py-1 rounded';
    }
  }

  // Método para obtener la clase CSS según el estado del producto
  getProductStatusClass(status: string): string {
    if (!status) return '';

    switch (status.toUpperCase()) {
      case 'ENTREGADO':
        return 'bg-green-100 text-green-800 px-2 py-1 rounded';
      case 'EN CAMINO':
        return 'bg-blue-100 text-blue-800 px-2 py-1 rounded';
      case 'PENDIENTE':
        return 'bg-purple-100 text-purple-800 px-2 py-1 rounded';
      case 'RETRASADO':
        return 'bg-red-100 text-red-800 px-2 py-1 rounded';
      case 'CANCELADO':
        return 'bg-gray-100 text-gray-800 px-2 py-1 rounded';
      default:
        return '';
    }
  }

  // Normaliza el estado a mostrar según shipping/product status
  computeDisplayProductStatus(part: any): string {
    const shipping = (part?.shippingStatus || '').toUpperCase();
    if (shipping === 'ENVIADO' || shipping === 'EXPORTADO') {
      return 'EXPORTADO';
    }
    if (shipping === 'EXPORTADO-NO-CARGADO') {
      return 'EXPORTADO-NO-CARGADO';
    }
    return part?.productStatus || 'CREADO';
  }

  private isTruthyFlag(value: any): boolean {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value === 1;
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      return normalized === 'true' || normalized === '1' || normalized === 'yes' || normalized === 'si';
    }
    return false;
  }

  private normalizeSparePartFlags<T extends Record<string, any>>(part: T): T {
    const target = part as any;
    target.isFant = this.isTruthyFlag(target.isFant ?? target.IsFant);
    target.fantUrgency = target.isFant
      ? normalizeFantUrgency(target.fantUrgency ?? target.FantUrgency)
      : null;
    target.recompra = this.isTruthyFlag(target.recompra ?? target.Recompra);
    target.control = this.isTruthyFlag(target.control ?? target.Control);
    target.anulado = this.isTruthyFlag(target.anulado ?? target.Anulado);
    target.trello = this.isTruthyFlag(target.trello ?? target.Trello);
    target.devZureo = this.isTruthyFlag(target.devZureo ?? target.DevZureo);
    target.entradaSage = this.isTruthyFlag(target.entradaSage ?? target.EntradaSage);
    target.controleSeba = this.isTruthyFlag(target.controleSeba ?? target.ControleSeba);
    target.okAnulacao = this.isTruthyFlag(target.okAnulacao ?? target.OkAnulacao);
    target.facturado = this.isTruthyFlag(target.facturado ?? target.Facturado);
    target.needReview = this.isTruthyFlag(target.needReview ?? target.NeedReview);
    target.isDamaged = normalizeIsDamaged(target.isDamaged ?? target.IsDamaged);
    delete target.IsFant;
    delete target.FantUrgency;
    delete target.Recompra;
    delete target.Control;
    delete target.Anulado;
    delete target.Trello;
    delete target.DevZureo;
    delete target.EntradaSage;
    delete target.ControleSeba;
    delete target.OkAnulacao;
    delete target.Facturado;
    delete target.NeedReview;
    delete target.IsDamaged;
    return part;
  }

  private updateSparePartInLocalState(updatedPart: SparePartModel, orderId?: number | null): void {
    const targetOrderId = orderId ?? updatedPart.orderId;
    const updateOrder = (order: OrderOutModel): OrderOutModel => {
      if (targetOrderId != null && Number(order.orderId) !== Number(targetOrderId)) {
        return order;
      }
      const spareParts = order.spareParts || [];
      if (!spareParts.some(part => Number(part.sparePartId) === Number(updatedPart.sparePartId))) {
        return order;
      }
      return {
        ...order,
        spareParts: spareParts.map(part =>
          Number(part.sparePartId) === Number(updatedPart.sparePartId)
            ? this.normalizeSparePartFlags({ ...part, ...updatedPart } as any)
            : part
        )
      };
    };

    this.orders = this.orders.map(updateOrder);
    this.filteredOrdersList = this.filteredOrdersList.map(updateOrder);
    this.sortedAndFilteredOrders = this.sortedAndFilteredOrders.map(updateOrder);
    this.allFilteredOrders = this.allFilteredOrders.map(updateOrder);
    this.allOrdersBaseCache = this.allOrdersBaseCache ? this.allOrdersBaseCache.map(updateOrder) : this.allOrdersBaseCache;
    this.syncOrdersTableAllSelectedHeader();

    if (this.selectedOrder) {
      this.selectedOrder = updateOrder(this.selectedOrder);
    }
    if (this.selectedSparePart && Number(this.selectedSparePart.sparePartId) === Number(updatedPart.sparePartId)) {
      this.selectedSparePart = this.normalizeSparePartFlags({ ...this.selectedSparePart, ...updatedPart } as any);
    }

    this.cdr.detectChanges();
  }

  private isActiveSparePartModalLoad(token: number, sparePartId: number): boolean {
    return token === this.sparePartModalLoadToken &&
      !!this.selectedSparePart &&
      Number(this.selectedSparePart.sparePartId) === Number(sparePartId);
  }

  isPartFant(part: any): boolean {
    return this.isTruthyFlag(part?.isFant ?? part?.IsFant);
  }

  getFantUrgency(part: any): FantUrgency | null {
    return normalizeFantUrgency(part?.fantUrgency ?? part?.FantUrgency);
  }

  onFantCheckboxChanged(isFant: boolean): void {
    if (!this.selectedSparePart) return;
    this.selectedSparePart.fantUrgency = defaultFantUrgency(isFant, null);
  }

  setFantUrgency(urgency: FantUrgency): void {
    if (!this.selectedSparePart?.isFant) return;
    const current = normalizeFantUrgency(this.selectedSparePart.fantUrgency);
    this.selectedSparePart.fantUrgency = current === urgency ? null : urgency;
  }

  getOrderDetailSparePartRowClass(part: any): string {
    const isAnulado = this.isTruthyFlag(part?.anulado ?? part?.Anulado);
    const isFant = this.isTruthyFlag(part?.isFant ?? part?.IsFant);
    const classes: string[] = [];
    if (isAnulado) {
      classes.push('anulado-row');
    } else if (isFant) {
      classes.push('fant-row');
    }
    const severity = this.getSparePartDelaySeverity(part as SparePartModel);
    if (severity === 'yellow') classes.push('delay-row-yellow');
    if (severity === 'orange') classes.push('delay-row-orange');
    if (severity === 'red') classes.push('delay-row-red');
    return classes.join(' ');
  }

  // Método para calcular el costo total de una orden
  getTotalCost(order: OrderOutModel): number {
    if (!order || !order.spareParts) return 0;

    return order.spareParts.reduce((total, part) => {
      return total + (part.amount * part.unitPrice);
    }, 0);
  }

  // Método para calcular el total de la orden seleccionada
  getOrderTotal(order: OrderOutModel): number {
    if (!order || !order.spareParts) return 0;

    return order.spareParts.reduce((total, part) => {
      return total + (part.amount * part.unitPrice);
    }, 0);
  }

  applySearch(): void {
    // Emitir el evento de búsqueda solo si no estamos en medio de una carga
    if (!this.loading) {
      this.searchFilterChanged.emit(this.searchTerm);

      // Reiniciar a la primera página cuando se aplica un nuevo filtro
      this.currentPage = 1;
    }

    // Guardar filtros en localStorage
    this.saveFiltersToLocalStorage();

    // Forzar la actualización de la interfaz de usuario
    setTimeout(() => {
      this.debugPagination('después de applySearch');
    }, 0);
  }

  // Método para depurar la paginación
  debugPagination(context = 'actual'): void {
    // NO llamar a getPageNumbers() aquí para evitar loops infinitos
  }

  // Método para verificar la consistencia de la paginación
  verifyPaginationConsistency(context = ''): void {
    // Calcular el número esperado de páginas
    const expectedPages = Math.max(1, Math.ceil(this.filteredTotal / this.pageSize));

    // Verificar si el número de páginas es consistente
    if (this.totalPages !== expectedPages) {
      console.error(`¡INCONSISTENCIA DETECTADA! Total de páginas (${this.totalPages}) no coincide con el cálculo esperado (${expectedPages})`);
      console.error(`filteredTotal: ${this.filteredTotal}, pageSize: ${this.pageSize}`);

      // Corregir la inconsistencia
      this.totalPages = expectedPages;

      // Si la página actual es mayor que el total de páginas, ajustarla
      if (this.currentPage > this.totalPages) {
        this.currentPage = this.totalPages;
      }

    } else {
    }
  }

  private saveFiltersToLocalStorage(): void {
    if (this.dataDrivenByParent) return;

    try {
      const filters = {
        searchTerm: this.searchTerm,
        dateFilterType: this.dateFilterType,
        customStartDate: this.customStartDate,
        customEndDate: this.customEndDate,
        startDate: this.startDate ? this.startDate.toISOString() : null,
        endDate: this.endDate ? this.endDate.toISOString() : null,
        currentPage: this.currentPage,
        selectedWorkshopId: this.selectedWorkshopId
      };

      localStorage.setItem('orderFilters', JSON.stringify(filters));
    } catch (error) {
      console.error('Error al guardar filtros en localStorage:', error);
    }
  }

  private loadFiltersFromLocalStorage(): void {
    try {
      const filtersJson = localStorage.getItem('orderFilters');
      if (filtersJson) {
        const filters = JSON.parse(filtersJson);

        // Cargar término de búsqueda
        if (filters.searchTerm !== undefined) {
          this.searchTerm = filters.searchTerm;
        }

        // Cargar tipo de filtro de fecha
        if (filters.dateFilterType) {
          this.dateFilterType = filters.dateFilterType;
        }

        // Cargar fechas personalizadas
        if (filters.customStartDate) {
          this.customStartDate = filters.customStartDate;
        }

        if (filters.customEndDate) {
          this.customEndDate = filters.customEndDate;
        }

        // Cargar fechas de inicio y fin
        if (filters.startDate) {
          this.startDate = new Date(filters.startDate);
        }

        if (filters.endDate) {
          this.endDate = new Date(filters.endDate);
        }

        // Cargar página actual
        if (filters.currentPage) {
          this.currentPage = filters.currentPage;
        }

        // Cargar filtro de taller
        if (filters.selectedWorkshopId !== undefined) {
          this.selectedWorkshopId = filters.selectedWorkshopId;
        }
      }
    } catch (error) {
      console.error('Error al cargar filtros desde localStorage:', error);
    }
  }

  // Método para convertir entre string y boolean
  convertStringToBoolean(value: string | boolean): string | boolean {
    // Si ya es una cadena, devolver el valor original
    if (typeof value === 'string') {
      return value;
    }

    // Si es un boolean, convertirlo a string según corresponda
    if (value === true) {
      return this.Yes; // Para sendYesOrNo
    } else {
      return this.No;
    }
  }

  convertBooleanToString(value: boolean | string, isExportField = false): string {
    if (typeof value === 'string') {
      if (isExportField) {
        return value === this.si ? this.si : this.no;
      }
      return value === this.Yes ? this.Yes : this.No;
    }

    if (isExportField) {
      return value ? this.si : this.no;
    }
    return value ? this.Yes : this.No;
  }

  // Método para mostrar mensaje informativo
  showInfoMessage(message: string): void {
    const infoMessage = document.createElement('div');
    infoMessage.className = 'fixed top-4 right-4 bg-blue-500 text-white px-6 py-3 rounded shadow-lg z-50 animate-fade-in-out';
    infoMessage.textContent = message;
    document.body.appendChild(infoMessage);
    setTimeout(() => infoMessage.remove(), 3000);
  }

  // Método para forzar la recarga completa de datos desde la base de datos
  forceReload(): void {
    if (this.loading) {
      this.showInfoMessage('Ya hay una carga en progreso. Por favor espere...');
      return;
    }

    // Limpiar filtros
    this.searchTerm = '';
    this.startDate = undefined;
    this.endDate = undefined;

    // Mostrar indicador de carga
    this.loading = true;
    this.loadingMessage = 'Recargando datos';
    this.loadingDetail = 'Obteniendo información actualizada...';
    this.startLoadingAnimation();

    // Primero, obtener el total de órdenes en la base de datos
    this.orderService.getTotalOrdersCount().subscribe({
      next: (count) => {
        this.totalOrdersInDB = count;
        this.filteredTotal = count; // Reiniciar el total filtrado

        // Calcular el número total de páginas
        this.calculateTotalPages();

        // Cargar solo la primera página de órdenes (no todas)
        this.currentPage = 1;

        // Cargar la primera página
        this.orderService.getPaginatedOrders(
          1,
          this.pageSize,
          this.searchTerm,
          this.startDate,
          this.endDate,
          undefined,
          this.selectedWorkshopId ?? undefined,
          this.onlyLast60Days ? 60 : undefined,
          this.sortedBy || undefined,
          this.sortOrder,
          this.getServerOrderColumnFilters()
        ).subscribe({
          next: (response) => {
            if (response && response.orders) {
              this.orders = response.orders;
              this.filteredOrdersList = [...response.orders];
              this.filteredTotal = response.totalCount;
              this.calculateTotalPages();
              this.syncOrdersTableAllSelectedHeader();

              // Actualizar contadores de repuestos
              this.updateTotalCounts();

              // Mostrar mensaje de éxito
              this.showSuccessMessage('Datos recargados correctamente');
            } else {
              this.showInfoMessage('No se encontraron órdenes');
            }

            // Detener animación de carga
            this.stopLoadingAnimation();
            this.loading = false;
          },
          error: (error: any) => {
            console.error('Error al cargar la primera página de órdenes:', error);
            this.showErrorMessage('Error al cargar los datos. Intente nuevamente.');
            this.stopLoadingAnimation();
            this.loading = false;
          }
        });
      },
      error: (error: any) => {
        console.error('Error al obtener el total de órdenes:', error);
        this.stopLoadingAnimation();
        this.loading = false;
        this.showErrorMessage('Error al obtener el total de órdenes. Intente nuevamente.');
      }
    });
  }

  /**
   * Normaliza un valor numérico, preservando el punto como separador decimal
   * @param value Cualquier valor que pueda representar un número
   * @returns Número normalizado con máximo 2 decimales
   */
  normalizeNumericValue(value: any): number {
    // Si es null, undefined o vacío, devolver 0
    if (value === null || value === undefined || value === '') {
      return 0;
    }

    // Si ya es un número, devolvemos solo si es finito
    if (typeof value === 'number') {
      return Number.isFinite(value) ? parseFloat(value.toFixed(2)) : 0;
    }

    // Convertir a string si no lo es
    let stringValue = value.toString();

    // Tratamos el formato específico dependiendo de si es formato europeo (1.234,56) o estadounidense (1,234.56)
    // Verificamos si hay un punto que parece ser un separador decimal (si es el último punto)
    const lastDotIndex = stringValue.lastIndexOf('.');
    const lastCommaIndex = stringValue.lastIndexOf(',');

    // Si hay un punto y está después de la última coma, o no hay comas,
    // asumimos que el punto es el separador decimal
    if (lastDotIndex > -1 && (lastDotIndex > lastCommaIndex || lastCommaIndex === -1)) {
      // Formato con punto decimal (1,234.56 o 1234.56)
      // Eliminamos las comas (que serían separadores de miles)
      stringValue = stringValue.replace(/,/g, '');
    } else if (lastCommaIndex > -1) {
      // Formato con coma decimal (1.234,56 o 1234,56)
      // Primero eliminamos los puntos (que serían separadores de miles)
      stringValue = stringValue.replace(/\./g, '');
      // Luego reemplazamos la coma por punto para el formato estándar
      stringValue = stringValue.replace(/,/g, '.');
    }

    // Asegurar que solo hay un punto decimal
    const parts = stringValue.split('.');
    if (parts.length > 2) {
      // Si hay más de un punto, conservamos solo el primero
      stringValue = parts[0] + '.' + parts.slice(1).join('');
    }

    // Asegurar exactamente 2 decimales
    if (parts.length === 2) {
      const integerPart = parts[0];
      let decimalPart = parts[1];

      // Si hay más de 2 decimales, truncamos a 2
      if (decimalPart.length > 2) {
        decimalPart = decimalPart.substring(0, 2);
      }

      // Si hay menos de 2 decimales, completamos con ceros
      if (decimalPart.length < 2) {
        decimalPart = decimalPart.padEnd(2, '0');
      }

      stringValue = integerPart + '.' + decimalPart;
    } else if (!stringValue.includes('.')) {
      // Si no hay punto decimal, agregamos .00
      stringValue = stringValue + '.00';
    }

    // Convertir a número y verificar que sea válido
    const numValue = parseFloat(stringValue);
    if (isNaN(numValue)) {
      return 0;
    }

    // Asegurar que el valor sea un número con 2 decimales exactos
    const rounded = parseFloat(numValue.toFixed(2));
    return Number.isFinite(rounded) ? rounded : 0;
  }

  private prepareSparePartForOrderCreate(part: SparePartModel): any {
    const normalizedPart = {
      ...part,
      sparePartId: 0,
      orderId: 0,
      emergentComment: '',
      unitPrice: parseFloat(this.normalizeNumericValue(part.unitPrice).toFixed(2)),
      valorDeVenta: parseFloat(this.normalizeNumericValue(part.valorDeVenta).toFixed(2)),
      netWheight: parseFloat(this.normalizeNumericValue(part.netWheight).toFixed(2)),
      grossWheight: parseFloat(this.normalizeNumericValue(part.grossWheight).toFixed(2)),
      amount: Math.max(1, Math.round(this.normalizeNumericValue(part.amount))),
      fechaDeComunicado: part.fechaDeComunicado ?? null,
      fechaExportacion: part.fechaExportacion ?? null
    };

    return stripPascalCaseShadowKeys(normalizedPart);
  }

  private normalizeNullableInt(value: any): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const parsed = Number(value);
    if (!Number.isInteger(parsed) || parsed <= 0) {
      return null;
    }

    return parsed;
  }

  private normalizeNullableText(value: any): string | null {
    if (value === null || value === undefined) {
      return null;
    }

    const text = String(value).trim();
    return text.length > 0 ? text : null;
  }

  /**
   * Maneja la entrada de valores numéricos en campos de texto
   * @param event - Evento de entrada
   */
  handleNumericInput(event: any): void {
    const inputElement = event.target as HTMLInputElement;
    if (!inputElement || !inputElement.value) return;

    // Preservar posición del cursor
    const cursorPosition = inputElement.selectionStart;

    // Valor inicial para comparación
    const initialValue = inputElement.value;

    // Permitir solo dígitos, comas, puntos y el signo negativo al principio
    let value = initialValue.replace(/[^0-9,.\\-]/g, '');

    // Verificar que el signo negativo solo esté al inicio
    if (value.indexOf('-') > 0) {
      value = value.replace(/-/g, '');
    }

    // Determinar el formato (europeo o americano) por la posición del último punto y coma
    const lastDotIndex = value.lastIndexOf('.');
    const lastCommaIndex = value.lastIndexOf(',');

    // Si hay un punto y está después de la última coma, o no hay comas,
    // asumimos formato con punto decimal (1,234.56)
    if (lastDotIndex > -1 && (lastDotIndex > lastCommaIndex || lastCommaIndex === -1)) {
      // Asegurarnos de que solo hay un punto decimal
      const parts = value.split('.');
      if (parts.length > 2) {
        // Si hay más de un punto, conservamos solo el último
        value = parts[0] + '.' + parts.slice(1).join('');
      }

      // Limitar a 2 decimales
      if (parts.length > 1 && parts[1].length > 2) {
        parts[1] = parts[1].substring(0, 2);
        value = parts[0] + '.' + parts[1];
      }
    }
    // Si hay una coma y está después del último punto, o no hay puntos,
    // asumimos formato con coma decimal (1.234,56)
    else if (lastCommaIndex > -1 && (lastCommaIndex > lastDotIndex || lastDotIndex === -1)) {
      // Asegurarnos de que solo hay una coma decimal
      const parts = value.split(',');
      if (parts.length > 2) {
        // Si hay más de una coma, conservamos solo la última
        value = parts[0] + ',' + parts.slice(1).join('');
      }

      // Limitar a 2 decimales
      if (parts.length > 1 && parts[1].length > 2) {
        parts[1] = parts[1].substring(0, 2);
        value = parts[0] + ',' + parts[1];
      }
    }

    // Si el valor ha cambiado, actualizar el campo
    if (value !== initialValue) {
      const oldLength = initialValue.length;
      inputElement.value = value;

      // Restaurar posición del cursor, ajustando si se modificó el valor
      if (cursorPosition !== null) {
        const newPosition = Math.min(cursorPosition + (value.length - oldLength), value.length);
        inputElement.setSelectionRange(newPosition, newPosition);
      }
    }

    // Actualizamos directamente la propiedad correspondiente en el modelo
    const propertyName = inputElement.getAttribute('name');
    if (propertyName && this.selectedSparePart && propertyName in this.selectedSparePart) {
      // Convertimos a número pero conservamos el formato con punto decimal
      if (value === '' || value === '.' || value === ',') {
        this.selectedSparePart[propertyName] = 0;
      } else {
        // Normalizar según el formato detectado
        let normalizedValue = value;
        if (value.includes(',')) {
          // Formato europeo: primero quitamos puntos y luego reemplazamos coma por punto
          normalizedValue = value.replace(/\./g, '').replace(',', '.');
        } else if (value.includes('.')) {
          // Formato americano: ya tiene punto como decimal, solo quitamos comas si hay
          normalizedValue = value.replace(/,/g, '');
        }

        // Convertir a número preservando el punto decimal
        const numericValue = parseFloat(normalizedValue);

        // Verificar que sea un número válido
        if (!isNaN(numericValue)) {
          this.selectedSparePart[propertyName] = numericValue;
        } else {
        }
      }

      // Si es el campo amount, aseguramos que nunca sea menor que 1
      if (propertyName === 'amount' && this.selectedSparePart.amount < 1) {
        this.selectedSparePart.amount = 1;
        inputElement.value = '1';
      }
    }
  }

  private getActiveOrdersForKpis(): OrderOutModel[] {
    return this.sortedAndFilteredOrders.length > 0
      ? this.sortedAndFilteredOrders
      : this.filteredOrdersList;
  }

  private calculateKpisFromOrders(orders: OrderOutModel[]): void {
    this.kpiTotalOrdersCount = orders.length;

    this.kpiTotalSparePartsCount = orders.reduce((total: number, order) => {
      return total + (order.spareParts?.length || 0);
    }, 0);

    this.kpiTotalOrdersCost = orders.reduce((total, order) => {
      return total + this.getTotalCost(order);
    }, 0);

    this.kpiTotalOrdersValue = orders.reduce((total, order) => {
      let orderTotal = 0;
      for (const part of order.spareParts || []) {
        orderTotal += (part.valorDeVenta || 0) * (part.amount || 0);
      }
      return total + orderTotal;
    }, 0);
  }

  private refreshKpis(sourceOrders?: OrderOutModel[]): void {
    if (sourceOrders) {
      this.calculateKpisFromOrders(sourceOrders);
      return;
    }

    if (this.sortedAndFilteredOrders.length > 0) {
      this.calculateKpisFromOrders(this.sortedAndFilteredOrders);
      return;
    }

    if (this.dataDrivenByParent) {
      const requestId = ++this.kpiRefreshRequestId;
      this.fetchAllOrdersWithCurrentFilters().subscribe({
        next: (allOrders) => {
          if (requestId !== this.kpiRefreshRequestId) return;
          const filtered = this.hasActiveOrderColumnFilters()
            ? allOrders.filter(o => this.passesOrderColumnFilters(o))
            : allOrders;
          this.calculateKpisFromOrders(filtered);
        },
        error: () => {
          if (requestId !== this.kpiRefreshRequestId) return;
          this.calculateKpisFromOrders(this.filteredOrdersList);
        }
      });
      return;
    }

    this.calculateKpisFromOrders(this.filteredOrdersList);
  }

  getTotalOrdersCount(): number {
    return this.kpiTotalOrdersCount;
  }

  // Métodos para los resúmenes generales de todas las órdenes filtradas
  getTotalOrdersCost(): number {
    return this.kpiTotalOrdersCost;
  }

  getTotalOrdersValue(): number {
    return this.kpiTotalOrdersValue;
  }

  getTotalSparePartsCount(): number {
    return this.kpiTotalSparePartsCount;
  }

  // Métodos para los resúmenes de orden individual
  getOrderCost(order: OrderOutModel): number {
    if (!order || !order.spareParts) return 0;

    return order.spareParts.reduce((total, part) => {
      return total + (part.amount * part.unitPrice);
    }, 0);
  }

  getOrderSaleValue(order: OrderOutModel): number {
    if (!order || !order.spareParts) return 0;

    return order.spareParts.reduce((total, part) => {
      return total + (part.amount * (part.valorDeVenta || 0));
    }, 0);
  }

  // Métodos para los resúmenes de todos los repuestos
  getTotalSparePartsCost(): number {
    return this.kpiTotalOrdersCost;
  }

  getTotalSparePartsValue(): number {
    return this.kpiTotalOrdersValue;
  }

  /** Repuestos marcados como anulados en la vista agregada de repuestos (órdenes filtradas). */
  getAnnulledSparePartsInFilteredViewCount(): number {
    return this.getAllSpareParts().filter(p => !!(p.anulado ?? (p as any).Anulado)).length;
  }

  // Método para obtener el nombre del cliente a partir del número de orden
  getOrderClientName(orderNumber: string): string {
    const order = this.getActiveOrdersForKpis().find(o => o.orderNumber === orderNumber);
    return order ? order.clientName : '';
  }

  printLabels(order: OrderOutModel): void {
    // Verificar si los repuestos tienen números de exportación
    const sparePartsWithoutExportNumber = (order.spareParts || []).filter(sp => !sp.exportNumber || sp.exportNumber.trim() === '');

    if (sparePartsWithoutExportNumber.length > 0) {
      this.showErrorMessage(`Hay ${sparePartsWithoutExportNumber.length} repuesto(s) sin número de exportación. Por favor, asigne números de exportación a todos los repuestos antes de generar etiquetas.`);
      return;
    }

    // Mostrar modal de selección de repuestos
    this.showSparePartSelectionModal(order);
  }

  generateLabelsAndPackage(order: OrderOutModel, printLabels: boolean = true): void {
    if (!order.spareParts || order.spareParts.length === 0) {
      this.showErrorMessage('La orden no tiene repuestos para empaquetar.');
      return;
    }

    if (!printLabels && !this.canGeneratePackages(order)) {
      this.showErrorMessage('Todos los repuestos de esta orden ya están empaquetados.');
      return;
    }

    this.showSparePartSelectionModal(order, printLabels);
  }

  canGeneratePackages(order: OrderOutModel | null): boolean {
    return canGeneratePackagesForOrder(order?.spareParts);
  }

  /**
   * Recarga los datos de una orden específica
   */
  private refreshOrderData(orderId: number): Promise<void> {
    return new Promise((resolve, reject) => {
      // Recargar la orden completa desde el backend
      this.orderService.getOrderById(orderId).subscribe({
        next: (updatedOrder) => {
          // Actualizar la orden en la lista principal
          const mainIndex = this.orders.findIndex(o => o.orderId === orderId);
          if (mainIndex !== -1) {
            this.orders[mainIndex] = updatedOrder;
          }

          // Actualizar la orden en la lista filtrada
          const filteredIndex = this.filteredOrdersList.findIndex(o => o.orderId === orderId);
          if (filteredIndex !== -1) {
            const prevSelected = this.filteredOrdersList[filteredIndex].selected;
            this.filteredOrdersList[filteredIndex] = { ...updatedOrder, selected: prevSelected };
          }

          // Si la orden está seleccionada, actualizarla también
          if (this.selectedOrder && this.selectedOrder.orderId === orderId) {
            this.selectedOrder = updatedOrder;
          }
          resolve();
        },
        error: (error) => {
          console.error('❌ Error al refrescar datos de la orden:', error);
          // Fallback: intentar actualizar desde la lista local si falla el backend
          const order = this.filteredOrdersList.find(o => o.orderId === orderId);
          if (order) {
            const index = this.filteredOrdersList.findIndex(o => o.orderId === orderId);
            if (index !== -1) {
              this.filteredOrdersList[index] = order;
            }

            if (this.selectedOrder && this.selectedOrder.orderId === orderId) {
              this.selectedOrder = order;
            }
          }
          // Resolver de todas formas para no bloquear la UI
          resolve();
        }
      });
    });
  }

  showSparePartSelectionModal(order: OrderOutModel, printLabels: boolean = true): void {
    // CRÍTICO: NO refrescar los datos desde el backend aquí porque sobrescribe
    // los cambios locales que el usuario hizo en los repuestos
    // En su lugar, usar los datos locales actuales

    // Verificar si ya existen paquetes para esta orden
    const existingPackages = this.getExistingPackagesForOrder(order);
    const hasExistingPackages = existingPackages.length > 0;

    // Continuar con la lógica del modal usando la orden local
    this.showSparePartSelectionModalWithData(order, existingPackages, hasExistingPackages, printLabels);
  }

  private showSparePartSelectionModalWithData(order: OrderOutModel, existingPackages: any[], hasExistingPackages: boolean, printLabels: boolean = true): void {
    const unpackagedParts = getUnpackagedSpareParts(order.spareParts || []);
    const packagedParts = getPackagedSpareParts(order.spareParts || []);
    const canSavePackages = unpackagedParts.length > 0;
    const componentRef = this;

    const renderUnpackagedRow = (part: SparePartModel): string => {
      const partPackageNumber = (part.packageNumber || '').trim();
      const existingPackage = partPackageNumber
        ? existingPackages.find(pkg => pkg.packageNumber === partPackageNumber)
        : undefined;
      const isAlreadyAssigned = existingPackage !== undefined;

      return `
        <div class="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 ${isAlreadyAssigned ? 'bg-yellow-50 border-yellow-200' : ''}" data-spare-part-id="${part.sparePartId}">
          <div class="flex items-center space-x-3 flex-1">
            <input type="checkbox"
                   class="label-checkbox cursor-pointer"
                   data-spare-part-id="${part.sparePartId}"
                   checked
                   style="width: 18px; height: 18px;">
            <div class="flex-1">
              <div class="font-medium">${part.sparePartNameEs || part.sparePartName}</div>
              <div class="text-sm text-gray-500">Código: ${part.productCode} | Cantidad: ${part.amount} | Estado: ${part.productStatus}</div>
              <div class="text-xs text-blue-600 font-medium mt-1">
                <i class="fas fa-shipping-fast mr-1"></i>N° Exportación: E${part.exportNumber || 'Sin asignar'}
              </div>
              ${isAlreadyAssigned ? `<div class="text-xs text-yellow-700 font-medium">Asignado a ${existingPackage.packageNumber} (pendiente de escaneo)</div>` : ''}
            </div>
          </div>
          <div class="flex items-center space-x-3">
            <select class="package-select border rounded px-3 py-2 text-sm min-w-[150px]" data-spare-part-id="${part.sparePartId}">
              <option value="">Sin asignar</option>
              ${existingPackages
                .filter(pkg => {
                  const packageExportNumber = pkg.spareParts.length > 0 ?
                    (order.spareParts || []).find(sp => sp.sparePartId === pkg.spareParts[0].sparePartId)?.exportNumber : null;
                  return packageExportNumber === part.exportNumber;
                })
                .map(pkg => `<option value="${pkg.packageNumber}" ${isAlreadyAssigned && existingPackage.packageNumber === pkg.packageNumber ? 'selected' : ''}>${pkg.packageNumber}</option>`)
                .join('')}
              ${part.exportNumber ? `<option value="__create_new__" class="bg-green-100 text-green-800 font-medium">➕ Nuevo Paquete (E${part.exportNumber})</option>` : ''}
            </select>
          </div>
        </div>
      `;
    };

    const renderPackagedRow = (part: SparePartModel): string => {
      const packageNumber = (part.packageNumber || '').trim() || 'Sin paquete';
      return `
        <div class="flex items-center justify-between p-4 border rounded-lg bg-gray-100 border-gray-300" data-spare-part-id="${part.sparePartId}" data-packaged="true">
          <div class="flex items-center space-x-3 flex-1">
            <input type="checkbox"
                   class="label-checkbox cursor-pointer"
                   data-spare-part-id="${part.sparePartId}"
                   checked
                   style="width: 18px; height: 18px;">
            <div class="flex-1">
              <div class="font-medium text-gray-800">${part.sparePartNameEs || part.sparePartName}</div>
              <div class="text-sm text-gray-600">Código: ${part.productCode} | Cantidad: ${part.amount} | Estado: ${part.productStatus}</div>
              <div class="text-xs text-green-700 font-medium mt-1">
                <i class="fas fa-check-circle mr-1"></i>Empaquetado — Paquete ${packageNumber}
              </div>
              <div class="text-xs text-gray-500 mt-1">Solo informativo / reimpresión de etiquetas</div>
            </div>
          </div>
        </div>
      `;
    };

    // Crear un modal personalizado para seleccionar repuestos y asignar paquetes
    const modalHtml = `
      <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" id="sparePartSelectionModal" onclick="closeSparePartSelectionModal()">
        <div class="bg-white rounded-lg shadow-lg w-11/12 max-w-7xl max-h-[90vh] overflow-y-auto" onclick="event.stopPropagation()">
          <div class="p-4 border-b flex justify-between items-center">
            <h2 class="text-xl font-bold">Organizar Repuestos en Paquetes - Orden ${order.orderNumber}</h2>
            <button onclick="closeSparePartSelectionModal()" class="text-gray-500 hover:text-gray-700">
              <i class="fas fa-times"></i>
            </button>
          </div>
          
          <div class="p-4">
            <div class="mb-4 bg-blue-50 p-3 rounded-lg">
              <p class="text-sm text-blue-800">
                <i class="fas fa-info-circle mr-2"></i>
                <strong>Instrucciones:</strong> Asigne paquetes solo a repuestos pendientes de empaquetar.
                Los repuestos ya empaquetados se muestran abajo solo como referencia y para reimprimir etiquetas.
              </p>
            </div>
            ${!canSavePackages ? `
            <div class="mb-4 bg-green-50 p-3 rounded-lg border border-green-200">
              <p class="text-sm text-green-800">
                <i class="fas fa-check-circle mr-2"></i>
                Todos los repuestos de esta orden ya están empaquetados. Puede reimprimir etiquetas seleccionando repuestos y usando "Guardar e Imprimir Etiquetas".
              </p>
            </div>` : ''}
            
            <!-- Resumen de números de exportación -->
            <div class="mb-4 bg-gray-50 p-3 rounded-lg">
              <h4 class="font-semibold text-gray-700 mb-2">
                <i class="fas fa-list-alt mr-2"></i>Números de Exportación en esta Orden
              </h4>
              <div class="flex flex-wrap gap-2">
                ${[...new Set((order.spareParts || []).filter(sp => sp.exportNumber && sp.exportNumber.trim() !== '').map(sp => sp.exportNumber))].map(exportNum => {
      const count = (order.spareParts || []).filter(sp => sp.exportNumber === exportNum).length;
      return `<span class="px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm font-medium">E${exportNum} (${count} repuesto${count !== 1 ? 's' : ''})</span>`;
    }).join('')}
                ${[...new Set((order.spareParts || []).filter(sp => sp.exportNumber && sp.exportNumber.trim() !== '').map(sp => sp.exportNumber))].length === 0 ?
        '<span class="px-2 py-1 bg-red-100 text-red-800 rounded text-sm font-medium">Sin números de exportación</span>' : ''}
              </div>
            </div>
            
            <!-- Controles de paquetes -->
            <div class="mb-6">
              ${canSavePackages ? `
              <div class="flex space-x-2 mb-4">
                <button onclick="addNewPackage()" class="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600">
                  <i class="fas fa-plus mr-2"></i>Nuevo Paquete
                </button>
                <button onclick="clearAllAssignments()" class="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600">
                  <i class="fas fa-eraser mr-2"></i>Limpiar Asignaciones
                </button>
              </div>
              ` : ''}
              
              <!-- Vista previa de paquetes creados -->
              <div id="packagesPreviewContainer" class="mb-4">
                <h3 class="font-semibold mb-3">Paquetes Creados</h3>
                <div id="packagesPreview" class="space-y-2">
                  <div class="p-3 bg-gray-50 rounded text-center text-gray-500">
                    No hay paquetes creados. Haga clic en "Nuevo Paquete" para crear uno.
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Lista de repuestos con asignación individual -->
            <div class="mb-6">
              ${canSavePackages ? `
              <h3 class="font-semibold mb-3">Repuestos pendientes de empaquetar (${unpackagedParts.length})</h3>
              <div class="grid grid-cols-1 gap-3 mb-6">
                ${unpackagedParts.map(part => renderUnpackagedRow(part)).join('')}
              </div>
              ` : ''}
              ${packagedParts.length > 0 ? `
              <h3 class="font-semibold mb-3 text-gray-700">Repuestos ya empaquetados (${packagedParts.length}) — solo informativo</h3>
              <div class="grid grid-cols-1 gap-3">
                ${packagedParts.map(part => renderPackagedRow(part)).join('')}
              </div>
              ` : ''}
              <div class="mt-3 flex justify-between items-center">
                <button onclick="toggleAllLabelsSelection()" class="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm">
                  <i class="fas fa-check-square mr-1"></i>Seleccionar Todas
                </button>
                <span class="text-sm text-gray-600">
                  <span id="selectedLabelsCount">${(order.spareParts || []).length}</span> de ${(order.spareParts || []).length} repuestos seleccionados
                </span>
              </div>
            </div>
          </div>
          
          <div class="p-4 border-t flex justify-end space-x-2">
            <button onclick="closeSparePartSelectionModal()" class="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300">
              Cancelar
            </button>
            ${canSavePackages ? `
            <button onclick="generateLabelsAndPackages(false)" class="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600">
              <i class="fas fa-box mr-2"></i>Guardar
            </button>
            ` : ''}
            <button onclick="openLabelSelectionForPrint()" class="px-4 py-2 bg-purple-500 text-white rounded hover:bg-purple-600">
              <i class="fas fa-print mr-2"></i>Guardar e Imprimir Etiquetas
            </button>
          </div>
        </div>
      </div>
    `;

    // Agregar el modal al DOM
    document.body.insertAdjacentHTML('beforeend', modalHtml);

    // Variables globales para el modal
    let packageCounter = existingPackages.length;
    // Usar la misma orden con la que se renderizó el modal (no selectedOrder, puede diferir)
    let currentOrder = order;
    const initialPackageBySparePartId = new Map<number, string>(
      (order.spareParts || [])
        .filter(sp => !isSparePartPackaged(sp) && (sp.packageNumber || '').trim() !== '')
        .map(sp => [sp.sparePartId, (sp.packageNumber || '').trim()])
    );

    // Agregar funciones JavaScript al window
    (window as any).closeSparePartSelectionModal = () => {
      const modal = document.getElementById('sparePartSelectionModal');
      if (modal) {
        modal.remove();
      }
    };

    (window as any).addNewPackage = () => {
      // Obtener todos los números de exportación únicos de los repuestos
      const uniqueExportNumbers = [...new Set(
        (currentOrder.spareParts || [])
          .filter(sp => sp.exportNumber && sp.exportNumber.trim() !== '' && !isSparePartPackaged(sp))
          .map(sp => sp.exportNumber)
      )];

      if (uniqueExportNumbers.length === 0) {
        alert('No hay repuestos con número de exportación válido en esta orden.');
        return;
      }

      // Si solo hay un número de exportación, usarlo directamente
      if (uniqueExportNumbers.length === 1) {
        createPackageWithExportNumber(uniqueExportNumbers[0]);
        return;
      }

      // Si hay múltiples números de exportación, mostrar modal de selección
      showExportNumberSelectionModal(uniqueExportNumbers);
    };

    // Función para mostrar modal de selección de número de exportación
    function showExportNumberSelectionModal(exportNumbers: string[]) {
      const modalHtml = `
        <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" id="exportNumberSelectionModal" onclick="closeExportNumberSelectionModal()">
          <div class="bg-white rounded-lg shadow-lg w-96 max-h-[90vh] overflow-y-auto" onclick="event.stopPropagation()">
            <div class="p-4 border-b flex justify-between items-center">
              <h3 class="text-lg font-bold">Seleccionar Número de Exportación</h3>
              <button onclick="closeExportNumberSelectionModal()" class="text-gray-500 hover:text-gray-700">
                <i class="fas fa-times"></i>
              </button>
            </div>
            
            <div class="p-4">
              <p class="text-sm text-gray-600 mb-4">
                Esta orden tiene repuestos con diferentes números de exportación. 
                Seleccione con cuál número de exportación desea crear el nuevo paquete:
              </p>
              
              <div class="space-y-3">
                ${exportNumbers.map(exportNum => `
                  <div class="border rounded-lg p-3 hover:bg-gray-50 cursor-pointer export-number-option" data-export-number="${exportNum}">
                    <div class="font-medium text-blue-600">E${exportNum}</div>
                    <div class="text-sm text-gray-500">
                      ${(currentOrder.spareParts || []).filter(sp => sp.exportNumber === exportNum).length} repuesto(s) con este número
                    </div>
                  </div>
                `).join('')}
              </div>
            </div>
            
            <div class="p-4 border-t flex justify-end space-x-2">
              <button onclick="closeExportNumberSelectionModal()" class="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300">
                Cancelar
              </button>
            </div>
          </div>
        </div>
      `;

      document.body.insertAdjacentHTML('beforeend', modalHtml);

      // Agregar event listeners para las opciones
      document.querySelectorAll('.export-number-option').forEach(option => {
        option.addEventListener('click', (e) => {
          const exportNumber = (e.currentTarget as HTMLElement).getAttribute('data-export-number');
          if (exportNumber) {
            (window as any).closeExportNumberSelectionModal();
            createPackageWithExportNumber(exportNumber);
          }
        });
      });
    }

    // Función para cerrar el modal de selección de número de exportación
    (window as any).closeExportNumberSelectionModal = () => {
      const modal = document.getElementById('exportNumberSelectionModal');
      if (modal) {
        modal.remove();
      }
    };

    // Función para crear paquete con un número de exportación específico
    function createPackageWithExportNumber(exportNumber: string, requestingSparePartId?: string) {
      // Llamar al backend para obtener un número de paquete disponible
      fetch(`${environment.apiEndpoint}/Order/next-package-number/${encodeURIComponent(exportNumber)}`)
        .then(response => {
          if (!response.ok) {
            // Intentar obtener el mensaje de error del servidor
            return response.text().then(text => {
              console.error(`Server error response: ${text}`);
              throw new Error(`HTTP error! status: ${response.status}, message: ${text}`);
            });
          }
          return response.json();
        })
        .then(data => {
          if (data && data.nextPackageNumber) {
            const packageNumber = data.nextPackageNumber;
            packageCounter++;

            // Agregar opción a todos los selects
            const packageSelects = document.querySelectorAll('.package-select');
            packageSelects.forEach((select: any) => {
              const option = document.createElement('option');
              option.value = packageNumber;
              option.textContent = packageNumber;
              select.appendChild(option);

              // Si este select pertenece al repuesto que solicitó el nuevo paquete, seleccionarlo automáticamente
              if (requestingSparePartId && select.dataset['sparePartId'] === requestingSparePartId) {
                select.value = packageNumber;
              }
            });

            updatePackagesPreview();
          } else {
            console.error('Invalid response structure:', data);
            console.error('Expected: { nextPackageNumber: string, exportNumber: string }');
            console.error('Received:', JSON.stringify(data, null, 2));
            alert(`Error al generar número de paquete. Respuesta inválida del servidor.\n\nDetalles: ${JSON.stringify(data, null, 2)}`);
          }
        })
        .catch(error => {
          console.error('Error obteniendo número de paquete:', error);
          console.error('Error details:', {
            message: error.message,
            stack: error.stack,
            exportNumber: exportNumber,
            endpoint: `${environment.apiEndpoint}/Order/next-package-number/${encodeURIComponent(exportNumber)}`
          });
          alert(`Error al generar número de paquete. Detalles del error:\n\n${error.message}\n\nPor favor, verifique la conexión y intente nuevamente.`);
        });
    }

    (window as any).clearAllAssignments = () => {
      const packageSelects = document.querySelectorAll('.package-select');
      packageSelects.forEach((select: any) => {
        select.value = '';
      });
      updatePackagesPreview();
    };

    // Inicializar paquetes existentes en los selects
    if (hasExistingPackages) {
      existingPackages.forEach(pkg => {
        packageCounter = Math.max(packageCounter, parseInt(pkg.packageNumber.replace('Paquete ', '')) || 0);
        const packageSelects = document.querySelectorAll('.package-select');
        packageSelects.forEach((select: any) => {
          const option = document.createElement('option');
          option.value = pkg.packageNumber;
          option.textContent = pkg.packageNumber;
          select.appendChild(option);
        });
      });

      // Actualizar la vista previa de paquetes para mostrar los existentes
      setTimeout(() => {
        updatePackagesPreview();
      }, 100);
    }

    // Función para actualizar el contador de etiquetas seleccionadas
    (window as any).updateSelectedLabelsCount = () => {
      const checkedBoxes = document.querySelectorAll('.label-checkbox:checked');
      const totalBoxes = document.querySelectorAll('.label-checkbox');
      const countElement = document.getElementById('selectedLabelsCount');
      if (countElement) {
        countElement.textContent = checkedBoxes.length.toString();
      }
    };

    // Función para seleccionar/deseleccionar todas las etiquetas
    (window as any).toggleAllLabelsSelection = () => {
      const allCheckboxes = document.querySelectorAll('.label-checkbox') as NodeListOf<HTMLInputElement>;
      const allChecked = Array.from(allCheckboxes).every(cb => cb.checked);

      allCheckboxes.forEach(checkbox => {
        checkbox.checked = !allChecked;
      });

      (window as any).updateSelectedLabelsCount();
    };

    // Agregar event listeners a los checkboxes existentes y futuros
    setTimeout(() => {
      const checkboxes = document.querySelectorAll('.label-checkbox');
      checkboxes.forEach(checkbox => {
        checkbox.addEventListener('change', () => {
          (window as any).updateSelectedLabelsCount();
        });
      });
      // Inicializar el contador
      (window as any).updateSelectedLabelsCount();
    }, 100);

    const getModalAssignments = (forPrint: boolean): any[] => {
      const results: any[] = [];
      (currentOrder.spareParts || []).forEach(sp => {
        const checkbox = document.querySelector(`.label-checkbox[data-spare-part-id="${sp.sparePartId}"]`) as HTMLInputElement | null;
        if (forPrint && checkbox && !checkbox.checked) {
          return;
        }

        if (isSparePartPackaged(sp)) {
          const packageNumber = (sp.packageNumber || '').trim();
          if (!packageNumber) {
            return;
          }
          results.push({
            sparePartId: sp.sparePartId,
            sparePartName: sp.sparePartName || '',
            sparePartNameEs: sp.sparePartNameEs || sp.sparePartName || '',
            productCode: sp.productCode || '',
            packageNumber,
            exportNumber: sp.exportNumber || ''
          });
          return;
        }

        const select = document.querySelector(`.package-select[data-spare-part-id="${sp.sparePartId}"]`) as HTMLSelectElement | null;
        const packageNumber = select?.value || '';
        if (!packageNumber || packageNumber === '__create_new__') {
          return;
        }

        results.push({
          sparePartId: sp.sparePartId,
          sparePartName: sp.sparePartName || '',
          sparePartNameEs: sp.sparePartNameEs || sp.sparePartName || '',
          productCode: sp.productCode || '',
          packageNumber,
          exportNumber: sp.exportNumber || ''
        });
      });
      return results;
    };

    /** Repuestos que tenían paquete al abrir el modal y el usuario dejó en "Sin asignar". */
    const getModalUnassignments = (): { sparePartId: number; previousPackageNumber: string }[] => {
      const results: { sparePartId: number; previousPackageNumber: string }[] = [];
      initialPackageBySparePartId.forEach((previousPackageNumber, sparePartId) => {
        const select = document.querySelector(`.package-select[data-spare-part-id="${sparePartId}"]`) as HTMLSelectElement | null;
        const packageNumber = select?.value || '';
        if (!packageNumber || packageNumber === '__create_new__') {
          results.push({ sparePartId, previousPackageNumber });
        }
      });
      return results;
    };

    /** Persiste desasignaciones (limpia PackageNumber + sync PendingPackage en backend). */
    const persistUnassignments = async (): Promise<boolean> => {
      const unassignments = getModalUnassignments();
      if (unassignments.length === 0) {
        return true;
      }

      const clearUpdates: { [sparePartId: number]: string } = {};
      unassignments.forEach(u => {
        clearUpdates[u.sparePartId] = '';
      });

      try {
        await firstValueFrom(componentRef['sparePartService'].updateSparePartsPackageNumbers(clearUpdates));
        unassignments.forEach(u => {
          const sparePart = (currentOrder.spareParts || []).find(sp => sp.sparePartId === u.sparePartId);
          if (sparePart) {
            sparePart.packageNumber = '';
            // Muta un campo suelto: no cambia la identidad de ningun array, hay que avisar.
            componentRef.invalidateAllSparePartsCache();
          }
        });
        return true;
      } catch (error) {
        console.error('Error al desasignar repuestos del paquete:', error);
        alert('Error al desasignar repuestos del paquete. Los cambios no se guardaron.');
        return false;
      }
    };

    const validatePackageAssignments = (assignments: any[], forPrint: boolean): boolean => {
      if (assignments.length === 0) {
        alert(forPrint
          ? 'Debe seleccionar al menos un repuesto para imprimir etiquetas.'
          : 'Debe asignar al menos un repuesto pendiente a un paquete.');
        return false;
      }

      const packageExportNumbers = new Map<string, Set<string>>();
      for (const assignment of assignments) {
        if (!packageExportNumbers.has(assignment.packageNumber)) {
          packageExportNumbers.set(assignment.packageNumber, new Set());
        }
        packageExportNumbers.get(assignment.packageNumber)!.add(assignment.exportNumber || 'SIN_EXPORTACION');
      }

      for (const [packageNumber, exportNumbers] of packageExportNumbers) {
        if (exportNumbers.size > 1) {
          alert(`Error: El paquete ${packageNumber} contiene repuestos con diferentes números de exportación (${Array.from(exportNumbers).join(', ')}).`);
          return false;
        }
      }

      if (forPrint) {
        const usedPackages = [...new Set(assignments.map(item => item.packageNumber))];
        const selectedPackagesWithCheckedItems = [...new Set(
          Array.from(document.querySelectorAll('.package-select'))
            .map((select: any) => {
              const sparePartId = parseInt(select.dataset.sparePartId, 10);
              const sparePart = currentOrder.spareParts.find(sp => sp.sparePartId === sparePartId);
              if (!sparePart || isSparePartPackaged(sparePart)) {
                return null;
              }
              const checkbox = document.querySelector(`.label-checkbox[data-spare-part-id="${sparePartId}"]`) as HTMLInputElement;
              const packageValue = select.value;
              if (checkbox?.checked && packageValue && packageValue !== '' && packageValue !== '__create_new__') {
                return packageValue;
              }
              return null;
            })
            .filter(pkg => pkg !== null)
        )];

        const emptyPackages = selectedPackagesWithCheckedItems.filter(pkg => !usedPackages.includes(pkg));
        if (emptyPackages.length > 0) {
          alert(`No se pueden generar etiquetas con paquetes vacíos. Los siguientes paquetes no tienen repuestos asignados: ${emptyPackages.join(', ')}`);
          return false;
        }
      }

      return true;
    };

    const validateOpenPackagesBeforeSave = async (assignments: any[]): Promise<boolean> => {
      const selectedPackageNumbers = [
        ...new Set(
          assignments
            .map(item => (item?.packageNumber || '').trim())
            .filter((pkg: string) => pkg !== '')
        )
      ];

      if (!selectedPackageNumbers.length) {
        return true;
      }

      const packageChecks = await Promise.all(
        selectedPackageNumbers.map(async (packageNumber: string) => {
          try {
            const response = await fetch(
              `${environment.apiEndpoint}/api/PendingPackage/check/${encodeURIComponent(packageNumber)}`
            );

            if (!response.ok) {
              return {
                packageNumber,
                exists: false,
                isDeleted: false,
                status: 'ERROR'
              };
            }

            const data = await response.json();
            return {
              packageNumber,
              exists: !!data?.exists,
              isDeleted: !!data?.isDeleted,
              status: (data?.status || '').toUpperCase()
            };
          } catch {
            return {
              packageNumber,
              exists: false,
              isDeleted: false,
              status: 'ERROR'
            };
          }
        })
      );

      const blockedPackages = packageChecks
        .filter(pkg => !(pkg.exists && !pkg.isDeleted && pkg.status === 'CREADO'))
        .map(pkg => pkg.packageNumber);

      if (!blockedPackages.length) {
        return true;
      }

      const blockedSet = new Set(blockedPackages);
      const packageSelects = document.querySelectorAll('.package-select');
      packageSelects.forEach((select: any) => {
        if (blockedSet.has(select.value)) {
          select.value = '';
        }
      });

      updatePackagesPreview();
      alert('Algunos paquetes ya no admiten repuestos y fueron desasignados.');
      return false;
    };

    // Función para abrir modal de selección antes de imprimir
    (window as any).openLabelSelectionForPrint = async () => {
      const unassignments = getModalUnassignments();
      const allAssignments = getModalAssignments(true);

      if (allAssignments.length === 0 && unassignments.length === 0) {
        alert('Debe seleccionar al menos un repuesto para imprimir etiquetas.');
        return;
      }

      if (allAssignments.length > 0 && !validatePackageAssignments(allAssignments, true)) {
        return;
      }

      if (!(await persistUnassignments())) {
        return;
      }

      // Si solo se desasignó y no queda nada para imprimir, cerrar y refrescar
      if (allAssignments.length === 0) {
        (window as any).closeSparePartSelectionModal();
        componentRef.showSuccessMessage('Repuestos desasignados del paquete correctamente.');
        componentRef.refreshOrderData(currentOrder.orderId).then(() => {
          const refreshedOrder = componentRef.orders.find(o => o.orderId === currentOrder.orderId);
          if (refreshedOrder) {
            componentRef.selectedOrder = { ...refreshedOrder };
            componentRef.cdr.detectChanges();
          }
        }).catch((error: unknown) => {
          console.error('Error al recargar datos tras desasignar:', error);
        });
        return;
      }

      const confirmModalHtml = `
        <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center" style="z-index: 9999;" id="labelPrintConfirmationModal" onclick="closeLabelPrintConfirmationModal()">
          <div class="bg-white rounded-lg shadow-lg w-11/12 max-w-3xl max-h-[90vh] overflow-y-auto" onclick="event.stopPropagation()">
            <div class="p-4 border-b flex justify-between items-center bg-gray-50">
              <h2 class="text-xl font-bold">Confirmar Impresión de Etiquetas</h2>
              <button onclick="closeLabelPrintConfirmationModal()" class="text-gray-500 hover:text-gray-700">
                <i class="fas fa-times text-xl"></i>
              </button>
            </div>
            
            <div class="p-4">
              <p class="text-gray-600 mb-4">
                Se imprimirán las etiquetas de los siguientes <strong>${allAssignments.length}</strong> repuestos.
                ${packagedParts.length > 0 ? ' Incluye repuestos ya empaquetados (solo reimpresión).' : ''}
              </p>
              
              <div class="border rounded-lg overflow-hidden max-h-96 overflow-y-auto">
                <table class="min-w-full bg-white">
                  <thead class="bg-gray-100 sticky top-0">
                    <tr>
                      <th class="px-4 py-2 border text-left">Nombre</th>
                      <th class="px-4 py-2 border text-left">Código</th>
                      <th class="px-4 py-2 border text-center">N° Paquete</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${allAssignments.map(part => `
                      <tr class="hover:bg-gray-50">
                        <td class="px-4 py-2 border">${part.sparePartNameEs || part.sparePartName}</td>
                        <td class="px-4 py-2 border">${part.productCode}</td>
                        <td class="px-4 py-2 border text-center font-mono font-semibold">${part.packageNumber}</td>
                      </tr>
                    `).join('')}
                  </tbody>
                </table>
              </div>
            </div>
            
            <div class="p-4 border-t flex justify-end space-x-2 bg-gray-50">
              <button onclick="closeLabelPrintConfirmationModal()" 
                      class="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300">
                Cancelar
              </button>
              <button onclick="confirmPrintLabels()" 
                      class="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700">
                <i class="fas fa-print mr-2"></i>Confirmar e Imprimir
              </button>
            </div>
          </div>
        </div>
      `;

      document.body.insertAdjacentHTML('beforeend', confirmModalHtml);

      (window as any).closeLabelPrintConfirmationModal = () => {
        const modal = document.getElementById('labelPrintConfirmationModal');
        if (modal) {
          modal.remove();
        }
      };

      (window as any).confirmPrintLabels = () => {
        (window as any).closeLabelPrintConfirmationModal();
        (window as any).closeSparePartSelectionModal();
        componentRef.executePackAndPrintFlow(currentOrder, allAssignments);
      };
    };

    (window as any).generateLabelsAndPackages = async () => {
      const unassignments = getModalUnassignments();
      const unpackagedAssignments = getModalAssignments(false).filter(a => {
        const sp = currentOrder.spareParts.find(s => s.sparePartId === a.sparePartId);
        return sp && !isSparePartPackaged(sp);
      });

      if (unpackagedAssignments.length === 0 && unassignments.length === 0) {
        alert('Debe asignar al menos un repuesto pendiente a un paquete.');
        return;
      }

      if (unpackagedAssignments.length > 0) {
        if (!validatePackageAssignments(unpackagedAssignments, false)) {
          return;
        }

        if (!(await validateOpenPackagesBeforeSave(unpackagedAssignments))) {
          return;
        }
      }

      if (!(await persistUnassignments())) {
        return;
      }

      // Solo desasignaciones: no hay nada que empaquetar
      if (unpackagedAssignments.length === 0) {
        (window as any).closeSparePartSelectionModal();
        componentRef.showSuccessMessage('Repuestos desasignados del paquete correctamente.');
        componentRef.refreshOrderData(currentOrder.orderId).then(() => {
          const refreshedOrder = componentRef.orders.find(o => o.orderId === currentOrder.orderId);
          if (refreshedOrder) {
            componentRef.selectedOrder = { ...refreshedOrder };
            componentRef.cdr.detectChanges();
          }
        }).catch((error: unknown) => {
          console.error('Error al recargar datos tras desasignar:', error);
        });
        return;
      }

      (window as any).closeSparePartSelectionModal();
      componentRef.processLabelsAndPackages(currentOrder, unpackagedAssignments, false);
    };

    function updatePackagesPreview() {
      // Obtener todas las asignaciones actuales
      const allAssignments = Array.from(document.querySelectorAll('.package-select'))
        .map((select: any) => {
          const sparePartId = select.dataset.sparePartId;
          const packageNumber = select.value;
          const sparePart = currentOrder.spareParts.find(sp => sp.sparePartId === parseInt(sparePartId));

          return {
            id: sparePartId,
            name: sparePart?.sparePartName || '',
            code: sparePart?.productCode || '',
            package: packageNumber || 'Sin asignar'
          };
        });

      // Obtener todos los paquetes creados (incluyendo vacíos) - solo valores únicos
      const allPackages = [...new Set(Array.from(document.querySelectorAll('.package-select option'))
        .map((option: any) => option.value)
        .filter(value => value !== '' && value !== 'Sin asignar' && value !== '__create_new__'))];

      const packagesPreview = document.getElementById('packagesPreview');
      if (packagesPreview) {
        if (allPackages.length === 0) {
          packagesPreview.innerHTML = '<div class="p-3 bg-gray-50 rounded text-center text-gray-500">No hay paquetes creados. Haga clic en "Nuevo Paquete" para crear uno.</div>';
        } else {
          let previewHtml = '<div class="space-y-2">';

          // Mostrar cada paquete individualmente
          allPackages.forEach(packageName => {
            const partsInPackage = allAssignments.filter(part => part.package === packageName);

            if (partsInPackage.length === 0) {
              // Paquete vacío - mostrar como una línea individual
              previewHtml += `
                <div class="p-3 bg-yellow-50 border border-yellow-200 rounded flex justify-between items-center">
                  <div>
                    <strong class="text-yellow-800">${packageName}</strong>
                    <div class="text-sm text-yellow-600">Paquete vacío - Asigne repuestos</div>
                  </div>
                  <div class="flex space-x-2">
                    <button onclick="copyPackageNumber('${packageName}')" class="text-blue-500 hover:text-blue-700 p-1" title="Copiar número de paquete">
                      <i class="fas fa-copy text-sm"></i>
                    </button>
                    <button onclick="removePackage('${packageName}')" class="text-red-500 hover:text-red-700 p-1" title="Eliminar paquete">
                      <i class="fas fa-trash text-sm"></i>
                    </button>
                  </div>
                </div>
              `;
            } else {
              // Paquete con repuestos - mostrar todos los repuestos en una sola línea
              previewHtml += `
                <div class="p-3 bg-green-50 border border-green-200 rounded">
                  <div class="flex justify-between items-start">
                    <div class="flex-1">
                      <strong class="text-green-800">${packageName}</strong>
                      <div class="text-sm text-green-700 mt-1">
                        ${partsInPackage.map((part: any) => `${part.name} (${part.code})`).join(', ')}
                      </div>
                    </div>
                    <div class="flex items-center space-x-2">
                      <span class="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">${partsInPackage.length} repuesto${partsInPackage.length !== 1 ? 's' : ''}</span>
                      <button onclick="copyPackageNumber('${packageName}')" class="text-blue-500 hover:text-blue-700 p-1" title="Copiar número de paquete">
                        <i class="fas fa-copy text-sm"></i>
                      </button>
                    </div>
                  </div>
                </div>
              `;
            }
          });

          previewHtml += '</div>';
          packagesPreview.innerHTML = previewHtml;
        }
      }
    }

    (window as any).removePackage = (packageName: string) => {
      // Limpiar asignaciones de este paquete
      const packageSelects = document.querySelectorAll('.package-select');
      packageSelects.forEach((select: any) => {
        if (select.value === packageName) {
          select.value = '';
        }
        // Remover la opción del select
        const option = select.querySelector(`option[value="${packageName}"]`);
        if (option) {
          option.remove();
        }
      });
      updatePackagesPreview();
    };

    (window as any).copyPackageNumber = (packageName: string) => {
      // Copiar el número de paquete al portapapeles
      navigator.clipboard.writeText(packageName).then(() => {
        // Mostrar mensaje de confirmación
        const button = event?.target as HTMLElement;
        if (button) {
          const originalIcon = button.innerHTML;
          button.innerHTML = '<i class="fas fa-check text-sm"></i>';
          button.classList.remove('text-blue-500', 'hover:text-blue-700');
          button.classList.add('text-green-500');

          // Restaurar el ícono original después de 2 segundos
          setTimeout(() => {
            button.innerHTML = originalIcon;
            button.classList.remove('text-green-500');
            button.classList.add('text-blue-500', 'hover:text-blue-700');
          }, 2000);
        }
      }).catch(err => {
        console.error('Error al copiar al portapapeles:', err);
        // Fallback para navegadores que no soportan clipboard API
        const textArea = document.createElement('textarea');
        textArea.value = packageName;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      });
    };

    // Agregar event listeners
    document.addEventListener('change', (e) => {
      const target = e.target as HTMLSelectElement;

      if (target.classList.contains('package-select')) {
        // Manejar selección de "Nuevo Paquete"
        if (target.value === '__create_new__') {
          const sparePartId = target.dataset['sparePartId'];
          if (sparePartId) {
            // Encontrar el repuesto correspondiente
            const sparePart = (currentOrder.spareParts || []).find(sp => sp.sparePartId === parseInt(sparePartId));
            if (sparePart && sparePart.exportNumber) {
              // Resetear el select a "Sin asignar" inmediatamente para evitar que aparezca en la vista previa
              target.value = '';

              // Crear nuevo paquete con el exportNumber del repuesto
              createPackageWithExportNumber(sparePart.exportNumber, sparePartId);
            } else {
              alert('Este repuesto no tiene número de exportación válido.');
              target.value = '';
            }
          }
          return;
        }

        // Actualizar vista previa para cambios normales
        updatePackagesPreview();
      }

      if (target.classList.contains('spare-part-checkbox')) {
        updatePackagesPreview();
      }
    });
  }

  updateGroupsPreview(): void {
    const checkboxes = document.querySelectorAll('.spare-part-checkbox:checked') as NodeListOf<HTMLInputElement>;
    const groupSelects = document.querySelectorAll('.group-select') as NodeListOf<HTMLSelectElement>;

    // Organizar por grupos e individuales
    const groups: { [key: string]: string[] } = {};
    const individualParts: string[] = [];

    checkboxes.forEach(checkbox => {
      const sparePartId = checkbox.dataset['sparePartId']!;
      const sparePartName = checkbox.dataset['sparePartName']!;

      // Encontrar el grupo seleccionado para este repuesto
      const groupSelect = Array.from(groupSelects).find(select =>
        select.dataset['sparePartId'] === sparePartId
      );

      if (groupSelect) {
        const groupIndex = groupSelect.value;
        if (groupIndex === 'individual') {
          individualParts.push(sparePartName);
        } else {
          if (!groups[groupIndex]) {
            groups[groupIndex] = [];
          }
          groups[groupIndex].push(sparePartName);
        }
      }
    });

    // Actualizar vista previa de números individuales
    const individualElement = document.getElementById('individual-items');
    if (individualElement) {
      if (individualParts.length > 0) {
        individualElement.textContent = individualParts.join(', ') + ` (${individualParts.length} número${individualParts.length > 1 ? 's' : ''} individual${individualParts.length > 1 ? 'es' : ''})`;
      } else {
        individualElement.textContent = 'Ningún repuesto seleccionado';
      }
    }

    // Actualizar vista previa de grupos
    Object.keys(groups).forEach(groupIndex => {
      const groupElement = document.getElementById(`group-${groupIndex}-items`);
      if (groupElement) {
        if (groups[groupIndex].length > 0) {
          groupElement.textContent = groups[groupIndex].join(', ') + ` (1 número compartido)`;
        } else {
          groupElement.textContent = 'Ningún repuesto seleccionado';
        }
      }
    });

    // Limpiar grupos vacíos
    const allGroupElements = document.querySelectorAll('[id^="group-"][id$="-items"]');
    allGroupElements.forEach(element => {
      const groupIndex = element.id.split('-')[1];
      if (groupIndex !== 'individual' && !groups[groupIndex]) {
        element.textContent = 'Ningún repuesto seleccionado';
      }
    });
  }

  generateLabelsForSelectedParts(order: OrderOutModel): void {
    const checkboxes = document.querySelectorAll('.spare-part-checkbox:checked') as NodeListOf<HTMLInputElement>;
    const groupSelects = document.querySelectorAll('.group-select') as NodeListOf<HTMLSelectElement>;

    if (checkboxes.length === 0) {
      this.showErrorMessage('Por favor, seleccione al menos un repuesto.');
      return;
    }

    // Organizar repuestos por grupos e individuales
    const groups: { [key: string]: { sparePartId: number; sparePartName: string; sparePartNameEs?: string; productCode: string }[] } = {};
    const individualParts: { sparePartId: number; sparePartName: string; sparePartNameEs?: string; productCode: string }[] = [];

    checkboxes.forEach(checkbox => {
      const sparePartId = parseInt(checkbox.dataset['sparePartId']!);
      const sparePartName = checkbox.dataset['sparePartName']!;
      const productCode = checkbox.dataset['productCode']!;

      // Encontrar el sparePart para obtener el nombre en español
      const sparePart = order.spareParts?.find(sp => sp.sparePartId === sparePartId);
      const sparePartNameEs = sparePart?.sparePartNameEs;

      // Encontrar el grupo seleccionado para este repuesto
      const groupSelect = Array.from(groupSelects).find(select =>
        select.dataset['sparePartId'] === checkbox.dataset['sparePartId']
      );

      if (groupSelect) {
        const groupIndex = groupSelect.value;
        if (groupIndex === 'individual') {
          individualParts.push({
            sparePartId,
            sparePartName,
            sparePartNameEs,
            productCode
          });
        } else {
          if (!groups[groupIndex]) {
            groups[groupIndex] = [];
          }
          groups[groupIndex].push({
            sparePartId,
            sparePartName,
            sparePartNameEs,
            productCode
          });
        }
      }
    });

    // Cerrar el modal
    (window as any).closeSparePartSelectionModal();

    // Generar números de paquete y etiquetas
    this.generatePackageNumbersWithIndividuals(order, groups, individualParts);
  }

  generatePackageNumbers(order: OrderOutModel, selectedParts: { sparePartId: number; sparePartName: string; productCode: string }[], useSharedNumber: boolean): void {
    // Obtener el número de exportación del primer repuesto seleccionado
    // Si useSharedNumber es true, todos los repuestos seleccionados deberían tener el mismo ExportNumber
    const firstSelectedPart = (order.spareParts || []).find(sp => selectedParts.some(sel => sel.sparePartId === sp.sparePartId));

    if (!firstSelectedPart || !firstSelectedPart.exportNumber) {
      this.showErrorMessage('No se pudo obtener el número de exportación de los repuestos seleccionados.');
      return;
    }

    const exportNumber = firstSelectedPart.exportNumber;

    // Obtener el siguiente número de paquete disponible del backend
    this.orderService.getNextPackageNumber(exportNumber).subscribe({
      next: (response) => {
        const packageNumber = response.nextPackageNumber;

        // Actualizar los números de paquete de los spare parts seleccionados
        const sparePartPackageUpdates: { [sparePartId: number]: string } = {};
        selectedParts.forEach(part => {
          sparePartPackageUpdates[part.sparePartId] = packageNumber;
        });

        // Actualizar en backend
        this.sparePartService.updateSparePartsPackageNumbers(sparePartPackageUpdates).subscribe({
          next: (response) => {
            this.showSuccessMessage(`Se actualizaron ${selectedParts.length} números de paquete exitosamente.`);

            // Generar PDF con las etiquetas
            this.generateLabelsPDF(order, selectedParts, [packageNumber], useSharedNumber);
          },
          error: (error) => {
            console.error('Error al actualizar números de paquete:', error);
            this.showErrorMessage('Error al actualizar los números de paquete de los repuestos.');
          }
        });
      },
      error: (error) => {
        console.error('Error al obtener el siguiente número de paquete:', error);
        this.showErrorMessage('Error al obtener el siguiente número de paquete disponible.');
      }
    });
  }

  generatePackageNumbersByGroups(order: OrderOutModel, groups: { [key: string]: { sparePartId: number; sparePartName: string; productCode: string }[] }): void {
    // Obtener el primer repuesto para obtener el número de exportación
    // Todos los repuestos en esta función deberían tener el mismo ExportNumber
    const firstGroup = Object.values(groups).find(group => group.length > 0);
    if (!firstGroup || firstGroup.length === 0) {
      this.showErrorMessage('No hay repuestos seleccionados para agrupar.');
      return;
    }

    const firstSelectedPart = (order.spareParts || []).find(sp => sp.sparePartId === firstGroup[0].sparePartId);
    if (!firstSelectedPart || !firstSelectedPart.exportNumber) {
      this.showErrorMessage('No se pudo obtener el número de exportación de los repuestos seleccionados.');
      return;
    }

    const exportNumber = firstSelectedPart.exportNumber;

    // Obtener el siguiente número de paquete disponible del backend
    this.orderService.getNextPackageNumber(exportNumber).subscribe({
      next: (response) => {
        // Extraer el número del paquete retornado (ej: E464574567345-PAC0001 -> 1)
        const packageNumberMatch = response.nextPackageNumber.match(/PAC(\d+)$/);
        let packageNumberCounter = packageNumberMatch ? parseInt(packageNumberMatch[1]) : 1;

        // Generar números de paquete por grupo
        const groupPackageNumbers: { [key: string]: string } = {};

        Object.keys(groups).forEach(groupIndex => {
          if (groups[groupIndex].length > 0) {
            const packageNumber = `E${response.exportNumber}-PAC${packageNumberCounter.toString().padStart(4, '0')}`;
            groupPackageNumbers[groupIndex] = packageNumber;
            packageNumberCounter++;
          }
        });

        // Generar etiquetas con códigos de barras
        this.generateLabelsWithBarcodes(order, groups, groupPackageNumbers);
      },
      error: (error) => {
        console.error('Error al obtener el siguiente número de paquete:', error);
        this.showErrorMessage('Error al obtener el siguiente número de paquete disponible.');
      }
    });
  }

  generatePackageNumbersWithIndividuals(order: OrderOutModel, groups: { [key: string]: { sparePartId: number; sparePartName: string; sparePartNameEs?: string; productCode: string }[] }, individualParts: { sparePartId: number; sparePartName: string; sparePartNameEs?: string; productCode: string }[]): void {
    // VALIDACIÓN: Verificar que todos los repuestos en cada grupo tengan el mismo número de exportación
    for (const [groupIndex, groupParts] of Object.entries(groups)) {
      if (groupParts.length > 0) {
        // Obtener el número de exportación del primer repuesto del grupo
        const firstPart = (order.spareParts || []).find(sp => sp.sparePartId === groupParts[0].sparePartId);
        if (!firstPart || !firstPart.exportNumber) {
          this.showErrorMessage(`No se pudo obtener el número de exportación del primer repuesto del grupo ${groupIndex}.`);
          return;
        }

        const groupExportNumber = firstPart.exportNumber;

        // Verificar que todos los repuestos del grupo tengan el mismo número de exportación
        for (const part of groupParts) {
          const sparePart = (order.spareParts || []).find(sp => sp.sparePartId === part.sparePartId);
          if (!sparePart) {
            this.showErrorMessage(`Repuesto ${part.sparePartNameEs || part.sparePartName} no encontrado en la orden.`);
            return;
          }

          if (!sparePart.exportNumber) {
            this.showErrorMessage(`El repuesto ${sparePart.sparePartName} no tiene número de exportación asignado.`);
            return;
          }

          if (sparePart.exportNumber !== groupExportNumber) {
            this.showErrorMessage(`El repuesto ${sparePart.sparePartName} tiene el número de exportación ${sparePart.exportNumber}, pero el grupo requiere ${groupExportNumber}. Todos los repuestos de un paquete deben tener el mismo número de exportación.`);
            return;
          }
        }
      }
    }

    // Obtener el primer grupo para determinar el número de exportación base
    const firstGroup = Object.values(groups).find(group => group.length > 0);
    if (!firstGroup || firstGroup.length === 0) {
      this.showErrorMessage('No hay repuestos seleccionados para agrupar.');
      return;
    }

    const firstSelectedPart = (order.spareParts || []).find(sp => sp.sparePartId === firstGroup[0].sparePartId);
    if (!firstSelectedPart || !firstSelectedPart.exportNumber) {
      this.showErrorMessage('No se pudo obtener el número de exportación de los repuestos seleccionados.');
      return;
    }

    const exportNumber = firstSelectedPart.exportNumber;

    // Obtener el siguiente número de paquete disponible del backend
    this.orderService.getNextPackageNumber(exportNumber).subscribe({
      next: (response) => {
        // Extraer el número del paquete retornado (ej: E464574567345-PAC0001 -> 1)
        const packageNumberMatch = response.nextPackageNumber.match(/PAC(\d+)$/);
        let packageNumberCounter = packageNumberMatch ? parseInt(packageNumberMatch[1]) : 1;

        // Generar números de paquete por grupo
        const groupPackageNumbers: { [key: string]: string } = {};
        const sparePartPackageUpdates: { [sparePartId: number]: string } = {};

        Object.keys(groups).forEach(groupIndex => {
          if (groups[groupIndex].length > 0) {
            const packageNumber = `E${response.exportNumber}-PAC${packageNumberCounter.toString().padStart(4, '0')}`;
            groupPackageNumbers[groupIndex] = packageNumber;

            // Asignar el mismo número de paquete a todos los spare parts del grupo
            groups[groupIndex].forEach(part => {
              sparePartPackageUpdates[part.sparePartId] = packageNumber;
            });

            packageNumberCounter++;
          }
        });

        // Generar números individuales para cada repuesto
        const individualPackageNumbers: { sparePartId: number; packageNumber: string; sparePartName: string; sparePartNameEs?: string; productCode: string }[] = [];

        individualParts.forEach(part => {
          // Obtener el número de exportación de este repuesto específico
          const sparePart = (order.spareParts || []).find(sp => sp.sparePartId === part.sparePartId);
          if (sparePart && sparePart.exportNumber) {
            const packageNumber = `E${response.exportNumber}-PAC${packageNumberCounter.toString().padStart(4, '0')}`;
            individualPackageNumbers.push({
              sparePartId: part.sparePartId,
              packageNumber,
              sparePartName: part.sparePartNameEs || part.sparePartName,
              productCode: part.productCode
            });

            // Agregar al diccionario de actualizaciones
            sparePartPackageUpdates[part.sparePartId] = packageNumber;
            packageNumberCounter++;
          }
        });

        // Actualizar los números de paquete de los spare parts en la base de datos
        if (Object.keys(sparePartPackageUpdates).length > 0) {
          this.sparePartService.updateSparePartsPackageNumbers(sparePartPackageUpdates).subscribe({
            next: (response) => {
              this.showSuccessMessage(`Se actualizaron ${Object.keys(sparePartPackageUpdates).length} números de paquete exitosamente.`);

              // Generar etiquetas con códigos de barras
              this.generateLabelsWithBarcodesAndIndividuals(order, groups, groupPackageNumbers, individualPackageNumbers);
            },
            error: (error) => {
              console.error('Error al actualizar números de paquete de spare parts:', error);
              this.showErrorMessage('Error al actualizar los números de paquete de los repuestos.');
            }
          });
        } else {
          this.showErrorMessage('No se seleccionaron repuestos para generar etiquetas.');
        }
      },
      error: (error) => {
        console.error('Error al obtener el siguiente número de paquete:', error);
        this.showErrorMessage('Error al obtener el siguiente número de paquete disponible.');
      }
    });
  }



  /**
   * Formatea el número de exportación para uso en códigos de paquete
   * Si es un número, lo formatea con padding, si es texto lo usa directamente
   */
  formatExportNumber(exportNumber: string): string {
    // Si es un número, aplicar padding
    if (/^\d+$/.test(exportNumber)) {
      return exportNumber.padStart(4, '0');
    }
    // Si es texto, limpiarlo de caracteres especiales y devolver como está
    return exportNumber.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
  }

  private getCompanyLogoPath(company: string | undefined): string {
    // Usar el servicio de labels para obtener el logo correcto
    return this.labelsService.getLogoForCompany(company);
  }

  private getContactInfoHtml(): string {
    return `
      <div class="contact-info">
        <div><strong>Nombre:</strong> Mateo Carniero</div>
        <div><strong>Teléfono:</strong> +598 95 500 745</div>
      </div>
    `;
  }



  generateLabelsPDF(order: OrderOutModel, selectedParts: { sparePartId: number; sparePartName: string; productCode: string }[], packageNumbers: string[], useSharedNumber: boolean): void {
    this.printLabelsWindow(order, selectedParts, packageNumbers, useSharedNumber);
  }

  generateLabelsWithBarcodes(order: OrderOutModel, groups: { [key: string]: { sparePartId: number; sparePartName: string; productCode: string }[] }, groupPackageNumbers: { [key: string]: string }): void {
    const labelsHtml = this.generateLabelsHtmlWithBarcodes(order, groups, groupPackageNumbers);

    // Crear una nueva ventana para imprimir
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    if (printWindow) {
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Etiquetas de Repuestos</title>
          <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.5/dist/JsBarcode.all.min.js"></script>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .label { 
              border: 2px solid #000; 
              padding: 6px; 
              margin: 10px 0; 
              page-break-inside: avoid; 
              width: 264px;
              height: 189px;
              display: flex;
              flex-direction: column;
              background-color: white;
              box-shadow: 0 2px 4px rgba(0,0,0,0.1);
              box-sizing: border-box;
              overflow: hidden;
            }
            .label-header {
              display: flex;
              align-items: center;
              justify-content: space-between;
              margin-bottom: 2px;
              height: 55px;
            }
            .label-logo {
              max-height: 50px;
              max-width: 160px;
              object-fit: contain;
            }
            .label-title { 
              font-size: 18px; 
              font-weight: bold; 
              text-align: center; 
              margin-bottom: 15px;
            }
            .label-info { 
              font-size: 14px; 
              line-height: 1.5; 
            }
            .label-barcode { 
              text-align: center; 
              margin: 10px 0;
            }
            .label-barcode svg {
              max-width: 100%;
              height: 60px;
            }
            .contact-info { 
              font-size: 12px; 
              text-align: right; 
            }
            .label-number { 
              font-size: 12px; 
              text-align: right; 
              margin-top: 10px;
            }
            @media print {
              body { margin: 0; }
              .label { 
                page-break-after: always; 
                margin: 0;
                width: 100%;
                height: 100vh;
              }
            }
          </style>
        </head>
        <body>
          ${labelsHtml}
          <script>
            window.onload = function() {
              // Generar códigos de barras
              document.querySelectorAll('.barcode').forEach(function(element) {
                const code = element.dataset.code;
                if (code) {
                  JsBarcode(element, code, {
                    format: "CODE128",
                    width: 2,
                    height: 60,
                    displayValue: false
                  });
                }
              });
              
              // Imprimir después de generar códigos de barras
              setTimeout(function() {
                window.print();
                window.onafterprint = function() {
                  window.close();
                };
              }, 1000);
            };
          </script>
        </body>
        </html>
      `);
      printWindow.document.close();
    }

    this.showSuccessMessage('Etiquetas generadas exitosamente para impresión.');
  }

  generateLabelsWithBarcodesAndIndividuals(order: OrderOutModel, groups: { [key: string]: { sparePartId: number; sparePartName: string; productCode: string }[] }, groupPackageNumbers: { [key: string]: string }, individualPackageNumbers: { sparePartId: number; packageNumber: string; sparePartName: string; productCode: string }[]): void {
    const labelsHtml = this.generateLabelsHtmlWithBarcodesAndIndividuals(order, groups, groupPackageNumbers, individualPackageNumbers);

    // Crear una nueva ventana para imprimir
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    if (printWindow) {
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Etiquetas de Repuestos</title>
          <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.5/dist/JsBarcode.all.min.js"></script>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .label { 
              border: 2px solid #000; 
              padding: 6px; 
              margin: 10px 0; 
              page-break-inside: avoid; 
              width: 264px;
              height: 189px;
              display: flex;
              flex-direction: column;
              background-color: white;
              box-shadow: 0 2px 4px rgba(0,0,0,0.1);
              box-sizing: border-box;
              overflow: hidden;
            }
            .label-header {
              display: flex;
              align-items: center;
              justify-content: space-between;
              margin-bottom: 2px;
              height: 55px;
            }
            .label-logo {
              max-height: 50px;
              max-width: 160px;
              object-fit: contain;
            }
            .label-title { 
              font-size: 18px; 
              font-weight: bold; 
              text-align: center; 
              margin-bottom: 15px;
            }
            .label-info { 
              font-size: 14px; 
              line-height: 1.5; 
            }
            .label-barcode { 
              text-align: center; 
              margin: 10px 0;
            }
            .label-barcode svg {
              max-width: 100%;
              height: 60px;
            }
            .contact-info { 
              font-size: 12px; 
              text-align: right; 
            }
            .label-number { 
              font-size: 12px; 
              text-align: right; 
              margin-top: 10px;
            }
            @media print {
              body { margin: 0; }
              .label { 
                page-break-after: always; 
                margin: 0;
                width: 100%;
                height: 100vh;
              }
            }
          </style>
        </head>
        <body>
          ${labelsHtml}
          <script>
            window.onload = function() {
              // Generar códigos de barras
              document.querySelectorAll('.barcode').forEach(function(element) {
                const code = element.dataset.code;
                if (code) {
                  JsBarcode(element, code, {
                    format: "CODE128",
                    width: 2,
                    height: 60,
                    displayValue: false
                  });
                }
              });
              
              // Imprimir después de generar códigos de barras
              setTimeout(function() {
                window.print();
                window.onafterprint = function() {
                  window.close();
                };
              }, 1000);
            };
          </script>
        </body>
        </html>
      `);
      printWindow.document.close();
    }

    this.showSuccessMessage('Etiquetas generadas exitosamente para impresión.');
  }

  printLabelsWindow(order: OrderOutModel, selectedParts: { sparePartId: number; sparePartName: string; sparePartNameEs?: string; productCode: string }[], packageNumbers: string[], useSharedNumber: boolean): void {
    const labelsHtml = this.generateLabelsHtml(order, selectedParts, packageNumbers, useSharedNumber);

    // Crear una nueva ventana para imprimir
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    if (printWindow) {
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Etiquetas de Repuestos</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .label { 
              border: 2px solid #000; 
              padding: 20px; 
              margin: 20px 0; 
              page-break-inside: avoid; 
              width: 400px;
              height: 250px;
              display: flex;
              flex-direction: column;
              justify-content: space-between;
            }
            .label-header {
              display: flex;
              align-items: center;
              justify-content: space-between;
              margin-bottom: 2px;
              height: 55px;
            }
            .label-logo {
              max-height: 50px;
              max-width: 160px;
              object-fit: contain;
            }
            .label-title { 
              font-size: 18px; 
              font-weight: bold; 
              text-align: center; 
              margin-bottom: 15px;
            }
            .label-info { 
              font-size: 14px; 
              line-height: 1.5; 
            }
            .contact-info { 
              font-size: 12px; 
              text-align: right; 
            }
            .label-number { 
              font-size: 12px; 
              text-align: right; 
              margin-top: 10px;
            }
            @media print {
              body { margin: 0; }
              .label { 
                page-break-after: always; 
                margin: 0;
                width: 100%;
                height: 100vh;
              }
            }
          </style>
        </head>
        <body>
          ${labelsHtml}
          <script>
            window.onload = function() {
              window.print();
              window.onafterprint = function() {
                window.close();
              };
            };
          </script>
        </body>
        </html>
      `);
      printWindow.document.close();
    }

    this.showSuccessMessage('Etiquetas generadas exitosamente para impresión.');
  }

  generateLabelsHtml(order: OrderOutModel, selectedParts: { sparePartId: number; sparePartName: string; sparePartNameEs?: string; productCode: string }[], packageNumbers: string[], useSharedNumber: boolean): string {
    let html = '';

    selectedParts.forEach((part, index) => {
      const packageNumber = useSharedNumber ? packageNumbers[0] : packageNumbers[index];
      const totalLabels = useSharedNumber ? selectedParts.length : 1;
      const currentLabelNumber = useSharedNumber ? index + 1 : 1;
      const logoSrc = this.getCompanyLogoPath(order?.clienteUY);

      html += `
        <div class="label">
          <div class="label-header">
            <img class="label-logo" src="${logoSrc}" onerror="this.style.display='none'" />
            ${this.getContactInfoHtml()}
          </div>
          
          <div class="label-info">
            <div><strong>Siniestro:</strong> ${order.siniestro}</div>
            <div><strong>Orden:</strong> ${order.orderNumber}</div>
            <div><strong>Repuesto:</strong> ${part.sparePartNameEs || part.sparePartName}</div>
            <div><strong>Código de pieza:</strong> ${part.productCode}</div>
          </div>
          <div class="label-number">Etiqueta: ${useSharedNumber ? `${currentLabelNumber}/${totalLabels}` : '1/1'}</div>
        </div>
      `;
    });

    return html;
  }

  generateLabelsHtmlWithBarcodes(order: OrderOutModel, groups: { [key: string]: { sparePartId: number; sparePartName: string; sparePartNameEs?: string; productCode: string }[] }, groupPackageNumbers: { [key: string]: string }): string {
    let html = '';

    Object.keys(groups).forEach(groupIndex => {
      const groupParts = groups[groupIndex];
      const packageNumber = groupPackageNumbers[groupIndex];
      const logoSrc = this.getCompanyLogoPath(order?.clienteUY);

      if (groupParts.length > 0) {
        groupParts.forEach((part, partIndex) => {
          const labelNumber = groupParts.length > 1 ? `${partIndex + 1}/${groupParts.length}` : '1/1';

          html += `
            <div class="label">
              <div class="label-header">
                <img class="label-logo" src="${logoSrc}" onerror="this.style.display='none'" />
                ${this.getContactInfoHtml()}
              </div>
              
              <div class="label-barcode">
                <svg class="barcode" data-code="${packageNumber}"></svg>
              </div>
              <div class="label-info">
                <div><strong>Siniestro:</strong> ${order.siniestro}</div>
                <div><strong>Orden:</strong> ${order.orderNumber}</div>
                <div><strong>Repuesto:</strong> ${part.sparePartNameEs || part.sparePartName}</div>
                <div><strong>Código de pieza:</strong> ${part.productCode}</div>
              </div>
              <div class="label-number">Etiqueta: ${labelNumber}</div>
            </div>
          `;
        });
      }
    });

    return html;
  }

  generateLabelsHtmlWithBarcodesAndIndividuals(order: OrderOutModel, groups: { [key: string]: { sparePartId: number; sparePartName: string; sparePartNameEs?: string; productCode: string }[] }, groupPackageNumbers: { [key: string]: string }, individualPackageNumbers: { sparePartId: number; packageNumber: string; sparePartName: string; sparePartNameEs?: string; productCode: string }[]): string {
    let html = '';

    // Generar etiquetas para grupos
    Object.keys(groups).forEach(groupIndex => {
      const groupParts = groups[groupIndex];
      const packageNumber = groupPackageNumbers[groupIndex];
      const logoSrc = this.getCompanyLogoPath(order?.clienteUY);

      if (groupParts.length > 0) {
        groupParts.forEach((part, partIndex) => {
          const labelNumber = groupParts.length > 1 ? `${partIndex + 1}/${groupParts.length}` : '1/1';

          html += `
            <div class="label">
              <div class="label-header">
                <img class="label-logo" src="${logoSrc}" onerror="this.style.display='none'" />
                ${this.getContactInfoHtml()}
              </div>
              
              <div class="label-barcode">
                <svg class="barcode" data-code="${packageNumber}"></svg>
              </div>
              <div class="label-info">
                <div><strong>Siniestro:</strong> ${order.siniestro}</div>
                <div><strong>Orden:</strong> ${order.orderNumber}</div>
                <div><strong>Repuesto:</strong> ${part.sparePartNameEs || part.sparePartName}</div>
                <div><strong>Código de pieza:</strong> ${part.productCode}</div>
              </div>
              <div class="label-number">Etiqueta: ${labelNumber}</div>
            </div>
          `;
        });
      }
    });

    // Generar etiquetas individuales
    individualPackageNumbers.forEach(part => {
      const logoSrc = this.getCompanyLogoPath(order?.clienteUY);
      html += `
        <div class="label">
          <div class="label-header">
            <img class="label-logo" src="${logoSrc}" onerror="this.style.display='none'" />
            ${this.getContactInfoHtml()}
          </div>
          
          <div class="label-barcode">
            <svg class="barcode" data-code="${part.packageNumber}"></svg>
          </div>
          <div class="label-info">
            <div><strong>Siniestro:</strong> ${order.siniestro}</div>
            <div><strong>Orden:</strong> ${order.orderNumber}</div>
            <div><strong>Repuesto:</strong> ${part.sparePartNameEs || part.sparePartName}</div>
            <div><strong>Código de pieza:</strong> ${part.productCode}</div>
          </div>
          <div class="label-number">Etiqueta: 1/1</div>
        </div>
      `;
    });

    return html;
  }

  // ========== MÉTODOS PARA EXPORTACIÓN DE CARPETAS ==========

  // Métodos del dropdown
  toggleExportDropdown() {
    this.showExportDropdown = !this.showExportDropdown;
  }

  async copyFilteredOrdersForWhatsapp(): Promise<void> {
    if (this.isCopyingWhatsappOrders) return;

    try {
      this.isCopyingWhatsappOrders = true;
      const selectedOrders = this.getSelectedOrdersForWhatsapp();
      if (selectedOrders.length === 0) {
        this.showErrorMessage('No hay órdenes seleccionadas para copiar');
        return;
      }

      const text = this.buildWhatsappOrdersClipboardText(selectedOrders);
      if (!text.trim()) {
        this.showErrorMessage('No hay datos para copiar');
        return;
      }

      await this.copyTextToClipboardSafe(text);
      this.showSuccessMessage('Datos copiados al portapapeles para WhatsApp');
    } catch (error) {
      console.error('Error al copiar órdenes seleccionadas para WhatsApp:', error);
      this.showErrorMessage('No se pudieron copiar las órdenes seleccionadas');
    } finally {
      this.isCopyingWhatsappOrders = false;
    }
  }

  private getSelectedOrdersForWhatsapp(): OrderOutModel[] {
    const selectedByOrderId = new Map<number, OrderOutModel>();
    const orderSources: OrderOutModel[] = [
      ...(this.filteredOrdersList || []),
      ...(this.sortedAndFilteredOrders || []),
      ...(this.orders || [])
    ];

    orderSources.forEach(order => {
      const orderId = Number(order?.orderId);
      if (!order?.selected || !Number.isFinite(orderId)) return;
      if (!selectedByOrderId.has(orderId)) {
        selectedByOrderId.set(orderId, order);
      }
    });

    return Array.from(selectedByOrderId.values());
  }

  private buildWhatsappOrdersClipboardText(orders: OrderOutModel[]): string {
    const lines: string[] = [];

    lines.push('LISTADO ORDENES SELECCIONADAS');
    lines.push(`Total ordenes: ${orders.length}`);
    lines.push('');

    if (orders.length === 0) {
      lines.push('Sin resultados');
      return lines.join('\n');
    }

    orders.forEach(order => {
      const siniestro = this.getWhatsappSafeValue(order?.siniestro);
      const orderNumber = this.getWhatsappSafeValue(order?.orderNumber);
      const spareParts = Array.isArray(order?.spareParts) ? order.spareParts : [];

      if (spareParts.length === 0) {
        lines.push(
          `Siniestro: ${siniestro} | Orden: ${orderNumber} | Sin repuestos | Nombre pieza BR: N/A | Codigo pieza: N/A | Nro paquete: N/A`
        );
        return;
      }

      spareParts.forEach((part, index) => {
        const sparePartNameBr = this.getWhatsappSafeValue(
          part?.sparePartNamePt || part?.sparePartName || part?.sparePartNameEs
        );
        const sparePartCode = this.getWhatsappSafeValue(part?.productCode || part?.codBr);
        const packageNumber = this.getWhatsappSafeValue(part?.packageNumber);

        lines.push(
          `Siniestro: ${siniestro} | Orden: ${orderNumber} | Repuesto ${index + 1}: ${sparePartNameBr} | Codigo pieza: ${sparePartCode} | Nro paquete: ${packageNumber}`
        );
      });
    });

    return lines.join('\n');
  }

  private getWhatsappSafeValue(value: any): string {
    if (value === null || value === undefined) return 'N/A';
    const text = String(value).trim();
    return text.length > 0 ? text : 'N/A';
  }

  private async copyTextToClipboardSafe(text: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.warn('Clipboard API no disponible, usando fallback:', err);
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-9999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
  }

  // Métodos de carpeta exportada
  async openCreateFolderDialog() {
    this.showExportDropdown = false;

    try {
      // Verificar conectividad con el backend
      const isConnected = await this.verifyBackendConnectivity();
      if (!isConnected) {
        this.showErrorMessage('No se puede conectar con el servidor. Verifique su conexión.');
        return;
      }

      // Obtener todas las órdenes que cumplen los filtros actuales
      const allOrders = await this.orderService.getAllFilteredOrders(
        this.searchTerm || undefined,
        this.startDate,
        this.endDate
      ).toPromise();

      this.allFilteredOrders = allOrders || [];
      this.totalFilteredOrders = this.allFilteredOrders.length;

      if (this.totalFilteredOrders === 0) {
        this.showErrorMessage('No hay órdenes para exportar con los filtros actuales');
        return;
      }

      this.folderName = this.exportedFoldersService.generateDefaultFolderName();
      this.folderDescription = '';
      this.showCreateFolderModal = true;
    } catch (error) {
      console.error('Error al obtener órdenes filtradas:', error);
      this.showErrorMessage('Error al obtener las órdenes filtradas');
    }
  }

  createExportedFolder() {
    if (!this.folderName.trim()) {
      this.showErrorMessage('El nombre de la carpeta es requerido');
      return;
    }

    this.showCreateFolderModal = false;
    this.showConfirmModal = true;
  }

  confirmCreateFolder() {
    this.showConfirmModal = false;
    this.startFolderCreation();
  }

  private async startFolderCreation() {
    try {
      this.isCreatingFolder = true;
      this.exportCancelled = false;
      this.canCancelExport = true;
      this.folderProgress = 0;
      this.createdFolderUrl = null;

      // Verificar si la exportación fue cancelada antes de continuar
      if (this.exportCancelled) {
        this.isCreatingFolder = false;
        this.canCancelExport = false;
        return;
      }

      // Animación inicial de carga
      await this.animateProgress(0, 10, 500, 'Iniciando proceso...');

      // Verificar cancelación después de cada paso
      if (this.exportCancelled) {
        this.isCreatingFolder = false;
        this.canCancelExport = false;
        return;
      }

      // Progreso: Preparando datos
      await this.animateProgress(10, 25, 800, 'Preparando datos...');

      if (this.exportCancelled) {
        this.isCreatingFolder = false;
        this.canCancelExport = false;
        return;
      }

      // Crear modelo de carpeta - el usuario se obtiene por autenticación en el backend
      const folderModel: CreateExportedFolderModel = {
        folderName: this.folderName,
        description: this.folderDescription,
        orderIds: this.allFilteredOrders.map(o => o.orderId),
        filterCriteria: this.getFilterCriteria()
      };

      // Progreso: Validando datos
      await this.animateProgress(25, 40, 600, 'Validando datos...');

      if (this.exportCancelled) {
        this.isCreatingFolder = false;
        this.canCancelExport = false;
        return;
      }

      // Progreso: Creando carpeta
      await this.animateProgress(40, 60, 1000, 'Creando carpeta exportada...');

      if (this.exportCancelled) {
        this.isCreatingFolder = false;
        this.canCancelExport = false;
        return;
      }

      const createdFolder = await this.exportedFoldersService.createExportedFolder(folderModel).toPromise();

      if (this.exportCancelled) {
        // Intentar cancelar en el backend si es posible
        try {
          await this.exportedFoldersService.cancelExport(createdFolder?.folderId).toPromise();
        } catch (cancelError) {
        }
        this.isCreatingFolder = false;
        this.canCancelExport = false;
        return;
      }

      // Progreso: Procesando archivos
      await this.animateProgress(60, 80, 1200, 'Procesando archivos...');

      if (this.exportCancelled) {
        this.isCreatingFolder = false;
        this.canCancelExport = false;
        return;
      }

      // Progreso: Generando archivos
      await this.animateProgress(80, 95, 800, 'Generando archivos...');

      if (this.exportCancelled) {
        this.isCreatingFolder = false;
        this.canCancelExport = false;
        return;
      }

      // Generar URL de descarga
      this.createdFolderUrl = `${environment.apiEndpoint}/ExportedFolder/${createdFolder?.folderId}/download`;

      // Progreso: Finalizando
      await this.animateProgress(95, 100, 500, 'Carpeta creada exitosamente');

      if (this.exportCancelled) {
        this.isCreatingFolder = false;
        this.canCancelExport = false;
        return;
      }

      setTimeout(() => {
        this.isCreatingFolder = false;
        this.canCancelExport = false;
        this.showSuccessMessage(`Carpeta "${this.folderName}" creada exitosamente`);
      }, 1000);

    } catch (error) {
      console.error('Error al crear carpeta:', error);

      // Manejo detallado de errores
      let errorMessage = 'Error al crear la carpeta exportada';
      if (error && typeof error === 'object') {
        if (error.status === 401) {
          errorMessage = 'Error de autenticación. Por favor, inicie sesión nuevamente.';
        } else if (error.status === 404) {
          errorMessage = 'Endpoint no encontrado. Verifique la configuración del servidor.';
        } else if (error.status === 500) {
          errorMessage = 'Error interno del servidor. Contacte al administrador.';
        } else if (error.message) {
          errorMessage = `Error: ${error.message}`;
        }
      }

      this.showErrorMessage(errorMessage);
      this.isCreatingFolder = false;
      this.canCancelExport = false;
      this.folderProgress = 0;
      this.folderProgressMessage = '';
    }
  }

  // Método auxiliar para animar el progreso de forma fluida
  private async animateProgress(from: number, to: number, duration: number, message: string): Promise<void> {
    const startTime = Date.now();
    const progressDiff = to - from;

    return new Promise((resolve) => {
      const animate = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);

        // Función de easing para movimiento más natural
        const easeProgress = this.easeInOutCubic(progress);
        this.folderProgress = from + (progressDiff * easeProgress);
        this.folderProgressMessage = message;

        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          resolve();
        }
      };

      requestAnimationFrame(animate);
    });
  }

  // Función de easing para animación más suave
  private easeInOutCubic(t: number): number {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }

  // Métodos de modales
  closeCreateFolderModal() {
    this.showCreateFolderModal = false;
  }

  closeConfirmModal() {
    this.showConfirmModal = false;
  }

  // Método para cancelar la exportación
  cancelExport() {
    this.exportCancelled = true;
    this.canCancelExport = false;
    this.folderProgressMessage = 'Cancelando exportación...';

    // Mostrar mensaje de cancelación
    setTimeout(() => {
      this.isCreatingFolder = false;
      this.folderProgress = 0;
      this.folderProgressMessage = '';
      this.showInfoMessage('Exportación cancelada por el usuario');
    }, 500);
  }

  // Método para verificar conectividad
  private async verifyBackendConnectivity(): Promise<boolean> {
    try {
      const testResponse = await this.http.get(`${environment.apiEndpoint}/order/count`).toPromise();
      return true;
    } catch (error) {
      console.error('Error de conectividad con el backend:', error);
      return false;
    }
  }

  // Método para obtener criterios de filtro actuales
  private getFilterCriteria(): any {
    const criteria: any = {
      appliedAt: new Date().toISOString()
    };

    // Filtros de búsqueda
    if (this.searchTerm) {
      criteria.searchTerm = this.searchTerm;
    }

    // Filtros de fecha
    if (this.startDate) {
      criteria.startDate = this.startDate.toISOString();
    }
    if (this.endDate) {
      criteria.endDate = this.endDate.toISOString();
    }
    if (this.dateFilterType && this.dateFilterType !== 'all') {
      criteria.dateFilterType = this.dateFilterType;
    }

    // Filtros personalizados de fecha
    if (this.customStartDate) {
      criteria.customStartDate = this.customStartDate;
    }
    if (this.customEndDate) {
      criteria.customEndDate = this.customEndDate;
    }

    // Información de paginación y conteos
    criteria.currentPage = this.currentPage;
    criteria.pageSize = this.pageSize;
    criteria.totalFiltered = this.totalFilteredOrders || this.filteredTotal;
    criteria.totalInDatabase = this.totalOrdersInDB;
    criteria.totalOrdersExported = this.allFilteredOrders.length;

    return criteria;
  }

  /**
   * Agrupa los repuestos por código BR Y número de paquete
   * Esto asegura que si dos repuestos tienen diferente código BR o están en diferentes paquetes,
   * aparecerán en líneas separadas
   */
  private groupSparePartsByCodBrAndPackage(spareParts: (SparePartModel & { orderInfo?: any })[]): Record<string, (SparePartModel & { orderInfo?: any })[]> {
    const grouped: Record<string, (SparePartModel & { orderInfo?: any })[]> = {};

    spareParts.forEach(sparePart => {
      const codBr = sparePart['codBr'] || sparePart.codBr || 'SIN_CODIGO_BR';
      const packageNumber = sparePart.packageNumber || 'SIN_PAQUETE';

      // Crear clave única combinando código BR y número de paquete
      const groupKey = `${codBr}___${packageNumber}`;

      if (!grouped[groupKey]) {
        grouped[groupKey] = [];
      }

      grouped[groupKey].push(sparePart);
    });

    return grouped;
  }

  /**
   * Agrupa los repuestos por código BR (función legacy, mantenida por compatibilidad)
   */
  private groupSparePartsByCodBr(spareParts: (SparePartModel & { orderInfo?: any })[]): Record<string, (SparePartModel & { orderInfo?: any })[]> {
    const grouped: Record<string, (SparePartModel & { orderInfo?: any })[]> = {};

    spareParts.forEach(sparePart => {
      const codBr = sparePart['codBr'] || sparePart.codBr || 'SIN_CODIGO_BR';

      if (!grouped[codBr]) {
        grouped[codBr] = [];
      }

      grouped[codBr].push(sparePart);
    });

    return grouped;
  }

  /**
   * Consolida un grupo de repuestos con el mismo código BR
   * Según especificación:
   * - Cantidad: suma de todas las cantidades
   * - Peso: peso máximo × cantidad total
   * - Precio: promedio de todos los precios
   * - Nombre: se toma el primero
   */
  private consolidateSparePartsGroup(group: (SparePartModel & { orderInfo?: any })[]): (SparePartModel & { orderInfo?: any }) | null {
    if (group.length === 0) {
      return null;
    }

    if (group.length === 1) {
      return group[0];
    }

    // Usar el primer elemento como base (mantener el primer nombre)
    const consolidated = { ...group[0] };

    // Encontrar el peso MÁXIMO (neto y bruto)
    let maxNetWeight = 0;
    let maxGrossWeight = 0;

    // Variables para calcular precio promedio
    let totalPrice = 0;
    let priceCount = 0;

    // Sumar las cantidades
    let totalAmount = 0;

    group.forEach(part => {
      // Buscar peso neto máximo
      const netWeight = part.netWheight || 0;
      if (netWeight > maxNetWeight) {
        maxNetWeight = netWeight;
      }

      // Buscar peso bruto máximo
      const grossWeight = part.grossWheight || 0;
      if (grossWeight > maxGrossWeight) {
        maxGrossWeight = grossWeight;
      }

      // Acumular precios para promedio
      const unitPrice = part.unitPrice || 0;
      if (unitPrice > 0) {
        totalPrice += unitPrice;
        priceCount++;
      }

      // Sumar cantidades
      totalAmount += part.amount || 0;
    });

    // Aplicar valores consolidados según especificación
    consolidated.amount = totalAmount; // Cantidad: suma

    // Peso: máximo × cantidad total
    consolidated.netWheight = maxNetWeight * totalAmount;
    consolidated.grossWheight = maxGrossWeight * totalAmount;

    // Precio: promedio
    consolidated.unitPrice = priceCount > 0 ? totalPrice / priceCount : 0;

    // Calcular valorDeVenta basado en precio promedio × cantidad total
    consolidated.valorDeVenta = consolidated.unitPrice * totalAmount;

    // Mantener el nombre, código, etc. del PRIMERO (ya está en consolidated por usar group[0])
    // consolidated.sparePartName ya tiene el primer nombre
    // consolidated.productCode ya tiene el primer código
    // consolidated.productStatus ya tiene el primer estado
    // consolidated.taxNote ya tiene la primera nota fiscal
    // consolidated.ncm ya tiene el primer NCM
    // consolidated.codBr ya tiene el primer código BR
    // consolidated.comments ya tiene los primeros comentarios
    // consolidated.isDamaged ya tiene el primer estado de daño
    // consolidated.country ya tiene el primer país
    // consolidated.shippingStatus ya tiene el primer estado de envío
    // consolidated.shippingReceipt ya tiene el primer recibo de envío
    // consolidated.expectedArrivalDate ya tiene la primera fecha esperada
    // consolidated.orderInfo ya tiene la información de orden del primero

    // Todos los campos del primero ya están en consolidated porque usamos { ...group[0] }

    // Para el número de paquete, usar el formato específico si existe
    const packageNumbers = group
      .map(part => part.packageNumber)
      .filter(pn => pn && pn.trim() !== '');

    if (packageNumbers.length > 0) {
      // Usar el primer número de paquete encontrado como base
      consolidated.packageNumber = packageNumbers[0];
    } else {
      // Si no hay número de paquete, generar uno con formato específico
      const codBr = consolidated.codBr || consolidated['codBr'] || 'SIN_CODIGO';
      const timestamp = new Date().getTime().toString().substr(-6);
      consolidated.packageNumber = `PAQ-${codBr}-${timestamp}`;
    }

    return consolidated;
  }

  /**
   * Obtiene los paquetes existentes para una orden específica
   */
  private getExistingPackagesForOrder(order: OrderOutModel): any[] {
    if (!order.spareParts) return [];

    // Agrupar repuestos por número de paquete
    const packageGroups = order.spareParts
      .filter(sp => sp.packageNumber && sp.packageNumber.trim() !== '')
      .reduce((acc, sparePart) => {
        const packageNumber = sparePart.packageNumber!;
        if (!acc[packageNumber]) {
          acc[packageNumber] = [];
        }
        acc[packageNumber].push(sparePart);
        return acc;
      }, {} as any);

    // Solo paquetes abiertos (pendientes de escaneo). Los ya empaquetados no son asignables.
    return Object.entries(packageGroups)
      .map(([packageNumber, spareParts]) => ({
        packageNumber,
        spareParts: spareParts as any[],
        count: (spareParts as any[]).length
      }))
      .filter(pkg => !isPackageClosedForAssignment(pkg.packageNumber, order.spareParts));
  }

  private buildLabelsFromExistingParts(order: OrderOutModel, assignments: any[]): SparePartLabelInfo[] {
    if (!assignments.length) {
      return [];
    }

    const byPackage = new Map<string, any[]>();
    assignments.forEach(assignment => {
      if (!byPackage.has(assignment.packageNumber)) {
        byPackage.set(assignment.packageNumber, []);
      }
      byPackage.get(assignment.packageNumber)!.push(assignment);
    });

    const labels: SparePartLabelInfo[] = [];
    byPackage.forEach((items, packageNumber) => {
      items.forEach((item, index) => {
        labels.push({
          sparePartId: item.sparePartId,
          sparePartName: item.sparePartName || '',
          sparePartNameEs: item.sparePartNameEs || item.sparePartName || '',
          productCode: item.productCode || '',
          packageNumber,
          orderNumber: order.orderNumber || '',
          siniestro: order.siniestro || 'N/A',
          clienteUY: order.clienteUY || 'AUTOCOMERCIAL',
          positionInPackage: index + 1,
          totalInPackage: items.length,
          clientName: order.clientName || '',
          clientPhone: order.clientPhone || '',
          deliveryAddress: order.deliveryAddress || '',
          workshopName: order.workshopName || '',
          workshopAddress: order.workshopAddress || '',
          workshopPhone: order.workshopPhone || '',
          workshopCity: order.workshopCity || '',
          workshopDepartment: order.workshopDepartment || '',
          providerPhone: this.labelsService.getCompanyWhatsApp(order.clienteUY)
        });
      });
    });

    return labels;
  }

  private syncLocalPackageNumbers(order: OrderOutModel, labels: SparePartLabelInfo[]): void {
    try {
      (labels || []).forEach(lbl => {
        const spLocal = (this.selectedOrder?.spareParts || []).find(sp => sp.sparePartId === lbl.sparePartId);
        if (spLocal) {
          if (!spLocal.originalProductStatus && spLocal.productStatus && !spLocal.productStatus.includes('EMPAQUETADO')) {
            spLocal.originalProductStatus = spLocal.productStatus;
          }
          spLocal.packageNumber = lbl.packageNumber;
        }

        const spInParam = (order?.spareParts || []).find(sp => sp.sparePartId === lbl.sparePartId);
        if (spInParam) {
          if (!spInParam.originalProductStatus && spInParam.productStatus && !spInParam.productStatus.includes('EMPAQUETADO')) {
            spInParam.originalProductStatus = spInParam.productStatus;
          }
          spInParam.packageNumber = lbl.packageNumber;
        }
      });
    } catch {
      // noop
    }
  }

  private executePackAndPrintFlow(order: OrderOutModel, allSelectedForPrint: any[]): void {
    const toPackage = allSelectedForPrint.filter(a => {
      const sp = order.spareParts?.find(s => s.sparePartId === a.sparePartId);
      return sp && !isSparePartPackaged(sp);
    });
    const toPrintOnly = allSelectedForPrint.filter(a => {
      const sp = order.spareParts?.find(s => s.sparePartId === a.sparePartId);
      return sp && isSparePartPackaged(sp);
    });

    const finishWithPrint = (apiLabels: SparePartLabelInfo[]) => {
      const packagedLabels = this.buildLabelsFromExistingParts(order, toPrintOnly);
      const allLabels = [...apiLabels, ...packagedLabels];

      if (allLabels.length === 0) {
        this.showErrorMessage('No hay etiquetas para imprimir.');
        return;
      }

      const currentOrder = this.selectedOrder || order;
      this.labelPrintAuth.printAuthorizedSparePartLabels(currentOrder, allLabels, {
        useSharedNumber: true,
        includePackageLabel: false,
        explicitWorkshopId: currentOrder.workshopId
      }).subscribe({
        next: () => {
          const message = toPackage.length > 0
            ? `Se empaquetaron ${toPackage.length} repuesto(s) y se imprimieron ${allLabels.length} etiqueta(s).`
            : `Se reimprimieron ${allLabels.length} etiqueta(s) de repuestos ya empaquetados.`;
          this.showSuccessMessage(message);
        },
        error: (error) => {
          this.showErrorMessage(error?.message || 'No se pudo completar la impresión.');
        }
      });
    };

    if (toPackage.length === 0) {
      finishWithPrint([]);
      return;
    }

    const currentOrder = this.selectedOrder || order;
    const request = {
      orderId: currentOrder.orderId,
      selectedSpareParts: toPackage,
      generatedBy: 'system'
    };

    this.loading = true;
    this.loadingMessage = 'Generando paquetes e imprimiendo etiquetas...';
    this.loadingDetail = 'Procesando repuestos...';

    this.labelsService.generateLabelsAndPackage(request).subscribe({
      next: (response) => {
        this.loading = false;

        if (!response.success) {
          this.showErrorMessage(`Error al empaquetar la orden: ${response.message}`);
          return;
        }

        this.showEmergentCommentAlertGroups((response as any)?.emergentCommentAlerts, 'Generación de paquetes');
        this.syncLocalPackageNumbers(order, response.labels || []);
        finishWithPrint(response.labels || []);
      },
      error: (error) => {
        this.loading = false;
        console.error('Error al generar paquetes e imprimir:', error);
        this.showErrorMessage(error.error?.message || error.message || 'Error al generar paquetes e imprimir etiquetas.');
      }
    });
  }

  /**
   * Procesa la generación de etiquetas y empaquetado con los repuestos seleccionados
   * @param printLabels Si es true, imprime las etiquetas. Si es false, solo guarda los paquetes.
   */
  private processLabelsAndPackages(order: OrderOutModel, selectedSpareParts: any[], printLabels: boolean = true): void {
    const currentOrder = this.selectedOrder || order;

    // Preparar los datos para el backend
    const request = {
      orderId: currentOrder.orderId,
      selectedSpareParts: selectedSpareParts,
      generatedBy: 'system'
    };

    // Mostrar indicador de carga
    this.loading = true;
    this.loadingMessage = printLabels ? 'Generando etiquetas y empaquetando orden...' : 'Empaquetando orden...';
    this.loadingDetail = 'Procesando repuestos...';

    // Llamar al servicio
    this.labelsService.generateLabelsAndPackage(request).subscribe({
      next: (response) => {
        this.loading = false;

        if (response.success) {
          this.showEmergentCommentAlertGroups((response as any)?.emergentCommentAlerts, 'Generación de paquetes');
          // Mostrar mensaje de éxito
          const successMessage = printLabels
            ? `¡Orden empaquetada exitosamente!\n\n` +
            `• Lista de paquetes creada: ID ${response.packageListId}\n` +
            `• ${response.totalSparePartsProcessed} repuestos procesados\n` +
            `• Etiquetas individuales generadas`
            : `¡Orden empaquetada exitosamente!\n\n` +
            `• Lista de paquetes creada: ID ${response.packageListId}\n` +
            `• ${response.totalSparePartsProcessed} repuestos procesados\n` +
            `• Paquetes guardados sin imprimir etiquetas`;

          this.showSuccessMessage(successMessage);

          // Solo imprimir etiquetas si printLabels es true
          if (printLabels) {
            this.labelPrintAuth.printAuthorizedSparePartLabels(currentOrder, response.labels, {
              useSharedNumber: true,
              includePackageLabel: false,
              explicitWorkshopId: currentOrder.workshopId
            }).subscribe({
              error: (error) => {
                this.showErrorMessage(error?.message || 'No se pudo completar la impresión.');
              }
            });
          }

          this.syncLocalPackageNumbers(order, response.labels || []);

          // CRÍTICO: Si solo se guardó sin imprimir, recargar los datos desde el backend
          // para asegurar que los números de paquete estén actualizados correctamente
          if (!printLabels) {
            this.refreshOrderData(currentOrder.orderId).then(() => {
              // Actualizar también selectedOrder con los datos frescos
              const refreshedOrder = this.orders.find(o => o.orderId === currentOrder.orderId);
              if (refreshedOrder) {
                this.selectedOrder = { ...refreshedOrder };
                this.cdr.detectChanges();
              }
            }).catch((error) => {
              console.error('⚠️ Error al recargar datos desde el backend:', error);
              // Continuar de todas formas, los datos locales ya están sincronizados
            });
          } else {
            // Si se imprimió, evitar recargar para no perder estado local recién sincronizado
          }
        } else {
          this.showErrorMessage(`Error al empaquetar la orden: ${response.message}`);
        }
      },
      error: (error) => {
        this.loading = false;
        console.error('Error al generar etiquetas y empaquetar:', error);
        console.error('Error details:', {
          status: error.status,
          statusText: error.statusText,
          url: error.url,
          error: error.error
        });

        // Mostrar error más detallado para debugging
        let errorMessage = 'Error al generar etiquetas y empaquetar la orden.';
        if (error.error && error.error.message) {
          errorMessage += `\n\nDetalles: ${error.error.message}`;
        }
        if (error.error && error.error.innerException) {
          errorMessage += `\n\nError interno: ${error.error.innerException}`;
        }
        if (error.error && error.error.stackTrace) {
          errorMessage += `\n\nStack trace: ${error.error.stackTrace}`;
        }

        this.showErrorMessage(errorMessage);
      }
    });
  }

  // Método auxiliar para cargar detalles de paquetes de manera recursiva
  private loadPackageDetailsRecursively(
    packages: any[],
    index: number,
    targetSet: Set<number>,
    type: 'empaquetado' | 'enviado' | 'cargado',
    storeTransportTypes: boolean = false
  ): void {
    while (
      index < packages.length &&
      packages[index].packageListId > 0 &&
      Array.isArray(packages[index].sparePartIds)
    ) {
      const current = packages[index];
      this.addPackageSparePartIds(current.sparePartIds, current, targetSet, type, storeTransportTypes);
      index++;
    }

    if (index >= packages.length) {
      return;
    }

    const pkg = packages[index];

    // Si packageListId no existe o es 0, y tenemos packageNumber, usar flujo alternativo (SpareParts)
    // De lo contrario, si tenemos packageListId válido, usar flujo normal
    if ((pkg.packageListId === undefined || pkg.packageListId === 0) && pkg.packageNumber) {
      this.packageListsService.getSparePartsByPackage(pkg.packageNumber).subscribe({
        next: (spareParts) => {
          spareParts.forEach((sp: any) => {
            targetSet.add(sp.sparePartId);
            // Si se solicita, almacenar el tipo de vehículo
            if (storeTransportTypes && type === 'cargado') {
              const vehicleType = pkg.transportType || 'camion';
              this.sparePartsTransportType.set(sp.sparePartId, vehicleType);
            }
          });
          // Cargar el siguiente paquete
          this.loadPackageDetailsRecursively(packages, index + 1, targetSet, type, storeTransportTypes);
        },
        error: (error) => {
          console.error(`Error al cargar detalles del paquete ${pkg.packageNumber}:`, error);
          // Continuar con el siguiente paquete incluso si hay error
          this.loadPackageDetailsRecursively(packages, index + 1, targetSet, type, storeTransportTypes);
        }
      });
    } else if (pkg.packageListId && pkg.packageListId > 0) {
      // Flujo normal: consultar PackageList por ID
      this.packageListsService.getPackageList(pkg.packageListId).subscribe({
        next: (packageDetail) => {
          if (packageDetail && packageDetail.items) {
            packageDetail.items.forEach(item => {
              targetSet.add(item.sparePartId);
              // Si se solicita, almacenar el tipo de vehículo
              if (storeTransportTypes && type === 'cargado') {
                const vehicleType = pkg.transportType || 'camion';
                this.sparePartsTransportType.set(item.sparePartId, vehicleType);
              }
            });
          }
          // Cargar el siguiente paquete
          this.loadPackageDetailsRecursively(packages, index + 1, targetSet, type, storeTransportTypes);
        },
        error: (error) => {
          console.error(`Error al cargar detalles del paquete ${pkg.packageListId}:`, error);
          // Continuar con el siguiente paquete incluso si hay error
          this.loadPackageDetailsRecursively(packages, index + 1, targetSet, type, storeTransportTypes);
        }
      });
    } else {
      // Paquete inválido (sin packageNumber ni packageListId válido), saltar
      this.loadPackageDetailsRecursively(packages, index + 1, targetSet, type, storeTransportTypes);
    }
  }

  private addPackageSparePartIds(
    sparePartIds: number[],
    pkg: any,
    targetSet: Set<number>,
    type: 'empaquetado' | 'enviado' | 'cargado',
    storeTransportTypes: boolean
  ): void {
    sparePartIds.forEach(sparePartId => {
      targetSet.add(sparePartId);
      if (storeTransportTypes && type === 'cargado') {
        const vehicleType = pkg.transportType || 'camion';
        this.sparePartsTransportType.set(sparePartId, vehicleType);
      }
    });
  }

  // Método para cargar los estados de empaquetado de los repuestos
  loadPackagingStatus(): void {
    // Cargar repuestos cargados (tipo 'cargado') - CORREGIDO PARA USAR PackageLists.Type
    this.packageListsService.getUniquePackages('cargado').subscribe({
      next: (packages) => {
        this.cargadoSpareParts.clear();
        this.sparePartsTransportType.clear();
        if (packages.length > 0) {
          // Procesar recursivamente y obtener tipos de vehículo al mismo tiempo
          this.loadPackageDetailsRecursively(packages, 0, this.cargadoSpareParts, 'cargado', true);
        }
      },
      error: (error) => {
        console.error('Error al cargar repuestos cargados:', error);
      }
    });

    // Cargar repuestos empaquetados (tipo 'empaquetado')
    this.packageListsService.getUniquePackages('empaquetado').subscribe({
      next: (packages) => {
        this.empaquetadoSpareParts.clear();
        if (packages.length > 0) {
          this.loadPackageDetailsRecursively(packages, 0, this.empaquetadoSpareParts, 'empaquetado', false);
        }
      },
      error: (error) => {
        console.error('Error al cargar repuestos empaquetados:', error);
      }
    });

    // Cargar repuestos enviados (tipo 'enviado')
    this.packageListsService.getUniquePackages('enviado').subscribe({
      next: (packages) => {
        this.enviadoSpareParts.clear();
        if (packages.length > 0) {
          this.loadPackageDetailsRecursively(packages, 0, this.enviadoSpareParts, 'enviado', false);
        }
      },
      error: (error) => {
        console.error('Error al cargar repuestos enviados:', error);
      }
    });
  }

  // Método para obtener el estado de empaquetado de un repuesto
  getSparePartPackagingStatus(sparePartId: number): 'empaquetado' | 'exportado' | 'enviado' | null {
    if (this.enviadoSpareParts.has(sparePartId)) {
      return 'enviado';
    }
    if (this.cargadoSpareParts.has(sparePartId)) {
      return 'exportado';
    }
    if (this.empaquetadoSpareParts.has(sparePartId)) {
      return 'empaquetado';
    }
    return null;
  }

  // Método para obtener el estado de empaquetado con información del vehículo
  getSparePartPackagingStatusWithVehicle(sparePart: any): { status: 'empaquetado' | 'exportado' | 'enviado' | null, vehicleType?: string } {
    let status = this.getSparePartPackagingStatus(sparePart.sparePartId);
    // Obtener el tipo de vehículo del mapa o del repuesto directamente
    const vehicleType = this.sparePartsTransportType.get(sparePart.sparePartId) ||
      sparePart.transportType ||
      sparePart.TransportType;

    // Si tiene TransportType asignado (cargado en camioneta o camión), considerar como "exportado"
    // aunque técnicamente el status sea "empaquetado" (caso de camioneta)
    if (vehicleType && status === 'empaquetado') {
      status = 'exportado';
    }

    return {
      status: status,
      vehicleType: vehicleType
    };
  }

  // Método para obtener el icono correspondiente al estado de empaquetado
  getPackagingStatusIcon(status: 'empaquetado' | 'exportado' | 'enviado' | null): string {
    switch (status) {
      case 'empaquetado':
        return '📦'; // Caja para empaquetado
      case 'exportado':
        return '🚛'; // Camión para exportado
      case 'enviado':
        return '📤'; // Flecha de envío para enviado
      default:
        return '';
    }
  }

  // Método helper para obtener imagen de camioneta
  getCamionetaIcon(): SafeHtml {
    const imgHtml = `<img src="/assets/images/pickup-car.png" alt="Camioneta" style="width: 24px; height: 24px; display: inline-block; vertical-align: middle;" />`;
    return this.sanitizer.bypassSecurityTrustHtml(imgHtml);
  }

  // Método para obtener el icono combinado (estado + vehículo)
  getPackagingStatusIconWithVehicle(sparePart: any): SafeHtml | string {
    const statusInfo = this.getSparePartPackagingStatusWithVehicle(sparePart);
    const { status, vehicleType } = statusInfo;

    // Si el repuesto tiene TransportType asignado (cargado en camioneta o camión)
    // mostrar el icono del vehículo correspondiente, independientemente del status
    if (vehicleType) {
      const vehicleLower = vehicleType.toLowerCase();

      if (status === 'enviado') {
        // Si fue enviado, mostrar emoji de envío + vehículo
        if (vehicleLower === 'camioneta') {
          const imgHtml = '📤' + `<img src="/assets/images/pickup-car.png" alt="Camioneta" style="width: 24px; height: 24px; display: inline-block; vertical-align: middle;" />`;
          return this.sanitizer.bypassSecurityTrustHtml(imgHtml);
        } else {
          return '📤🚛'; // Enviado + camión
        }
      } else {
        // Si está cargado (tiene TransportType) pero aún no enviado
        if (vehicleLower === 'camioneta') {
          return this.getCamionetaIcon(); // Imagen de camioneta verde
        } else {
          return '🚛'; // Camión para cargado en camión
        }
      }
    }

    // Si no tiene TransportType, usar el comportamiento anterior según el status
    if (!status) return '';

    switch (status) {
      case 'empaquetado':
        return '📦'; // Caja para empaquetado
      case 'exportado':
        return '🚛'; // Camión para exportado (comportamiento por defecto)
      case 'enviado':
        return '📤'; // Flecha de envío
      default:
        return '';
    }
  }

  // Método para obtener el texto descriptivo del estado
  getPackagingStatusText(status: 'empaquetado' | 'exportado' | 'enviado' | null): string {
    switch (status) {
      case 'empaquetado':
        return 'Empaquetado';
      case 'exportado':
        return 'Exportado';
      case 'enviado':
        return 'Enviado';
      default:
        return '';
    }
  }

  // Método para obtener el texto descriptivo con información del vehículo
  getPackagingStatusTextWithVehicle(sparePart: any): string {
    const statusInfo = this.getSparePartPackagingStatusWithVehicle(sparePart);
    const { status, vehicleType } = statusInfo;

    if (!status) return '';

    switch (status) {
      case 'empaquetado':
        return 'Empaquetado';
      case 'exportado':
        if (vehicleType?.toLowerCase() === 'camioneta') {
          return 'Exportado en Camioneta';
        } else {
          return 'Exportado en Camión';
        }
      case 'enviado':
        if (vehicleType?.toLowerCase() === 'camioneta') {
          return 'Enviado (desde Camioneta)';
        } else {
          return 'Enviado (desde Camión)';
        }
      default:
        return '';
    }
  }

  // Método para obtener la clase CSS del indicador
  getPackagingStatusClass(status: 'empaquetado' | 'exportado' | 'enviado' | null): string {
    switch (status) {
      case 'empaquetado':
        return 'packaging-indicator empaquetado';
      case 'exportado':
        return 'packaging-indicator exportado';
      case 'enviado':
        return 'packaging-indicator enviado';
      default:
        return '';
    }
  }

  // Método para obtener la clase del badge de estado responsive
  getStatusBadgeClass(status: string): string {
    if (!status) return '';

    const statusLower = status.toLowerCase();
    if (statusLower.includes('creado') || statusLower.includes('created')) return 'created';
    if (statusLower.includes('proceso') || statusLower.includes('processing') || statusLower.includes('en proceso')) return 'processing';
    if (statusLower.includes('finalizado') || statusLower.includes('completed') || statusLower.includes('completado')) return 'completed';
    if (statusLower.includes('cancelado') || statusLower.includes('cancelled')) return 'cancelled';
    if (statusLower.includes('pendiente') || statusLower.includes('pending')) return 'processing';
    if (statusLower.includes('llegó') || statusLower.includes('llegó') || statusLower.includes('arrived')) return 'completed';
    if (statusLower.includes('atención') || statusLower.includes('attention')) return 'cancelled';
    if (statusLower.includes('repuestos-pendientes')) return 'processing';

    return 'processing'; // Estado por defecto
  }

  // Para selección múltiple de repuestos para imprimir
  selectedSparePartsForPrint: Set<number> = new Set();

  // Modal para seleccionar repuestos antes de imprimir
  showLabelSelectionModal = false;
  sparePartsForLabelSelection: any[] = [];
  selectedSparePartsForLabels: Set<number> = new Set();

  /**
   * Toggle selección de repuesto para impresión
   */
  toggleSparePartSelection(sparePartId: number): void {
    if (this.selectedSparePartsForPrint.has(sparePartId)) {
      this.selectedSparePartsForPrint.delete(sparePartId);
    } else {
      this.selectedSparePartsForPrint.add(sparePartId);
    }
  }

  /**
   * Verificar si un repuesto está seleccionado para impresión
   */
  isSparePartSelected(sparePartId: number): boolean {
    return this.selectedSparePartsForPrint.has(sparePartId);
  }

  /**
   * Verificar si todos los repuestos con packageNumber están seleccionados
   */
  areAllSparePartsSelected(): boolean {
    if (!this.selectedOrder || !this.selectedOrder.spareParts) {
      return false;
    }

    const partsWithPackage = this.selectedOrder.spareParts.filter(p => p.packageNumber);
    return partsWithPackage.length > 0 &&
      this.selectedSparePartsForPrint.size === partsWithPackage.length;
  }

  /**
   * Seleccionar/deseleccionar todos los repuestos con packageNumber
   */
  toggleAllSparePartsSelection(): void {
    if (!this.selectedOrder || !this.selectedOrder.spareParts) {
      return;
    }

    const partsWithPackage = this.selectedOrder.spareParts.filter(p => p.packageNumber);

    if (this.selectedSparePartsForPrint.size === partsWithPackage.length) {
      // Deseleccionar todos
      this.selectedSparePartsForPrint.clear();
    } else {
      // Seleccionar todos los que tienen packageNumber
      partsWithPackage.forEach(part => {
        this.selectedSparePartsForPrint.add(part.sparePartId);
      });
    }
  }

  /**
   * Abrir modal para seleccionar repuestos antes de imprimir
   */
  openLabelSelectionModal(): void {
    if (!this.selectedOrder || !this.selectedOrder.spareParts) {
      this.showErrorMessage('No hay orden seleccionada');
      return;
    }

    // Filtrar solo repuestos con packageNumber
    this.sparePartsForLabelSelection = this.selectedOrder.spareParts.filter(part => part.packageNumber);

    if (this.sparePartsForLabelSelection.length === 0) {
      this.showErrorMessage('No hay repuestos con número de paquete asignado para imprimir');
      return;
    }

    // Limpiar selección previa
    this.selectedSparePartsForLabels.clear();

    // Abrir modal
    this.showLabelSelectionModal = true;
  }

  /**
   * Cerrar modal de selección de etiquetas
   */
  closeLabelSelectionModal(): void {
    this.showLabelSelectionModal = false;
    this.selectedSparePartsForLabels.clear();
  }

  /**
   * Toggle selección de repuesto en el modal
   */
  toggleLabelSelection(sparePartId: number): void {
    if (this.selectedSparePartsForLabels.has(sparePartId)) {
      this.selectedSparePartsForLabels.delete(sparePartId);
    } else {
      this.selectedSparePartsForLabels.add(sparePartId);
    }
  }

  /**
   * Verificar si un repuesto está seleccionado en el modal
   */
  isLabelSelected(sparePartId: number): boolean {
    return this.selectedSparePartsForLabels.has(sparePartId);
  }

  /**
   * Seleccionar/deseleccionar todos en el modal
   */
  toggleAllLabelsSelection(): void {
    if (this.selectedSparePartsForLabels.size === this.sparePartsForLabelSelection.length) {
      this.selectedSparePartsForLabels.clear();
    } else {
      this.sparePartsForLabelSelection.forEach(part => {
        this.selectedSparePartsForLabels.add(part.sparePartId);
      });
    }
  }

  /**
   * Verificar si todos están seleccionados en el modal
   */
  areAllLabelsSelected(): boolean {
    return this.sparePartsForLabelSelection.length > 0 &&
      this.selectedSparePartsForLabels.size === this.sparePartsForLabelSelection.length;
  }

  /**
   * Imprimir etiquetas de los repuestos seleccionados en el modal
   */
  printSelectedLabelsFromModal(): void {
    if (this.selectedSparePartsForLabels.size === 0) {
      this.showErrorMessage('Por favor, seleccione al menos un repuesto para imprimir');
      return;
    }

    if (!this.selectedOrder) {
      this.showErrorMessage('No hay orden seleccionada');
      return;
    }

    const selectedParts = this.sparePartsForLabelSelection.filter(part =>
      this.selectedSparePartsForLabels.has(part.sparePartId)
    );

    // Crear SparePartLabelInfo para cada repuesto seleccionado
    const labelsInfo: SparePartLabelInfo[] = selectedParts.map((part, index) => ({
      sparePartId: part.sparePartId,
      sparePartName: part.sparePartName || '',
      sparePartNameEs: part.sparePartNameEs || '',
      productCode: part.productCode || '',
      packageNumber: part.packageNumber!,
      orderNumber: this.selectedOrder!.orderNumber || '',
      siniestro: this.selectedOrder!.siniestro || 'N/A',
      clienteUY: this.selectedOrder!.clienteUY || 'AUTOCOMERCIAL',
      positionInPackage: index + 1,
      totalInPackage: selectedParts.length,
      clientName: this.selectedOrder!.clientName || '',
      clientPhone: this.selectedOrder!.clientPhone || '',
      deliveryAddress: this.selectedOrder!.deliveryAddress || '',
      workshopName: this.selectedOrder!.workshopName || '',
      workshopAddress: this.selectedOrder!.workshopAddress || '',
      workshopPhone: this.selectedOrder!.workshopPhone || '',
      workshopCity: this.selectedOrder!.workshopCity || '',
      workshopDepartment: this.selectedOrder!.workshopDepartment || '',
      providerPhone: this.labelsService.getCompanyWhatsApp(this.selectedOrder!.clienteUY)
    }));

    // Imprimir solo las etiquetas seleccionadas (sin etiqueta de paquete)
    this.labelPrintAuth.printAuthorizedSparePartLabels(
      this.selectedOrder,
      labelsInfo,
      {
        useSharedNumber: false,
        includePackageLabel: false,
        explicitWorkshopId: this.selectedOrder.workshopId
      }
    ).subscribe({
      next: () => {
        this.closeLabelSelectionModal();
      },
      error: (error) => {
        this.showErrorMessage(error?.message || 'No se pudo completar la impresión.');
      }
    });
  }

  /**
   * Imprimir etiquetas de los repuestos seleccionados (método antiguo - mantener para compatibilidad)
   */
  printSelectedLabels(): void {
    // Redirigir al modal
    this.openLabelSelectionModal();
  }

  /**
   * Imprimir solo una etiqueta de un repuesto seleccionado
   */
  printSingleLabel(sparePart: any): void {
    if (!sparePart || !sparePart.packageNumber) {
      this.showErrorMessage('El repuesto no tiene número de paquete asignado');
      return;
    }

    // Usar selectedOrder si está disponible, sino buscar la orden
    const order = this.selectedOrder || this.filteredOrdersList.find(o =>
      o.orderNumber === (sparePart.orderNumber || sparePart['orderNumber'])
    );

    if (!order) {
      this.showErrorMessage('No se pudo encontrar la orden del repuesto');
      return;
    }

    // Crear SparePartLabelInfo para el servicio de etiquetas
    const labelInfo: SparePartLabelInfo = {
      sparePartId: sparePart.sparePartId,
      sparePartName: sparePart.sparePartName || '',
      sparePartNameEs: sparePart.sparePartNameEs || '',
      productCode: sparePart.productCode || '',
      packageNumber: sparePart.packageNumber,
      orderNumber: order.orderNumber || '',
      siniestro: order.siniestro || 'N/A',
      clienteUY: order.clienteUY || 'AUTOCOMERCIAL',
      positionInPackage: 1,
      totalInPackage: 1,
      clientName: order.clientName || '',
      clientPhone: order.clientPhone || '',
      deliveryAddress: order.deliveryAddress || '',
      workshopName: order.workshopName || '',
      workshopAddress: order.workshopAddress || '',
      workshopPhone: order.workshopPhone || '',
      workshopCity: order.workshopCity || '',
      workshopDepartment: order.workshopDepartment || '',
      providerPhone: this.labelsService.getCompanyWhatsApp(order.clienteUY)
    };

    this.labelPrintAuth.printAuthorizedSparePartLabels(order, [labelInfo], {
      useSharedNumber: false,
      includePackageLabel: false,
      explicitWorkshopId: order.workshopId
    }).subscribe({
      error: (error) => {
        this.showErrorMessage(error?.message || 'No se pudo completar la impresión.');
      }
    });
  }

  goBack(): void {
    this.router.navigate(['/']);
  }
}

@Directive({
  selector: '[appNumberFormat]'
})
export class NumberFormatDirective {
  constructor(private el: ElementRef) {
    this.el.nativeElement.addEventListener('input', this.onInputChange.bind(this));
  }

  onInputChange(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input && input.value) {
      const newValue = input.value.replace(/,/g, '.');
      if (newValue !== input.value) {
        const cursorPosition = input.selectionStart;
        input.value = newValue;
        input.setSelectionRange(cursorPosition, cursorPosition);
      }
    }
  }

}

